import { auth } from './firebase'
// Build-time snapshot of the recipe manifest (scripts/bake-recipes-seed.mjs,
// refreshed by `prebuild`). Bundled so the Templates grid paints instantly,
// then reconciled against the live manifest version. See listTemplateRecipes.
import recipesSeedRaw from '@/data/recipes-seed.json'

// Exported for use by other lib/* files (e.g. gallery.ts) that want to
// keep the same auth/fetch pattern without duplicating it.
export const API_BASE = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000'

// Auth headers for /api/* calls. Attaches the Firebase ID token (Phase 3).
//
// auth.currentUser.getIdToken() returns a cached token until it's within
// ~5 min of expiry, then transparently refreshes via the Firebase Auth
// SDK. Standard pattern — no manual refresh logic needed.
//
// AuthGate ensures the wizard only renders while a session exists, so
// auth.currentUser should always be non-null here. If it isn't, the
// request goes out without an Authorization header and the backend
// returns 401, which AuthGate catches on the next render and redirects
// to /auth/sign-in.
export async function getAuthHeaders(): Promise<HeadersInit> {
  const user = auth.currentUser
  if (!user) return {}
  try {
    const token = await user.getIdToken()
    return { Authorization: `Bearer ${token}` }
  } catch {
    // Token fetch failed (network blip / refresh-token expired). Let the
    // request go out unauthenticated; backend returns 401 → AuthGate
    // redirects on the next render.
    return {}
  }
}

export interface AppStatus {
  maintenance: boolean;
  message: string;
}

/** Public liveness + maintenance flag. Read on app boot by MaintenanceGate to
 *  decide whether to show the maintenance page. No auth, no throw — any failure
 *  (offline, backend down) resolves to "not in maintenance" so the gate fails
 *  OPEN (we never lock users out because the status probe itself failed). */
export async function getStatus(): Promise<AppStatus> {
  try {
    const res = await fetch(`${API_BASE}/api/status`, { cache: 'no-store' });
    if (!res.ok) return { maintenance: false, message: '' };
    const data = await res.json();
    return {
      maintenance: !!data?.maintenance,
      message:
        typeof data?.message === 'string'
          ? data.message
          : "We're experiencing technical issues — we'll be back soon.",
    };
  } catch {
    return { maintenance: false, message: '' };
  }
}

/** Permanently delete the signed-in user's account + all their data on the
 *  backend (Cloud SQL cascade + GCS). The Firebase auth account is removed
 *  separately on the client (see useAuth.deleteAccount). Throws on failure. */
export async function deleteAccount(): Promise<void> {
  const res = await fetch(`${API_BASE}/api/account`, {
    method: 'DELETE',
    headers: await getAuthHeaders(),
  })
  if (!res.ok) {
    let detail = 'Account deletion failed. Please try again.'
    try {
      const body = await res.json()
      if (body?.detail) detail = String(body.detail)
    } catch { /* non-JSON error body */ }
    throw new Error(detail)
  }
}

export type Quality = 'low' | 'medium' | 'high'

// Mirror of composeRender's AspectRatio + backend SIZE_MAP. Backend rejects
// anything not in its SIZE_MAP, so keep these in sync.
export type AspectRatio = '1:1' | '4:5' | '2:3' | '9:16' | '1.91:1' | '16:9'

/** Image-gen provider. Default is 'gemini' (gemini-2.5-flash-image, 1:1 only,
 *  single quality, flat 2-credit cost) — flipped from openai after OpenAI
 *  deprecated gpt-image-2 access (the model with usable text rendering).
 *  'openai' (gpt-image-1, Low/Medium/High, all 6 aspect ratios) is auto-
 *  selected by the backend for paths Gemini can't handle (mask-based: Fashion
 *  / Product Swap / Held, plus social-bg and pattern and non-1:1 aspects).
 *  Callers can pass 'openai' explicitly to force it; the backend's auto-
 *  fallback flips 'gemini' → 'openai' silently for the constrained routes. */
export type Provider = 'openai' | 'gemini'

export type GenerateParams = {
  productFile: File
  templateId: string
  frameNumber: number
  aspectRatio: AspectRatio
  productName: string
  productDescription: string
  quality: Quality
  provider?: Provider   // defaults to 'gemini' if omitted (backend default too)
  /** Fashion route only — one of 'top' | 'bottoms' | 'shoes'. Selects which
   *  mask file the backend uses for the gpt-image-1 edit. Omitted for the
   *  default (template-based) route. */
  garmentType?: 'top' | 'bottoms' | 'shoes'
  /** Social Post route only — when `templateId` starts with 'social-bg-',
   *  this hex (e.g. '#efe7d8') is the solid bg color the backend generates
   *  in memory and sends to gpt-image-1 as the canvas. Per-design constant
   *  in social-designs/colors.ts. Ignored for other routes. */
  bgHex?: string
  /** Social Post route only — when true, backend swaps to a no-shadow
   *  editorial compose prompt so cropped human models don't get a fake
   *  ground shadow drawn under their cut-off limbs. Sourced from
   *  analyze-product's hasHumanModel field. Ignored for non-social routes. */
  hasHumanModel?: boolean
}

export type GenerateImageResult = {
  imageUrl: string
  cost: number
  creditsConsumed: number
  /** Echo of the provider that actually generated this image. Used by the
   *  preview's cost pill to label "OpenAI" vs "Gemini". */
  provider: Provider
}

/**
 * Thrown when /api/generate returns 402 — user is out of credits OR backend
 * rejected the request before reaching OpenAI. Caller should refresh the
 * credit balance and show an upgrade CTA rather than re-throwing.
 */
export class InsufficientCreditsError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'InsufficientCreditsError'
  }
}

/** Backend (or upstream OpenAI) took too long. Credits already refunded by
 *  the server, so caller just needs to offer a retry. */
export class GenerationTimeoutError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'GenerationTimeoutError'
  }
}

/** Couldn't reach the backend at all (DNS, offline, CORS preflight blocked,
 *  backend crashed). No request reached OpenAI, so nothing to refund. */
export class NetworkError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'NetworkError'
  }
}

/** OpenAI's content/safety filter rejected the input. Retrying with the same
 *  inputs will fail the same way — caller should prompt the user to change
 *  the product image / description rather than just offer retry. */
export class ContentRejectedError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'ContentRejectedError'
  }
}

/** Upstream temporarily refused us (rate limit, OpenAI degraded). Retry is
 *  reasonable but should be delayed. */
export class TransientUpstreamError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'TransientUpstreamError'
  }
}

/** Thrown by uploadProduct when the backend couldn't identify a manually
 *  uploaded product from its image (422 needs_manual_info). The product is
 *  NOT saved — the caller must collect a name + brief from the user and
 *  re-upload with them. Carries vision's best-guess prefill values. */
export class ProductNeedsInfoError extends Error {
  suggestedName: string
  suggestedDescription: string
  constructor(message: string, suggestedName = '', suggestedDescription = '') {
    super(message)
    this.name = 'ProductNeedsInfoError'
    this.suggestedName = suggestedName
    this.suggestedDescription = suggestedDescription
  }
}

/** Extract `{detail: "..."}` from a FastAPI error body. Falls back to the
 *  raw text if it isn't JSON, so we never lose the original message. */
async function parseErrorDetail(response: Response): Promise<string> {
  const text = await response.text()
  try {
    const parsed = JSON.parse(text)
    if (typeof parsed?.detail === 'string') return parsed.detail
  } catch {
    /* not JSON */
  }
  return text || `HTTP ${response.status}`
}

/** Map an HTTP failure (or a thrown fetch error) to one of our typed errors.
 *  Centralized so /generate, /suggest-copy, /analyze-product all surface
 *  identical semantics to the UI. */
export async function mapFailure(response: Response): Promise<Error> {
  const detail = await parseErrorDetail(response)
  switch (response.status) {
    case 401:
      // Backend's auth middleware returns `{"detail": "Unauthorized"}`
      // for missing/invalid/expired tokens. Surface a user-actionable
      // message instead of the raw backend string — usually means the
      // Firebase token expired (1h TTL) without refreshing in time.
      return new Error('Your session expired. Refresh the page to sign back in.')
    case 402:
      return new InsufficientCreditsError(detail)
    case 504:
      return new GenerationTimeoutError(detail)
    case 502:
      return new NetworkError(detail)
    case 429:
      return new TransientUpstreamError(detail)
    case 400:
      // 400 from /generate is content-policy reject. Other endpoints' 400s
      // (validation errors) also land here — message text disambiguates.
      return new ContentRejectedError(detail)
    case 403:
      // Invite-only gate (pre-launch beta): a verified session that hasn't
      // redeemed a valid code. Drop the frontend gate pass + signal
      // InviteLayer to re-show the gate so they can enter a fresh code
      // (their old one was invalid/used). Other 403s (e.g. email_not_verified)
      // pass through unchanged for their own handlers.
      if (detail === 'invite_required') {
        try {
          localStorage.removeItem('invite_gate_ok')
          window.dispatchEvent(new Event('sp:invite-required'))
        } catch {
          // ignore storage/event failures
        }
        return new Error('invite_required')
      }
      return new Error(detail)
    default:
      return new Error(detail)
  }
}

// ── Invite-only access gate (pre-launch beta) ────────────────────────────────
// Removed at launch by flipping INVITE_ONLY (backend) + VITE_INVITE_ONLY
// (frontend). See helpers/invites.py + components/feature/InviteGate.tsx.

/** Public, non-consuming: is this invite code currently valid? Unlocks the
 *  login/signup screen. Never throws — returns false on any error. */
export async function checkInvite(code: string): Promise<boolean> {
  try {
    const res = await fetch(`${API_BASE}/api/invite/check`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ code }),
    })
    if (!res.ok) return false
    const data = await res.json()
    return Boolean(data?.valid)
  } catch {
    return false
  }
}

/** Authed: tie a single-use code to the signed-in user (makes them "invited").
 *  Never throws — the redeem-after-auth effect treats failure as "not redeemed"
 *  and the backend's invite_required gate re-surfaces the code screen. */
export async function redeemInvite(
  code: string,
): Promise<{ ok: boolean; already?: boolean; reason?: string }> {
  try {
    const res = await fetch(`${API_BASE}/api/invite/redeem`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(await getAuthHeaders()) },
      body: JSON.stringify({ code }),
    })
    if (!res.ok) return { ok: false, reason: `http_${res.status}` }
    return await res.json()
  } catch {
    return { ok: false, reason: 'network' }
  }
}

// ── Branded auth emails via Resend (backend generates the Firebase action link
// and sends it from noreply@hookad.ai instead of Firebase's spam-prone sender) ──

/** Authed: email the signed-in user their verification link. `origin` is
 *  window.location.origin so the link returns to the same app. Never throws. */
export async function sendVerificationEmail(
  origin: string,
): Promise<{ ok: boolean; reason?: string }> {
  try {
    const res = await fetch(`${API_BASE}/api/auth/send-verification`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(await getAuthHeaders()) },
      body: JSON.stringify({ origin }),
    })
    if (!res.ok) return { ok: false, reason: `http_${res.status}` }
    return await res.json()
  } catch {
    return { ok: false, reason: 'network' }
  }
}

/** Authed: email the signed-in user a 6-digit verification CODE (OTP flow —
 *  replaces the magic link so there's no second tab / no verify loop). Never
 *  throws. */
export async function sendVerificationCode(): Promise<{ ok: boolean; reason?: string }> {
  try {
    const res = await fetch(`${API_BASE}/api/auth/send-verification-code`, {
      method: 'POST',
      headers: { ...(await getAuthHeaders()) },
    })
    if (!res.ok) return { ok: false, reason: `http_${res.status}` }
    const data = await res.json().catch(() => ({}))
    return { ok: !!data.ok }
  } catch {
    return { ok: false, reason: 'network' }
  }
}

/** Authed: confirm the 6-digit code. On ok the backend has already flipped the
 *  Firebase account to verified (Admin API) — the caller then reload()s to pick
 *  up the claim. `reason` carries wrong_code / expired / etc. Never throws. */
export async function verifyEmailCode(
  code: string,
): Promise<{ ok: boolean; reason?: string }> {
  try {
    const res = await fetch(`${API_BASE}/api/auth/verify-code`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(await getAuthHeaders()) },
      body: JSON.stringify({ code }),
    })
    const data = await res.json().catch(() => ({}))
    return { ok: res.ok && !!data.ok, reason: data.reason }
  } catch {
    return { ok: false, reason: 'network' }
  }
}

/** Public: email a password-reset link for `email`. Never throws. */
export async function sendResetEmail(
  email: string,
  origin: string,
): Promise<{ ok: boolean; reason?: string }> {
  try {
    const res = await fetch(`${API_BASE}/api/auth/send-reset`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, origin }),
    })
    if (res.status === 429) return { ok: false, reason: 'rate_limited' }
    if (!res.ok) return { ok: false, reason: `http_${res.status}` }
    return await res.json()
  } catch {
    return { ok: false, reason: 'network' }
  }
}

export async function generateImage(params: GenerateParams): Promise<GenerateImageResult> {
  const formData = new FormData()
  formData.append('product_image', params.productFile)
  formData.append('template_id', params.templateId)
  formData.append('frame_number', String(params.frameNumber))
  formData.append('aspect_ratio', params.aspectRatio)
  formData.append('product_name', params.productName)
  formData.append('product_description', params.productDescription)
  formData.append('quality', params.quality)
  formData.append('provider', params.provider ?? 'gemini')
  if (params.garmentType) {
    formData.append('garment_type', params.garmentType)
  }
  if (params.bgHex) {
    formData.append('bg_hex', params.bgHex)
  }
  if (params.hasHumanModel) {
    formData.append('has_human_model', 'true')
  }
  formData.append('product', params.productFile)

  // `fetch` throws TypeError on outright network failure (server down, DNS,
  // offline). Wrap so the UI sees a consistent NetworkError instead of a
  // raw "TypeError: Failed to fetch".
  let response: Response
  try {
    response = await fetch(`${API_BASE}/api/generate`, {
      method: 'POST',
      body: formData,
      headers: await getAuthHeaders(),
    })
  } catch (err) {
    throw new NetworkError(
      err instanceof Error ? `Network error: ${err.message}` : 'Network error',
    )
  }

  if (!response.ok) {
    throw await mapFailure(response)
  }

  const data = await response.json()
  return {
    imageUrl: `data:image/png;base64,${data.image_b64}`,
    cost: data._cost ?? 0,
    creditsConsumed: data.credits_consumed ?? 0,
    // Fallback to the request's provider in case an older backend doesn't
    // echo it back — keeps the UI label accurate.
    provider: (data.provider ?? params.provider ?? 'openai') as Provider,
  }
}

export type ComposeSceneParams = {
  productFile: File
  /** The template's background scene image (Blob fetched/decoded by the caller). */
  sceneBlob: Blob
  productName: string
  productDescription: string
  quality?: 'low' | 'medium' | 'high'
  /** Optional product-slot rect (canvas coords) → mask-based placement:
   *  the product is confined to + fills this region. */
  slot?: { x: number; y: number; w: number; h: number }
  /** Canvas size the slot coords are in (default 1080). */
  canvasSize?: number
}

export type ComposeSceneResult = {
  imageUrl: string
  cost: number
  creditsConsumed: number
}

/** Scene-merge compose: AI-composite the product into a template's
 *  background scene (for composeMode='scene-merge' templates). Returns
 *  the merged image as a data URL. */
export async function composeScene(params: ComposeSceneParams): Promise<ComposeSceneResult> {
  const formData = new FormData()
  formData.append('product', params.productFile)
  formData.append('scene', params.sceneBlob, 'scene.png')
  formData.append('product_name', params.productName)
  formData.append('product_description', params.productDescription)
  formData.append('quality', params.quality ?? 'low')
  if (params.slot) {
    formData.append('slot_x', String(params.slot.x))
    formData.append('slot_y', String(params.slot.y))
    formData.append('slot_w', String(params.slot.w))
    formData.append('slot_h', String(params.slot.h))
    formData.append('canvas_size', String(params.canvasSize ?? 1080))
  }

  let response: Response
  try {
    response = await fetch(`${API_BASE}/api/compose-scene`, {
      method: 'POST',
      body: formData,
      headers: await getAuthHeaders(),
    })
  } catch (err) {
    throw new NetworkError(
      err instanceof Error ? `Network error: ${err.message}` : 'Network error',
    )
  }

  if (!response.ok) {
    throw await mapFailure(response)
  }

  const data = await response.json()
  return {
    imageUrl: `data:image/png;base64,${data.image_b64}`,
    cost: data._cost ?? 0,
    creditsConsumed: data.credits_consumed ?? 0,
  }
}

export type SuggestCopyParams = {
  productFile: File
  productName: string
  productDescription: string
  frameCount: number
}

export type SuggestCopyResult = {
  frames: Array<{ headline: string; tagline: string }>
  _cost?: number
}

/** 12-slot copy schema for the Social Post designed templates. Mirrors the
 *  `tweaks` object in the Claude Design source. AI fills all 10 slots
 *  (headline + 9 supporting fields); user can edit any in the Tweaks Panel.
 *  badge_stars is intentionally a string ("★★★★★") so it works as a
 *  display label even without real review data. */
export type SocialPostSlots = {
  // ---- Always-AI-filled (suggest-copy returns all of these) --------------
  eyebrow:     string   // Builder label: "Subtitle" (small kicker above headline)
  headline:    string   // can contain <em>…</em> for italic emphasis
  subhead:     string   // Builder label: "Subheading"
  bullet1:     string
  bullet2:     string
  bullet3:     string
  cta:         string
  handle:      string
  badge_stars: string
  badge_text:  string
  // NOT AI-filled — injected from the wizard's productName state in
  // StepSocialFinal's effectiveSlots. Bind a text layer to this slot
  // (layer named "product_name" in the Figma plugin) to show the
  // actual product name the user entered/scraped.
  product_name?: string
  // ---- AI-filled additions (B-2.3) ---------------------------------------
  // body: a 1-2 sentence product paragraph. bullet_list: 3-5 short
  // newline-separated benefit bullets — the renderer splits on \n and
  // prepends a bullet glyph per line, producing a real <ul>-style list
  // instead of three separate text elements. Both are optional in the
  // type for legacy compat but ARE filled by suggest-copy.
  body?:         string
  bullet_list?:  string
  // ---- Optional brand / scaffolding fields --------------------------------
  // Used by the designed templates that include brand-wordmark, issue
  // labels, or product captions in their layout. Suggest-copy doesn't
  // fill these, so they ship with sensible defaults and the user edits
  // them in the Tweaks panel. Each design uses whichever subset fits
  // its layout — designs ignore the fields they don't need.
  brand_name?:    string   // Mono brand bar, Botanical wordmark
  brand_tagline?: string   // Mono "skin science", Botanical "est. 2019"
  issue_label?:   string   // Editorial "Édition Nº01", Mono date "SS · 26"
  caption_left?:  string   // Mono "Fig. 01", Botanical "Vol. 01 — Renewal"
  caption_right?: string   // Mono "The serum", Botanical "50ml"
  // ---- Generic user-defined labels (B-2.3) -------------------------------
  // For JSON templates whose content doesn't map cleanly to any of the
  // named slots above. The builder dropdown shows a single "Label"
  // entry that auto-assigns the next free label_N (1..8). Tweaks panel
  // displays only the label_N entries actually bound by the template.
  // AI-filled when the template provides a hint via the element's
  // staticText (passed to suggest-copy as label_hints). Otherwise the
  // user types content in the Tweaks panel.
  label_1?: string
  label_2?: string
  label_3?: string
  label_4?: string
  label_5?: string
  label_6?: string
  label_7?: string
  label_8?: string
}

export type SuggestSocialCopyParams = {
  productFile?: File | null    // optional — backend can work with just name+description
  productName: string
  productDescription: string
  /** Optional map of label_N → hint string. Pulled from a JSON
   *  template's elements (each label-bound element's staticText is
   *  the hint). The AI uses each hint to produce product-adapted
   *  content matching the hint's intent. Without hints, the backend
   *  leaves label_N empty. */
  labelHints?: Partial<Record<`label_${1 | 2 | 3 | 4 | 5 | 6 | 7 | 8}`, string>>
}

export type SuggestSocialCopyResult = {
  slots: SocialPostSlots
  _cost?: number
}

export async function suggestSocialCopy(params: SuggestSocialCopyParams): Promise<SuggestSocialCopyResult> {
  const formData = new FormData()
  // `product` is optional but the backend route shape (built on suggest-copy)
  // expects the field. Send a tiny empty stub when no file is provided so
  // the multipart parse doesn't 422.
  if (params.productFile) {
    formData.append('product', params.productFile)
  } else {
    formData.append('product', new Blob([''], { type: 'text/plain' }), 'noproduct.txt')
  }
  formData.append('product_name', params.productName)
  formData.append('product_description', params.productDescription)
  formData.append('frame_count', '1')
  formData.append('format', 'social-post-12-slot')
  // Optional label hints — backend uses each hint as a content guide
  // for the corresponding label_N slot. Serialized as JSON so the
  // FastAPI handler can parse and inject into the prompt.
  if (params.labelHints && Object.keys(params.labelHints).length > 0) {
    formData.append('label_hints', JSON.stringify(params.labelHints))
  }

  let response: Response
  try {
    response = await fetch(`${API_BASE}/api/suggest-copy`, {
      method: 'POST',
      body: formData,
      headers: await getAuthHeaders(),
    })
  } catch (err) {
    throw new NetworkError(
      err instanceof Error ? `Network error: ${err.message}` : 'Network error',
    )
  }

  if (!response.ok) {
    throw await mapFailure(response)
  }

  return await response.json()
}


export async function suggestCopy(params: SuggestCopyParams): Promise<SuggestCopyResult> {
  const formData = new FormData()
  formData.append('product', params.productFile)
  formData.append('product_name', params.productName)
  formData.append('product_description', params.productDescription)
  formData.append('frame_count', String(params.frameCount))

  let response: Response
  try {
    response = await fetch(`${API_BASE}/api/suggest-copy`, {
      method: 'POST',
      body: formData,
      headers: await getAuthHeaders(),
    })
  } catch (err) {
    throw new NetworkError(
      err instanceof Error ? `Network error: ${err.message}` : 'Network error',
    )
  }

  if (!response.ok) {
    throw await mapFailure(response)
  }

  return await response.json()  // already includes _cost
}

export type AnalyzeProductResult = {
  name: string
  description: string
  /** Auto-classified garment category. 'none' for non-clothing items. The
   *  Fashion route uses this to pick the matching mask region; other
   *  routes ignore it. */
  garmentType?: 'top' | 'bottoms' | 'shoes' | 'none'
  /** Restricted-content flag. The Fashion compositing pipeline can't
   *  process swimwear / lingerie / underwear / sheer items (OpenAI's
   *  content policy rejects them, and they're off-brand for the kind
   *  of model templates we ship). Detected by the same vision call as
   *  garmentType so we gate at Step 3 instead of letting the user
   *  burn credits hitting a 400 at generate-time. 'none' means the
   *  item is OK to process (normal clothing OR not a garment at all). */
  garmentRestriction?: 'swimwear' | 'lingerie' | 'underwear' | 'sheer' | 'none'
  /** True if the image contains a human model / person as a primary
   *  subject. Used by the Product Shot route to soft-warn the user that
   *  the Fashion route (model-try-on) is probably what they want
   *  instead — Product Shot is designed for standalone product photos. */
  hasHumanModel?: boolean
  /** Coarse product category from a fixed vocabulary (beauty, skincare,
   *  fragrance, candle, wellness, jewelry, food, beverage, fashion,
   *  accessory, tech, home, other). Used by StepBackground's
   *  "Recommended for you" group to filter scenes whose tags include
   *  this category. */
  productCategory?: string
  /** 3–6 vibe tags constrained to the same vocabulary scene.tags use.
   *  Drives recommendation scoring (tag-overlap → relevance). */
  tags?: string[]
  /** Product form-factor / silhouette. The Product Swap route uses this
   *  to recommend templates whose held placeholder matches the product's
   *  shape (a tall serum bottle vs a squat cream jar fill the mask very
   *  differently). 'other' = no good match. Other routes ignore it. */
  productSilhouette?:
    | 'tall-bottle'
    | 'pump-bottle'
    | 'dropper'
    | 'short-jar'
    | 'tube'
    | 'box'
    | 'other'
  _cost?: number
}

export async function analyzeProduct(productFile: File): Promise<AnalyzeProductResult> {
  const formData = new FormData()
  formData.append('product', productFile)

  let response: Response
  try {
    response = await fetch(`${API_BASE}/api/analyze-product`, {
      method: 'POST',
      body: formData,
      headers: await getAuthHeaders(),
    })
  } catch (err) {
    throw new NetworkError(
      err instanceof Error ? `Network error: ${err.message}` : 'Network error',
    )
  }

  if (!response.ok) {
    throw await mapFailure(response)
  }

  return await response.json()  // already includes _cost from backend
}

/** Product page URL → name + description + image. Backend parses
 *  OpenGraph + JSON-LD on the target page. Used by StepUpload as an
 *  alternative to manual file upload. Falls back to manual on any error
 *  (caller catches the typed error and surfaces the fallback CTA). */
export type ScrapeProductResult = {
  /** Base64-encoded image bytes (no data: prefix). Caller wraps into a
   *  File so the rest of the wizard treats this identically to a manually
   *  uploaded image. */
  imageBase64: string
  name: string
  description: string
  sourceUrl: string
  /** Cheap text-only classification run on the scraped name + description.
   *  Same shape as AnalyzeProductResult so StepBackground's recommendation
   *  logic works identically whether the product came via URL scrape or
   *  manual upload. Empty string / empty array when classification failed
   *  (the scrape itself still succeeds — recommendations just degrade
   *  to the empty-state UI). */
  productCategory?: string
  tags?: string[]
  _cost?: number
}

export async function scrapeProduct(url: string): Promise<ScrapeProductResult> {
  let response: Response
  try {
    response = await fetch(`${API_BASE}/api/scrape-product`, {
      method: 'POST',
      body: JSON.stringify({ url }),
      headers: {
        ...(await getAuthHeaders()),
        'Content-Type': 'application/json',
      },
    })
  } catch (err) {
    throw new NetworkError(
      err instanceof Error ? `Network error: ${err.message}` : 'Network error',
    )
  }

  if (!response.ok) {
    throw await mapFailure(response)
  }

  const data = await response.json()
  return {
    imageBase64: data.image_b64,
    name: data.name ?? '',
    description: data.description ?? '',
    sourceUrl: data.source_url ?? url,
    productCategory: data.productCategory ?? undefined,
    tags: Array.isArray(data.tags) ? data.tags : undefined,
    _cost: typeof data._cost === 'number' ? data._cost : undefined,
  }
}

// ── Public "Try It" teaser (no auth) ────────────────────────────────────────
// Anonymous endpoints for the /try landing page. NO getAuthHeaders() — these are
// hit by signed-out ad visitors and are auth-exempt on the backend (see
// backend/try_flow/). 429 → TransientUpstreamError (the cookie/IP guard).

export interface TryGenerateResult {
  product: { name: string; image_url: string }
  frames: string[] // base64 PNGs
}

/** Stable per-browser id for the teaser's cookie-primary run guard. */
export function getTryCookieId(): string {
  const gen = () =>
    (typeof crypto !== 'undefined' && 'randomUUID' in crypto)
      ? crypto.randomUUID()
      : `t_${Date.now()}_${Math.random().toString(36).slice(2)}`
  try {
    const k = 'sp_try_cookie'
    let v = localStorage.getItem(k)
    if (!v) {
      v = gen()
      localStorage.setItem(k, v)
    }
    return v
  } catch {
    return gen()
  }
}

export async function tryGenerate(url: string, cookieId: string): Promise<TryGenerateResult> {
  let response: Response
  try {
    response = await fetch(`${API_BASE}/api/try/generate`, {
      method: 'POST',
      body: JSON.stringify({ url, cookie_id: cookieId }),
      headers: { 'Content-Type': 'application/json' },
    })
  } catch (err) {
    throw new NetworkError(
      err instanceof Error ? `Network error: ${err.message}` : 'Network error',
    )
  }
  if (!response.ok) throw await mapFailure(response)
  const data = await response.json()
  return {
    product: {
      name: data?.product?.name ?? 'Your product',
      image_url: data?.product?.image_url ?? '',
    },
    frames: Array.isArray(data?.frames) ? data.frames : [],
  }
}

export async function tryLead(email: string, url: string): Promise<void> {
  let response: Response
  try {
    response = await fetch(`${API_BASE}/api/try/lead`, {
      method: 'POST',
      body: JSON.stringify({ email, url }),
      headers: { 'Content-Type': 'application/json' },
    })
  } catch (err) {
    throw new NetworkError(
      err instanceof Error ? `Network error: ${err.message}` : 'Network error',
    )
  }
  if (!response.ok) throw await mapFailure(response)
}

// ---------- Stripe Checkout ----------
// Creates a Stripe-hosted Checkout Session on the backend and returns the
// URL we should send the user to. Frontend then does `window.location.href =
// url` (or anchor click) to redirect — DO NOT fetch() the URL.
//
// `planId` matches PLANS[*].id from useSubscription.ts plus 'topup-50' for
// the one-time pack.

export type CheckoutPlanId = 'starter' | 'pro' | 'business' | 'topup-50'

export interface CheckoutSessionResult {
  url: string
  sessionId: string
}

export type CheckoutInterval = 'monthly' | 'yearly'

export async function createCheckoutSession(
  planId: CheckoutPlanId,
  interval: CheckoutInterval = 'monthly',
): Promise<CheckoutSessionResult> {
  let response: Response
  try {
    response = await fetch(`${API_BASE}/api/checkout/create-session`, {
      method: 'POST',
      body: JSON.stringify({ plan_id: planId, interval }),
      headers: {
        ...(await getAuthHeaders()),
        'Content-Type': 'application/json',
      },
    })
  } catch (err) {
    throw new NetworkError(
      err instanceof Error ? `Network error: ${err.message}` : 'Network error',
    )
  }

  if (!response.ok) {
    throw await mapFailure(response)
  }

  const data = await response.json()
  return {
    url: data.url,
    sessionId: data.session_id,
  }
}

// ---------- Stripe Customer Portal ----------
// Creates a Stripe Customer Portal session and returns the URL. Use for
// existing subscribers who want to: cancel, switch plans, update payment
// method, or download invoices. Frontend redirects via window.location.href.
// 404 means the user has never subscribed (no Stripe customer yet) —
// caller should send them through the regular checkout flow instead.

export async function createCustomerPortalSession(): Promise<{ url: string }> {
  let response: Response
  try {
    response = await fetch(`${API_BASE}/api/customer-portal/create-session`, {
      method: 'POST',
      headers: {
        ...(await getAuthHeaders()),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({}),
    })
  } catch (err) {
    throw new NetworkError(
      err instanceof Error ? `Network error: ${err.message}` : 'Network error',
    )
  }

  if (!response.ok) {
    throw await mapFailure(response)
  }

  const data = await response.json()
  return { url: data.url }
}

/** Activate a free trial immediately — ends the Stripe trial now so the card is
 *  charged and the user gets their full monthly credits (webhook does the grant).
 *  For a trialing user who's out of credits but still has trial days left. */
export async function activateTrial(): Promise<{ ok: boolean }> {
  let response: Response
  try {
    response = await fetch(`${API_BASE}/api/billing/activate-trial`, {
      method: 'POST',
      headers: {
        ...(await getAuthHeaders()),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({}),
    })
  } catch (err) {
    throw new NetworkError(
      err instanceof Error ? `Network error: ${err.message}` : 'Network error',
    )
  }

  if (!response.ok) {
    throw await mapFailure(response)
  }

  const data = await response.json()
  return { ok: !!data.ok }
}

// Video generation API wrappers (generateVideo, videoStatus + types
// VideoAspect, VideoJobState, VideoStatusResponse, GenerateVideoResponse,
// GenerateVideoParams) were removed on May 26, 2026 when the video route
// was stripped. See VIDEO-RESEARCH.md at the repo root for the R&D archive.


// ============================================================
// Background removal — /api/remove-bg
// ============================================================
// Strips the background from a product image via local rembg
// (server-side). Used by the Social Post route's "Confirm cutout"
// step. Free — no credit cost. Optionally accepts pixel-space crop
// coords so the user can refine the cutout region when auto fails.

export interface RemoveBgResult {
  /** Base64-encoded transparent PNG bytes (no data: prefix). */
  imageBase64: string
  _cost?: number
}

export interface RemoveBgParams {
  productFile: File
  /** Optional pixel-space crop into the original image. When all four
   *  fields are set, rembg runs on the cropped region only. Useful when
   *  the auto cutout pulls in background elements — user draws a
   *  tighter box around the product to give the model less noise. */
  crop?: { x: number; y: number; w: number; h: number }
  /** Enable rembg's pymatting-based edge refinement. Slower (~2-5x)
   *  but produces noticeably cleaner edges, especially on fuzzy /
   *  glass / hair products. Still local + $0. Off by default —
   *  toggle via the "Polish edges" button. */
  alphaMatting?: boolean
}

export async function removeBg(params: RemoveBgParams): Promise<RemoveBgResult> {
  const form = new FormData()
  form.append('product', params.productFile)
  if (params.crop) {
    form.append('crop_x', String(Math.round(params.crop.x)))
    form.append('crop_y', String(Math.round(params.crop.y)))
    form.append('crop_w', String(Math.round(params.crop.w)))
    form.append('crop_h', String(Math.round(params.crop.h)))
  }
  if (params.alphaMatting) {
    form.append('alpha_matting', 'true')
  }
  let response: Response
  try {
    response = await fetch(`${API_BASE}/api/remove-bg`, {
      method: 'POST',
      body: form,
      headers: await getAuthHeaders(),
    })
  } catch (err) {
    throw new NetworkError(
      err instanceof Error ? `Network error: ${err.message}` : 'Network error',
    )
  }
  if (!response.ok) {
    throw await mapFailure(response)
  }
  const data = await response.json()
  return {
    imageBase64: data.image_b64,
    _cost: typeof data._cost === 'number' ? data._cost : 0,
  }
}


// ============================================================
// Brand profile
// ============================================================
// Per-user brand assets (name, brief, logo, color palette). Backs the
// /brand page. See backend/scripts/user_brand_profile.sql for schema.

/**
 * Brand font with Google-Fonts substitution provenance (Jun 15).
 *
 * `detected` is the original font name pulled from the website scrape.
 * `substitute` is what we'll actually render with — always one of the
 * 40-font Google Fonts catalog (we can ONLY use SIL-OFL fonts in shipped
 * outputs). `method` tells the UI why this substitution was chosen.
 */
export interface BrandFontEntry {
  detected: string
  substitute: string
  method: 'exact-google' | 'fast-path' | 'llm' | 'fallback' | 'manual-swap'
  source: string | null
}

export interface ScrapeBrandResult {
  name: string
  brief: string
  logoBase64: string | null   // data URI (data:image/...;base64,...) or null
  colors: string[]             // hex strings, e.g. ['#1A73E8', '#FBBC04']
  sourceUrl: string
  /** @deprecated Use `fonts` instead. Kept for back-compat with the old
   *  wizard brand-review screen. Flat list of detected font names. */
  typography: string[]
  /** Jun 15 — each entry pairs the detected font with its Google-Fonts
   *  substitute. `fonts.length === typography.length` (1:1). */
  fonts: BrandFontEntry[]
  /** Jun 22 — homepage favicon URL (apple-touch-icon → rel=icon → /favicon.ico).
   *  Optional, best-effort. Separate from logo — used for browser tab / app
   *  accent etc., NOT a logo fallback. Null when nothing useful was found. */
  faviconUrl: string | null
  /** Jul 11 — brand imagery URLs (campaign / lifestyle shots) parsed from the
   *  homepage HTML. Shown in the review screen's "Images" section. Best-effort,
   *  may be empty. */
  images: string[]
}

export async function scrapeBrand(url: string): Promise<ScrapeBrandResult> {
  let response: Response
  try {
    response = await fetch(`${API_BASE}/api/scrape-brand`, {
      method: 'POST',
      body: JSON.stringify({ url }),
      headers: {
        ...(await getAuthHeaders()),
        'Content-Type': 'application/json',
      },
    })
  } catch (err) {
    throw new NetworkError(
      err instanceof Error ? `Network error: ${err.message}` : 'Network error',
    )
  }
  if (!response.ok) {
    throw await mapFailure(response)
  }
  const data = await response.json()
  return {
    name: data.name ?? '',
    brief: data.brief ?? '',
    logoBase64: data.logo_base64 ?? null,
    colors: Array.isArray(data.colors) ? data.colors : [],
    sourceUrl: data.source_url ?? '',
    typography: Array.isArray(data.typography) ? data.typography : [],
    fonts: coerceBrandFonts(data.fonts),
    faviconUrl: data.favicon_url ?? null,
    images: Array.isArray(data.images) ? data.images : [],
  }
}


/** Defensive coerce for the `fonts` JSON array from the backend. The
 *  shape is enforced by Pydantic on the server, but a malformed entry
 *  shouldn't kill the whole brand-review render. */
function coerceBrandFonts(raw: unknown): BrandFontEntry[] {
  if (!Array.isArray(raw)) return []
  const out: BrandFontEntry[] = []
  for (const entry of raw) {
    if (!entry || typeof entry !== 'object') continue
    const e = entry as Record<string, unknown>
    const detected = typeof e.detected === 'string' ? e.detected : ''
    const substitute = typeof e.substitute === 'string' ? e.substitute : 'Inter'
    if (!detected) continue
    const method = (e.method as BrandFontEntry['method']) ?? 'fallback'
    out.push({
      detected,
      substitute,
      method,
      source: typeof e.source === 'string' ? e.source : null,
    })
  }
  return out
}


export interface BrandProfile {
  name: string | null
  brief: string | null
  logoUrl: string | null      // public URL to the logo PNG (or null)
  colors: string[]
  fonts: BrandFontEntry[]     // Jun 15
  sourceUrl: string | null
  faviconUrl: string | null   // Jun 22 — separate from logo, optional
  updatedAt: string | null
}

export async function getBrand(): Promise<BrandProfile | null> {
  let response: Response
  try {
    response = await fetch(`${API_BASE}/api/brand`, {
      headers: await getAuthHeaders(),
    })
  } catch (err) {
    throw new NetworkError(
      err instanceof Error ? `Network error: ${err.message}` : 'Network error',
    )
  }
  if (response.status === 404) return null
  if (!response.ok) {
    throw await mapFailure(response)
  }
  const data = await response.json()
  if (!data) return null
  return {
    name: data.name ?? null,
    brief: data.brief ?? null,
    logoUrl: data.logo_url ?? null,
    colors: Array.isArray(data.colors) ? data.colors : [],
    fonts: coerceBrandFonts(data.fonts),
    sourceUrl: data.source_url ?? null,
    faviconUrl: data.favicon_url ?? null,
    updatedAt: data.updated_at ?? null,
  }
}


// ── Brand imagery (Brand DNA source) ────────────────────────────────────
// Scraped promo / lifestyle photography the user curates (keep/delete). A later
// phase merges the kept set into a single "Brand DNA" spec attached to prompts.

export interface BrandImage {
  id: string
  url: string          // public GCS url
  sourceUrl: string    // original scraped URL
  kind: string         // vision verdict (editorial/model/scene/lifestyle)
  hasText: boolean     // carries campaign copy (strong DNA signal)
  score: number
  kept: boolean
  width: number | null
  height: number | null
}

export interface BrandImageryResult {
  images: BrandImage[]
  /** False when the brands.imagery column isn't migrated yet — scrape/review
   *  still work in-memory, they just don't persist across reloads. */
  persisted: boolean
}

function mapBrandImage(d: any): BrandImage {
  return {
    id: String(d.id),
    url: d.url ?? '',
    sourceUrl: d.source_url ?? '',
    kind: d.kind ?? '',
    hasText: Boolean(d.has_text),
    score: typeof d.score === 'number' ? d.score : 0,
    kept: d.kept !== false,
    width: d.width ?? null,
    height: d.height ?? null,
  }
}

function mapBrandImagery(data: any): BrandImageryResult {
  return {
    images: Array.isArray(data?.images) ? data.images.map(mapBrandImage) : [],
    persisted: data?.persisted !== false,
  }
}

export async function getBrandImagery(): Promise<BrandImageryResult> {
  let response: Response
  try {
    response = await fetch(`${API_BASE}/api/brand/imagery`, {
      headers: await getAuthHeaders(),
    })
  } catch (err) {
    throw new NetworkError(
      err instanceof Error ? `Network error: ${err.message}` : 'Network error',
    )
  }
  if (!response.ok) throw await mapFailure(response)
  return mapBrandImagery(await response.json())
}

// ── Brand Imagery — "more of the same" variation generation ─────────────────
export interface BrandImageryGenerateResult {
  post_id: string
  image_url: string
}

/** Generate ONE on-brand variation of the selected brand image. `withProduct`
 *  keeps the product in frame; otherwise a product-free scene variation. `text`
 *  (optional) replaces existing on-image copy or adds new copy; empty keeps the
 *  reference's own behavior. Saves to My Creatives; returns the new post id so
 *  the caller can open it in the editor. 1 credit. */
export async function generateBrandImagery(
  imageId: string,
  withProduct: boolean,
  text = '',
  aspectRatio = '1:1',
  cameraAngle = '',
): Promise<BrandImageryGenerateResult> {
  let response: Response
  try {
    response = await fetch(`${API_BASE}/api/brand-imagery/generate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(await getAuthHeaders()) },
      body: JSON.stringify({
        image_id: imageId,
        with_product: withProduct,
        text: text.trim() || undefined,
        camera_angle: cameraAngle || undefined,
        aspect_ratio: aspectRatio,
      }),
    })
  } catch (err) {
    throw new NetworkError(
      err instanceof Error ? `Network error: ${err.message}` : 'Network error',
    )
  }
  if (!response.ok) throw await mapFailure(response)
  const data = await response.json()
  return {
    post_id: String(data?.post_id ?? ''),
    image_url: String(data?.image_url ?? ''),
  }
}

/** Scrape the brand's homepage (defaults to its stored source URL) → returns
 *  the merged, curated image set. Non-destructive: re-running only adds finds. */
export async function scrapeBrandImagery(
  input: { url?: string; limit?: number; imageUrls?: string[] } = {},
): Promise<BrandImageryResult> {
  let response: Response
  try {
    response = await fetch(`${API_BASE}/api/brand/imagery/scrape`, {
      method: 'POST',
      body: JSON.stringify({
        url: input.url ?? null,
        limit: input.limit ?? 10,
        image_urls: input.imageUrls && input.imageUrls.length > 0 ? input.imageUrls : null,
      }),
      headers: { ...(await getAuthHeaders()), 'Content-Type': 'application/json' },
    })
  } catch (err) {
    throw new NetworkError(
      err instanceof Error ? `Network error: ${err.message}` : 'Network error',
    )
  }
  if (!response.ok) throw await mapFailure(response)
  return mapBrandImagery(await response.json())
}

/** Upload one brand photo → GCS + brands.imagery (kept=true). The honest
 *  fallback when scraping couldn't pull imagery. Returns the merged set. */
export async function uploadBrandImage(file: File): Promise<BrandImageryResult> {
  const form = new FormData()
  form.append('file', file)
  let response: Response
  try {
    response = await fetch(`${API_BASE}/api/brand/imagery/upload`, {
      method: 'POST',
      body: form,
      headers: await getAuthHeaders(),
    })
  } catch (err) {
    throw new NetworkError(
      err instanceof Error ? `Network error: ${err.message}` : 'Network error',
    )
  }
  if (!response.ok) throw await mapFailure(response)
  return mapBrandImagery(await response.json())
}

/** Set the kept flag from a full kept-id set (ids in → kept, others → unkept). */
export async function patchBrandImagery(keptIds: string[]): Promise<BrandImageryResult> {
  let response: Response
  try {
    response = await fetch(`${API_BASE}/api/brand/imagery`, {
      method: 'PATCH',
      body: JSON.stringify({ kept_ids: keptIds }),
      headers: { ...(await getAuthHeaders()), 'Content-Type': 'application/json' },
    })
  } catch (err) {
    throw new NetworkError(
      err instanceof Error ? `Network error: ${err.message}` : 'Network error',
    )
  }
  if (!response.ok) throw await mapFailure(response)
  return mapBrandImagery(await response.json())
}

export async function deleteBrandImage(id: string): Promise<BrandImageryResult> {
  let response: Response
  try {
    response = await fetch(`${API_BASE}/api/brand/imagery/${encodeURIComponent(id)}`, {
      method: 'DELETE',
      headers: await getAuthHeaders(),
    })
  } catch (err) {
    throw new NetworkError(
      err instanceof Error ? `Network error: ${err.message}` : 'Network error',
    )
  }
  if (!response.ok) throw await mapFailure(response)
  return mapBrandImagery(await response.json())
}


// ── Brand DNA (merged visual-identity spec) ─────────────────────────────
// Distilled from the KEPT brand imagery: palette / lighting / composition /
// typography / mood / styling + a compact prompt fragment attached to render
// prompts when the brand-presence knob (enabled) is on.

export interface BrandDna {
  palette: string[]
  paletteWords: string
  lighting: string
  composition: string
  typography: string
  mood: string
  styling: string
  promptFragment: string
  summary: string
  imageCount?: number
}

export interface BrandDnaResult {
  dna: BrandDna | null
  enabled: boolean
  persisted: boolean
}

function mapBrandDna(d: any): BrandDna | null {
  if (!d || typeof d !== 'object') return null
  return {
    palette: Array.isArray(d.palette) ? d.palette : [],
    paletteWords: d.palette_words ?? '',
    lighting: d.lighting ?? '',
    composition: d.composition ?? '',
    typography: d.typography ?? '',
    mood: d.mood ?? '',
    styling: d.styling ?? '',
    promptFragment: d.prompt_fragment ?? '',
    summary: d.summary ?? '',
    imageCount: typeof d.image_count === 'number' ? d.image_count : undefined,
  }
}

function mapBrandDnaResult(data: any): BrandDnaResult {
  return {
    dna: mapBrandDna(data?.dna),
    enabled: data?.enabled !== false,
    persisted: data?.persisted !== false,
  }
}

export async function getBrandDna(): Promise<BrandDnaResult> {
  let response: Response
  try {
    response = await fetch(`${API_BASE}/api/brand/dna`, { headers: await getAuthHeaders() })
  } catch (err) {
    throw new NetworkError(
      err instanceof Error ? `Network error: ${err.message}` : 'Network error',
    )
  }
  if (!response.ok) throw await mapFailure(response)
  return mapBrandDnaResult(await response.json())
}

/** Distill a Brand DNA from the kept imagery. Pass image_urls to build from an
 *  explicit set (the wizard's in-memory kept set); omit to use brands.imagery. */
export async function buildBrandDna(imageUrls?: string[]): Promise<BrandDnaResult> {
  let response: Response
  try {
    response = await fetch(`${API_BASE}/api/brand/dna/build`, {
      method: 'POST',
      body: JSON.stringify({ image_urls: imageUrls ?? null }),
      headers: { ...(await getAuthHeaders()), 'Content-Type': 'application/json' },
    })
  } catch (err) {
    throw new NetworkError(
      err instanceof Error ? `Network error: ${err.message}` : 'Network error',
    )
  }
  if (!response.ok) throw await mapFailure(response)
  return mapBrandDnaResult(await response.json())
}

export async function setBrandDnaEnabled(enabled: boolean): Promise<BrandDnaResult> {
  let response: Response
  try {
    response = await fetch(`${API_BASE}/api/brand/dna`, {
      method: 'PATCH',
      body: JSON.stringify({ enabled }),
      headers: { ...(await getAuthHeaders()), 'Content-Type': 'application/json' },
    })
  } catch (err) {
    throw new NetworkError(
      err instanceof Error ? `Network error: ${err.message}` : 'Network error',
    )
  }
  if (!response.ok) throw await mapFailure(response)
  return mapBrandDnaResult(await response.json())
}


export interface SaveBrandInput {
  name?: string | null
  brief?: string | null
  colors?: string[] | null
  /** Jun 15 — full font entries including manual swaps. Each entry must
   *  carry detected + substitute so a manual swap-by-swap is reversible. */
  fonts?: BrandFontEntry[] | null
  sourceUrl?: string | null
  /** Jun 22 — separate from logo. Pass null/undefined to leave unchanged,
   *  "" to clear, a URL string to store. */
  faviconUrl?: string | null
  /** When `null` / undefined, leaves logo unchanged. Empty string deletes
   *  the existing logo. Otherwise pass a full data URI to upload. */
  logoBase64?: string | null
}

export async function saveBrand(input: SaveBrandInput): Promise<BrandProfile> {
  let response: Response
  try {
    response = await fetch(`${API_BASE}/api/brand`, {
      method: 'POST',
      body: JSON.stringify({
        name: input.name ?? null,
        brief: input.brief ?? null,
        colors: input.colors ?? null,
        fonts: input.fonts ?? null,
        source_url: input.sourceUrl ?? null,
        favicon_url: input.faviconUrl ?? null,
        logo_base64: input.logoBase64 ?? null,
      }),
      headers: {
        ...(await getAuthHeaders()),
        'Content-Type': 'application/json',
      },
    })
  } catch (err) {
    throw new NetworkError(
      err instanceof Error ? `Network error: ${err.message}` : 'Network error',
    )
  }
  if (!response.ok) {
    throw await mapFailure(response)
  }
  const data = await response.json()
  return {
    name: data.name ?? null,
    brief: data.brief ?? null,
    logoUrl: data.logo_url ?? null,
    colors: Array.isArray(data.colors) ? data.colors : [],
    fonts: coerceBrandFonts(data.fonts),
    sourceUrl: data.source_url ?? null,
    faviconUrl: data.favicon_url ?? null,
    updatedAt: data.updated_at ?? null,
  }
}


/**
 * Google Fonts catalog (Jun 15) — the curated 40-font list the FontSwapModal
 * presents when the user wants to change their brand font. Sorted by
 * popularity (lower = more popular) so the modal opens with top picks
 * above the fold.
 */
export interface GoogleFontCatalogEntry {
  family: string
  category: 'sans' | 'serif' | 'display' | 'handwriting' | 'monospace'
  classification: string
  xHeight: 'small' | 'medium' | 'large'
  popularity: number
}

let _catalogCache: GoogleFontCatalogEntry[] | null = null

export async function getGoogleFontsCatalog(): Promise<GoogleFontCatalogEntry[]> {
  // Cache for the session — the catalog is immutable per backend revision.
  if (_catalogCache) return _catalogCache

  let response: Response
  try {
    response = await fetch(`${API_BASE}/api/brand/fonts/catalog`, {
      headers: await getAuthHeaders(),
    })
  } catch (err) {
    throw new NetworkError(
      err instanceof Error ? `Network error: ${err.message}` : 'Network error',
    )
  }
  if (!response.ok) throw await mapFailure(response)
  const data = await response.json()
  const fonts = Array.isArray(data?.fonts) ? data.fonts : []
  _catalogCache = fonts.map((f: Record<string, unknown>) => ({
    family: String(f.family ?? ''),
    category: (f.category as GoogleFontCatalogEntry['category']) ?? 'sans',
    classification: String(f.classification ?? ''),
    xHeight: (f.x_height as GoogleFontCatalogEntry['xHeight']) ?? 'medium',
    popularity: Number(f.popularity ?? 999),
  }))
  return _catalogCache!
}


// ============================================================
// Brands — multi-brand support (/api/brands CRUD)
// ============================================================
// A user can own multiple brands. Every brand-scoped surface (My Brand,
// Products, Competitors, Creatives, Calendar, Ad Strategist, Auto-batches)
// filters by the user's active brand. Switching brand is server-side
// (POST /api/brands/:id/activate) → useBrand refetches → all data hooks
// re-fire on the new activeBrandId.

export interface BrandRecord {
  id: string
  name: string
  brief: string | null
  logoUrl: string | null
  colors: string[]
  fonts: BrandFontEntry[]       // Jun 15 — Google-Fonts-substituted entries
  sourceUrl: string | null
  faviconUrl: string | null     // Jun 22 — separate from logo, optional
  createdAt: string | null
  updatedAt: string | null
  isActive: boolean
}

function rowToBrandRecord(row: any): BrandRecord {
  return {
    id: row.id,
    name: row.name ?? 'My Brand',
    brief: row.brief ?? null,
    logoUrl: row.logo_url ?? null,
    colors: Array.isArray(row.colors) ? row.colors : [],
    fonts: coerceBrandFonts(row.fonts),
    sourceUrl: row.source_url ?? null,
    faviconUrl: row.favicon_url ?? null,
    createdAt: row.created_at ?? null,
    updatedAt: row.updated_at ?? null,
    isActive: !!row.is_active,
  }
}

export async function listBrands(): Promise<BrandRecord[]> {
  let response: Response
  try {
    response = await fetch(`${API_BASE}/api/brands`, {
      headers: await getAuthHeaders(),
    })
  } catch (err) {
    throw new NetworkError(
      err instanceof Error ? `Network error: ${err.message}` : 'Network error',
    )
  }
  if (!response.ok) throw await mapFailure(response)
  const data = await response.json()
  return Array.isArray(data) ? data.map(rowToBrandRecord) : []
}

export async function createBrand(name?: string): Promise<BrandRecord> {
  let response: Response
  try {
    response = await fetch(`${API_BASE}/api/brands`, {
      method: 'POST',
      body: JSON.stringify({ name: name ?? null }),
      headers: {
        ...(await getAuthHeaders()),
        'Content-Type': 'application/json',
      },
    })
  } catch (err) {
    throw new NetworkError(
      err instanceof Error ? `Network error: ${err.message}` : 'Network error',
    )
  }
  if (!response.ok) throw await mapFailure(response)
  return rowToBrandRecord(await response.json())
}

export interface PatchBrandInput {
  name?: string | null
  brief?: string | null
  colors?: string[] | null
  fonts?: BrandFontEntry[] | null   // Jun 15
  sourceUrl?: string | null
  /** Jun 22 — null/undefined leaves unchanged; "" clears; URL stores. */
  faviconUrl?: string | null
  /** null/undefined leaves the logo unchanged; "" deletes; data URI uploads. */
  logoBase64?: string | null
}

export async function patchBrand(brandId: string, input: PatchBrandInput): Promise<BrandRecord> {
  let response: Response
  try {
    response = await fetch(`${API_BASE}/api/brands/${brandId}`, {
      method: 'PATCH',
      body: JSON.stringify({
        name: input.name ?? null,
        brief: input.brief ?? null,
        colors: input.colors ?? null,
        fonts: input.fonts ?? null,
        source_url: input.sourceUrl ?? null,
        favicon_url: input.faviconUrl ?? null,
        logo_base64: input.logoBase64 ?? null,
      }),
      headers: {
        ...(await getAuthHeaders()),
        'Content-Type': 'application/json',
      },
    })
  } catch (err) {
    throw new NetworkError(
      err instanceof Error ? `Network error: ${err.message}` : 'Network error',
    )
  }
  if (!response.ok) throw await mapFailure(response)
  return rowToBrandRecord(await response.json())
}

export async function deleteBrand(brandId: string): Promise<{ activeBrandId: string | null }> {
  let response: Response
  try {
    response = await fetch(`${API_BASE}/api/brands/${brandId}`, {
      method: 'DELETE',
      headers: await getAuthHeaders(),
    })
  } catch (err) {
    throw new NetworkError(
      err instanceof Error ? `Network error: ${err.message}` : 'Network error',
    )
  }
  if (!response.ok) throw await mapFailure(response)
  const data = await response.json()
  return { activeBrandId: data?.active_brand_id ?? null }
}

export async function activateBrand(brandId: string): Promise<{ activeBrandId: string }> {
  let response: Response
  try {
    response = await fetch(`${API_BASE}/api/brands/${brandId}/activate`, {
      method: 'POST',
      headers: await getAuthHeaders(),
    })
  } catch (err) {
    throw new NetworkError(
      err instanceof Error ? `Network error: ${err.message}` : 'Network error',
    )
  }
  if (!response.ok) throw await mapFailure(response)
  const data = await response.json()
  return { activeBrandId: data?.active_brand_id ?? brandId }
}


// ============================================================
// Brand Setup wizard — /api/brand-setup/{start,status,cancel}
// ============================================================
// Background job that renders N (default 20) AI Magic creatives for the
// active brand's products with rotating creative direction. Frontend
// polls /status every ~3s and shows thumbnails as they complete.

export interface BrandSetupStartResult {
  jobId: string
  targetCount: number
  /** Full plan size (20 by default). Gallery uses this to render the
   *  remaining `total - target` slots as "locked" placeholders the moment
   *  the user arrives, BEFORE backend stub inserts land. */
  totalCount: number
}

export interface BrandSetupRecentPost {
  id: string
  imagePath: string
  title: string | null
  createdAt: string | null
}

export type BrandSetupStatus = 'pending' | 'running' | 'completed' | 'failed' | 'cancelled'

export interface BrandSetupStatusResult {
  id: string
  status: BrandSetupStatus
  targetCount: number
  /** Full plan size (mirrors BrandSetupStartResult.totalCount). Gallery
   *  recovers this from /status on cold-load when sessionStorage was
   *  cleared. */
  totalCount: number
  completedCount: number
  failedCount: number
  recentPosts: BrandSetupRecentPost[]
  error: string | null
  createdAt: string | null
  /** ISO timestamp of the last progress write. Used to detect worker
   *  death — if status is 'running' but this hasn't advanced for several
   *  minutes, the gallery treats the job as effectively terminal. */
  lastProgressAt: string | null
}

export async function startBrandSetup(
  targetCount?: number,
  /** Plan returned by getBrandSetupPreview() — opaque to frontend, just
   *  passed through. When supplied, the backend worker + stub inserter
   *  skip per-slot _build_wizard_concept calls (LLM cost moved to
   *  /preview, where it happens during the wizard's existing loading
   *  screen). Omitting falls back to building concepts at render-time. */
  plan?: unknown[],
  /** Jun 15 — sub-selection of the brand's products to feature in the
   *  renders. When provided, the backend rebuilds the plan from JUST
   *  these products (cached plan from /preview is dropped — it may
   *  reference unpicked products). Empty/undefined = use all brand
   *  products (legacy behavior). */
  featuredProductIds?: string[],
): Promise<BrandSetupStartResult> {
  let response: Response
  try {
    response = await fetch(`${API_BASE}/api/brand-setup/start`, {
      method: 'POST',
      body: JSON.stringify({
        target_count: targetCount ?? null,
        plan: plan ?? null,
        featured_product_ids: featuredProductIds && featuredProductIds.length > 0
          ? featuredProductIds : null,
      }),
      headers: {
        ...(await getAuthHeaders()),
        'Content-Type': 'application/json',
      },
    })
  } catch (err) {
    throw new NetworkError(
      err instanceof Error ? `Network error: ${err.message}` : 'Network error',
    )
  }
  if (!response.ok) throw await mapFailure(response)
  const data = await response.json()
  return {
    jobId: data.job_id,
    targetCount: data.target_count,
    totalCount: data.total_count,
  }
}

/** Jul 11 — NO-PRODUCT path. Generates on-brand lifestyle ads from the brand's
 *  own site imagery when the brand has zero products. Same result shape +
 *  /status polling as startBrandSetup, so the gallery handoff is identical. */
export async function startBrandImagery(
  opts?: { targetCount?: number; aspectRatio?: string; imageUrls?: string[] },
): Promise<BrandSetupStartResult> {
  let response: Response
  try {
    response = await fetch(`${API_BASE}/api/brand-setup/start-brand-imagery`, {
      method: 'POST',
      body: JSON.stringify({
        target_count: opts?.targetCount ?? null,
        aspect_ratio: opts?.aspectRatio ?? '1:1',
        image_urls: opts?.imageUrls && opts.imageUrls.length > 0 ? opts.imageUrls : null,
      }),
      headers: {
        ...(await getAuthHeaders()),
        'Content-Type': 'application/json',
      },
    })
  } catch (err) {
    throw new NetworkError(
      err instanceof Error ? `Network error: ${err.message}` : 'Network error',
    )
  }
  if (!response.ok) throw await mapFailure(response)
  const data = await response.json()
  return {
    jobId: data.job_id,
    targetCount: data.target_count,
    totalCount: data.total_count,
  }
}

export async function getBrandSetupStatus(jobId: string): Promise<BrandSetupStatusResult> {
  let response: Response
  try {
    response = await fetch(`${API_BASE}/api/brand-setup/status/${jobId}`, {
      headers: await getAuthHeaders(),
    })
  } catch (err) {
    throw new NetworkError(
      err instanceof Error ? `Network error: ${err.message}` : 'Network error',
    )
  }
  if (!response.ok) throw await mapFailure(response)
  const data = await response.json()
  return {
    id: data.id,
    status: data.status,
    targetCount: data.target_count,
    totalCount: data.total_count ?? data.target_count,
    completedCount: data.completed_count,
    failedCount: data.failed_count,
    recentPosts: (data.recent_posts ?? []).map((p: any) => ({
      id: p.id,
      imagePath: p.image_path,
      title: p.title ?? null,
      createdAt: p.created_at ?? null,
    })),
    error: data.error ?? null,
    createdAt: data.created_at ?? null,
    lastProgressAt: data.last_progress_at ?? null,
  }
}

export interface CdPreviewItem {
  vibe: string
  imageBase64: string  // data URI ready for <img src>
}

export async function fetchCdPreviews(productId?: string): Promise<CdPreviewItem[]> {
  let response: Response
  try {
    response = await fetch(`${API_BASE}/api/brand-setup/cd-previews`, {
      method: 'POST',
      body: JSON.stringify({ product_id: productId ?? null }),
      headers: {
        ...(await getAuthHeaders()),
        'Content-Type': 'application/json',
      },
    })
  } catch (err) {
    throw new NetworkError(
      err instanceof Error ? `Network error: ${err.message}` : 'Network error',
    )
  }
  if (!response.ok) throw await mapFailure(response)
  const data = await response.json()
  return (data.previews ?? []).map((p: { vibe: string; image_base64: string }) => ({
    vibe: p.vibe,
    imageBase64: p.image_base64,
  }))
}


export async function cancelBrandSetup(jobId: string): Promise<{ status: BrandSetupStatus }> {
  let response: Response
  try {
    response = await fetch(`${API_BASE}/api/brand-setup/cancel/${jobId}`, {
      method: 'POST',
      headers: await getAuthHeaders(),
    })
  } catch (err) {
    throw new NetworkError(
      err instanceof Error ? `Network error: ${err.message}` : 'Network error',
    )
  }
  if (!response.ok) throw await mapFailure(response)
  const data = await response.json()
  return { status: data.status }
}


// ============================================================
// My Products — user_products library
// ============================================================
// Per-user product library (up to 10 saved products). The user pastes a
// product URL once on the /products page; backend scrapes + saves. Future
// wizard runs can pick a product from this library instead of re-scraping.
// See backend/scripts/user_products.sql for schema, and the /api/products*
// endpoints in backend/main.py.

export interface UserProduct {
  id: string
  name: string | null
  description: string | null
  imageUrl: string                  // public URL (bucket is public)
  sourceUrl: string | null          // original product page URL
  productCategory: string | null    // cached classification
  tags: string[]                    // cached classification
  createdAt: string | null
}

/** Per-brand product cap by plan tier — mirrors backend
 *  helpers/products.py:PRODUCT_LIMIT_BY_TIER. Only Free stays gated; Starter and
 *  above get the FULL catalog (10k = the discovery ceiling). Used for
 *  client-side UX guards (counter, "Add" disabled at-cap); the backend is the
 *  real enforcer and rejects over-cap with a tier-specific error. */
export const PRODUCT_LIMIT_BY_TIER: Record<string, number> = {
  free: 25,
  starter: 10000,
  pro: 10000,
  business: 10000,
}
export function productLimitForTier(tier: string | null | undefined): number {
  return PRODUCT_LIMIT_BY_TIER[(tier ?? '').toLowerCase()] ?? 25
}
/** @deprecated Use productLimitForTier(planTier). Kept as the max-tier ceiling
 *  for any legacy callsite that isn't tier-aware yet. */
export const PRODUCTS_MAX = 10000

function shapeUserProduct(row: any): UserProduct {
  return {
    id: String(row.id),
    name: row.name ?? null,
    description: row.description ?? null,
    imageUrl: row.image_url ?? '',
    sourceUrl: row.source_url ?? null,
    productCategory: row.product_category ?? null,
    tags: Array.isArray(row.tags) ? row.tags : [],
    createdAt: row.created_at ?? null,
  }
}

export async function listProducts(): Promise<UserProduct[]> {
  let response: Response
  try {
    response = await fetch(`${API_BASE}/api/products`, {
      headers: await getAuthHeaders(),
    })
  } catch (err) {
    throw new NetworkError(
      err instanceof Error ? `Network error: ${err.message}` : 'Network error',
    )
  }
  if (!response.ok) {
    throw await mapFailure(response)
  }
  const data = await response.json()
  if (!Array.isArray(data)) return []
  return data.map(shapeUserProduct)
}

/** Result of POST /api/products. The endpoint accepts either a single
 *  product URL OR a collection / category URL. For a collection, it fans
 *  out parallel saves of every product found on the page (capped at
 *  PRODUCTS_MAX) and returns all newly-saved rows plus a count of any
 *  items it had to skip (overflow past the cap, or per-item scrape
 *  failures). `added` is always at least one row on success — if NOTHING
 *  could be saved, the endpoint raises 400 instead. */
export interface AddProductsResult {
  added: UserProduct[]
  skipped: number
  /** Human-readable reason when `skipped > 0`. Surface verbatim to the
   *  user — already worded for end-user display. */
  skipReason: string | null
}

/** Scrape a product URL OR a collection URL and save the result(s). The
 *  backend caps the library at PRODUCTS_MAX — a 400 with "limit" in the
 *  message means the user must remove some products before adding more. */
export async function addProductsFromUrl(url: string, limit?: number): Promise<AddProductsResult> {
  let response: Response
  try {
    response = await fetch(`${API_BASE}/api/products`, {
      method: 'POST',
      body: JSON.stringify({ url, limit: limit ?? null }),
      headers: {
        ...(await getAuthHeaders()),
        'Content-Type': 'application/json',
      },
    })
  } catch (err) {
    throw new NetworkError(
      err instanceof Error ? `Network error: ${err.message}` : 'Network error',
    )
  }
  if (!response.ok) {
    throw await mapFailure(response)
  }
  const data = await response.json()
  const added = Array.isArray(data.added) ? data.added.map(shapeUserProduct) : []
  return {
    added,
    skipped: typeof data.skipped === 'number' ? data.skipped : 0,
    skipReason: data.skip_reason ?? null,
  }
}

/** Save a MANUALLY UPLOADED product image as a permanent My Products row.
 *  The fallback for WAF-blocked brands (ae.com/KitKat) where URL scraping
 *  can't reach products. name/description are optional — when blank the
 *  backend auto-fills them from the image via vision. Persists to GCS +
 *  user_products, identical to a scraped product (shows on /products,
 *  reusable across generations). */
export async function uploadProduct(
  file: File,
  name?: string,
  description?: string,
): Promise<AddProductsResult> {
  const formData = new FormData()
  formData.append('file', file)
  if (name && name.trim()) formData.append('name', name.trim())
  if (description && description.trim()) formData.append('description', description.trim())

  let response: Response
  try {
    response = await fetch(`${API_BASE}/api/products/upload`, {
      method: 'POST',
      body: formData,
      headers: await getAuthHeaders(),
    })
  } catch (err) {
    throw new NetworkError(
      err instanceof Error ? `Network error: ${err.message}` : 'Network error',
    )
  }
  if (!response.ok) {
    // Jul 12 — 422 needs_manual_info: vision couldn't identify the product,
    // so the backend refused to save it. Surface a typed error carrying the
    // prefill suggestions; the caller collects name+brief and re-uploads.
    if (response.status === 422) {
      let needsInfo: ProductNeedsInfoError | null = null
      try {
        const body = await response.clone().json()
        const d = body?.detail
        if (d && typeof d === 'object' && d.error === 'needs_manual_info') {
          needsInfo = new ProductNeedsInfoError(
            typeof d.message === 'string' ? d.message : 'This product needs a name and description.',
            typeof d.suggested_name === 'string' ? d.suggested_name : '',
            typeof d.suggested_description === 'string' ? d.suggested_description : '',
          )
        }
      } catch { /* not the structured shape — fall through to mapFailure */ }
      if (needsInfo) throw needsInfo
    }
    throw await mapFailure(response)
  }
  const data = await response.json()
  const added = Array.isArray(data.added) ? data.added.map(shapeUserProduct) : []
  return {
    added,
    skipped: typeof data.skipped === 'number' ? data.skipped : 0,
    skipReason: data.skip_reason ?? null,
  }
}

/** Result from POST /api/products/sync-more. Mirrors the backend
 *  SyncMoreProductsResponse shape. job_id is null when there was nothing
 *  new to save (we're already up to date) — frontend should show a
 *  positive "no new products" message rather than queue a job. */
export interface SyncMoreResult {
  jobId: string | null
  discovered: number       // total products found on the site
  alreadySaved: number     // of those, how many were already in My Products
  targetCount: number      // of remaining, how many we kicked off saving
  skippedCount: number     // would-have-saved but blocked by cap
  discoveryMethod: string  // 'shopify' | 'sitemap' | etc.
}

/** Re-scrape the active brand's website and append new products (dedupe
 *  by source_url). Async: returns a job_id; poll
 *  getProductSaveJobStatus to see products appear in /products as the
 *  worker completes. */
export async function syncMoreProducts(): Promise<SyncMoreResult> {
  let response: Response
  try {
    response = await fetch(`${API_BASE}/api/products/sync-more`, {
      method: 'POST',
      headers: {
        ...(await getAuthHeaders()),
        'Content-Type': 'application/json',
      },
    })
  } catch (err) {
    throw new NetworkError(
      err instanceof Error ? `Network error: ${err.message}` : 'Network error',
    )
  }
  if (!response.ok) {
    throw await mapFailure(response)
  }
  const data = await response.json()
  return {
    jobId: data.job_id ?? null,
    discovered: typeof data.discovered === 'number' ? data.discovered : 0,
    alreadySaved: typeof data.already_saved === 'number' ? data.already_saved : 0,
    targetCount: typeof data.target_count === 'number' ? data.target_count : 0,
    skippedCount: typeof data.skipped_count === 'number' ? data.skipped_count : 0,
    discoveryMethod: data.discovery_method ?? 'unknown',
  }
}

/** Discovered-but-not-yet-saved product. Shaped to be a drop-in for the
 *  product picker grid (id + imageUrl), with `imageUrl` pointing at the
 *  brand's own remote CDN (Shopify etc.) rather than our storage bucket.
 *  Persisted only when the user picks + continues via saveProducts(). */
export async function discoverProducts(url: string, limit?: number): Promise<UserProduct[]> {
  let response: Response
  try {
    response = await fetch(`${API_BASE}/api/products/discover`, {
      method: 'POST',
      body: JSON.stringify({ url, limit: limit ?? null }),
      headers: {
        ...(await getAuthHeaders()),
        'Content-Type': 'application/json',
      },
    })
  } catch (err) {
    throw new NetworkError(
      err instanceof Error ? `Network error: ${err.message}` : 'Network error',
    )
  }
  if (!response.ok) {
    throw await mapFailure(response)
  }
  const data = await response.json()
  const rows = Array.isArray(data.products) ? data.products : []
  // Map to UserProduct shape: source_url is the stable id (no DB row yet),
  // imageUrl is the remote CDN URL (renders fine in the picker <img>).
  return rows.map((r: any): UserProduct => ({
    id: String(r.source_url),
    name: r.name ?? null,
    description: r.description ?? null,
    imageUrl: r.image_url ?? '',
    sourceUrl: r.source_url ?? null,
    productCategory: null,
    tags: [],
    createdAt: null,
  }))
}

/** Persist a set of discovered products (the user's picks from
 *  discoverProducts). This is the slow half — download + classify + upload +
 *  insert each — so call it after the user has chosen, ideally with a progress
 *  affordance. Returns the saved rows (now with our storage image URLs). */
export async function saveProducts(products: UserProduct[]): Promise<AddProductsResult> {
  let response: Response
  try {
    response = await fetch(`${API_BASE}/api/products/save-batch`, {
      method: 'POST',
      body: JSON.stringify({
        products: products.map(p => ({
          source_url: p.sourceUrl ?? p.id,
          name: p.name ?? '',
          description: p.description ?? '',
          image_url: p.imageUrl,
        })),
      }),
      headers: {
        ...(await getAuthHeaders()),
        'Content-Type': 'application/json',
      },
    })
  } catch (err) {
    throw new NetworkError(
      err instanceof Error ? `Network error: ${err.message}` : 'Network error',
    )
  }
  if (!response.ok) {
    throw await mapFailure(response)
  }
  const data = await response.json()
  const added = Array.isArray(data.added) ? data.added.map(shapeUserProduct) : []
  return {
    added,
    skipped: typeof data.skipped === 'number' ? data.skipped : 0,
    skipReason: data.skip_reason ?? null,
  }
}

/** Async variant of saveProducts — returns a job_id in ~50ms and processes
 *  in the background. Wizard advances immediately; products fill into My
 *  Products as the worker finishes each one. Use this for large picks (the
 *  250-product Shopify case) where the sync /save-batch would block 60-120s. */
export interface SaveProductsBatchAsyncResult {
  jobId: string
  targetCount: number
  skippedCount: number
}

export async function saveProductsAsync(
  products: UserProduct[],
): Promise<SaveProductsBatchAsyncResult> {
  let response: Response
  try {
    response = await fetch(`${API_BASE}/api/products/save-batch-async`, {
      method: 'POST',
      body: JSON.stringify({
        products: products.map(p => ({
          source_url: p.sourceUrl ?? p.id,
          name: p.name ?? '',
          description: p.description ?? '',
          image_url: p.imageUrl,
        })),
      }),
      headers: {
        ...(await getAuthHeaders()),
        'Content-Type': 'application/json',
      },
    })
  } catch (err) {
    throw new NetworkError(
      err instanceof Error ? `Network error: ${err.message}` : 'Network error',
    )
  }
  if (!response.ok) throw await mapFailure(response)
  const data = await response.json()
  return {
    jobId: data.job_id,
    targetCount: data.target_count,
    skippedCount: data.skipped_count ?? 0,
  }
}

export type ProductSaveJobStatus =
  | 'pending' | 'running' | 'completed' | 'failed' | 'cancelled'

export interface ProductSaveJobStatusResult {
  id: string
  status: ProductSaveJobStatus
  targetCount: number
  processedCount: number
  failedCount: number
  skippedCount: number
  error: string | null
  createdAt: string | null
  lastProgressAt: string | null
}

export async function getProductSaveJobStatus(
  jobId: string,
): Promise<ProductSaveJobStatusResult> {
  let response: Response
  try {
    response = await fetch(
      `${API_BASE}/api/products/save-batch/status/${encodeURIComponent(jobId)}`,
      { headers: await getAuthHeaders() },
    )
  } catch (err) {
    throw new NetworkError(
      err instanceof Error ? `Network error: ${err.message}` : 'Network error',
    )
  }
  if (!response.ok) throw await mapFailure(response)
  const data = await response.json()
  return {
    id: data.id,
    status: data.status,
    targetCount: data.target_count,
    processedCount: data.processed_count,
    failedCount: data.failed_count,
    skippedCount: data.skipped_count ?? 0,
    error: data.error ?? null,
    createdAt: data.created_at ?? null,
    lastProgressAt: data.last_progress_at ?? null,
  }
}

export async function deleteProduct(id: string): Promise<void> {
  let response: Response
  try {
    response = await fetch(`${API_BASE}/api/products/${encodeURIComponent(id)}`, {
      method: 'DELETE',
      headers: await getAuthHeaders(),
    })
  } catch (err) {
    throw new NetworkError(
      err instanceof Error ? `Network error: ${err.message}` : 'Network error',
    )
  }
  if (!response.ok) {
    throw await mapFailure(response)
  }
}

/** Wipe the user's entire My Products library — DB rows + Storage files.
 *  Returns counts for the UI to show a friendly summary. Idempotent —
 *  running twice on an empty library returns zeros. */
export interface ClearAllProductsResult {
  products_deleted: number
  storage_cleaned: number
}

export async function clearAllProducts(): Promise<ClearAllProductsResult> {
  let response: Response
  try {
    response = await fetch(`${API_BASE}/api/products/clear-all`, {
      method: 'POST',
      headers: await getAuthHeaders(),
    })
  } catch (err) {
    throw new NetworkError(
      err instanceof Error ? `Network error: ${err.message}` : 'Network error',
    )
  }
  if (!response.ok) throw await mapFailure(response)
  return await response.json()
}


// ===========================================================================
// Collections — user-defined product groupings (June 7, 2026).
// ===========================================================================
//
// A collection is a brand-scoped grouping of My Products rows. Users
// create collections (e.g. "Spring serums") and add products to them
// for organisation + future scoped generation flows. Architecture:
// many-to-many via a junction table so one product can belong to
// multiple collections.

export interface Collection {
  id: string
  brandId: string
  name: string
  description: string | null
  productCount: number
  /** First 4 product images in the collection — public GCS URLs.
   *  Drives the card thumbnail mosaic on the Collections tab. */
  thumbnails: string[]
  createdAt: string
  updatedAt: string
}

function shapeCollection(row: any): Collection {
  return {
    id: String(row.id),
    brandId: String(row.brand_id),
    name: typeof row.name === 'string' ? row.name : '',
    description: row.description ?? null,
    productCount: typeof row.product_count === 'number' ? row.product_count : 0,
    thumbnails: Array.isArray(row.thumbnails) ? row.thumbnails : [],
    createdAt: row.created_at ?? '',
    updatedAt: row.updated_at ?? '',
  }
}

/** All collections for the active brand, most-recently-updated first. */
export async function listCollections(): Promise<Collection[]> {
  let response: Response
  try {
    response = await fetch(`${API_BASE}/api/collections`, {
      headers: await getAuthHeaders(),
    })
  } catch (err) {
    throw new NetworkError(
      err instanceof Error ? `Network error: ${err.message}` : 'Network error',
    )
  }
  if (!response.ok) throw await mapFailure(response)
  const data = await response.json()
  return Array.isArray(data) ? data.map(shapeCollection) : []
}

export async function createCollection(name: string, description?: string): Promise<Collection> {
  let response: Response
  try {
    response = await fetch(`${API_BASE}/api/collections`, {
      method: 'POST',
      body: JSON.stringify({ name, description: description ?? null }),
      headers: {
        ...(await getAuthHeaders()),
        'Content-Type': 'application/json',
      },
    })
  } catch (err) {
    throw new NetworkError(
      err instanceof Error ? `Network error: ${err.message}` : 'Network error',
    )
  }
  if (!response.ok) throw await mapFailure(response)
  return shapeCollection(await response.json())
}

export async function updateCollection(
  id: string,
  changes: { name?: string; description?: string | null },
): Promise<Collection> {
  let response: Response
  try {
    response = await fetch(`${API_BASE}/api/collections/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(changes),
      headers: {
        ...(await getAuthHeaders()),
        'Content-Type': 'application/json',
      },
    })
  } catch (err) {
    throw new NetworkError(
      err instanceof Error ? `Network error: ${err.message}` : 'Network error',
    )
  }
  if (!response.ok) throw await mapFailure(response)
  return shapeCollection(await response.json())
}

export async function deleteCollection(id: string): Promise<void> {
  let response: Response
  try {
    response = await fetch(`${API_BASE}/api/collections/${id}`, {
      method: 'DELETE',
      headers: await getAuthHeaders(),
    })
  } catch (err) {
    throw new NetworkError(
      err instanceof Error ? `Network error: ${err.message}` : 'Network error',
    )
  }
  if (!response.ok) throw await mapFailure(response)
}

/** Products in a single collection. Same shape as listProducts(). */
export async function listCollectionProducts(id: string): Promise<UserProduct[]> {
  let response: Response
  try {
    response = await fetch(`${API_BASE}/api/collections/${id}/products`, {
      headers: await getAuthHeaders(),
    })
  } catch (err) {
    throw new NetworkError(
      err instanceof Error ? `Network error: ${err.message}` : 'Network error',
    )
  }
  if (!response.ok) throw await mapFailure(response)
  const data = await response.json()
  return Array.isArray(data) ? data.map(shapeUserProduct) : []
}

/** Add one or more existing products to a collection. Idempotent. */
export async function addProductsToCollection(
  collectionId: string,
  productIds: string[],
): Promise<{ inserted: number; skipped: number }> {
  let response: Response
  try {
    response = await fetch(`${API_BASE}/api/collections/${collectionId}/products`, {
      method: 'POST',
      body: JSON.stringify({ product_ids: productIds }),
      headers: {
        ...(await getAuthHeaders()),
        'Content-Type': 'application/json',
      },
    })
  } catch (err) {
    throw new NetworkError(
      err instanceof Error ? `Network error: ${err.message}` : 'Network error',
    )
  }
  if (!response.ok) throw await mapFailure(response)
  const data = await response.json()
  return {
    inserted: typeof data.inserted === 'number' ? data.inserted : 0,
    skipped: typeof data.skipped === 'number' ? data.skipped : 0,
  }
}

/** Remove a single product from a collection (does NOT delete the product). */
export async function removeProductFromCollection(
  collectionId: string,
  productId: string,
): Promise<void> {
  let response: Response
  try {
    response = await fetch(
      `${API_BASE}/api/collections/${collectionId}/products/${productId}`,
      { method: 'DELETE', headers: await getAuthHeaders() },
    )
  } catch (err) {
    throw new NetworkError(
      err instanceof Error ? `Network error: ${err.message}` : 'Network error',
    )
  }
  if (!response.ok) throw await mapFailure(response)
}


// ===========================================================================
// Ad Strategist (premium) — competitive ad intelligence.
// ===========================================================================

export interface GatheredAd {
  advertiser?: string
  ad_text?: string
  headline?: string
  cta?: string
  format?: string
  longevity_days?: number
  variations?: number
  is_rich?: boolean
  first_shown?: string
  last_shown?: string
  media_cached_url?: string | null
  media_ref?: string | null
  /** Meta surfaces this ad targets — `['FACEBOOK', 'INSTAGRAM', 'MESSENGER',
   *  'AUDIENCE_NETWORK']` (any subset). Empty/undefined when the vendor
   *  didn't classify. Used by the Step 3 thumbnail strip to show FB / IG
   *  badges so users can tell at a glance which surface(s) each ad runs on. */
  platforms?: string[]
  /** Meta Ad Library archive id. When set, can be used to deep-link to the
   *  ad on facebook.com/ads/library/?id=... — lets users view the original
   *  ad in its native context (with video playback, all carousel frames,
   *  etc. that our static thumbnail strips can't show). */
  ad_archive_id?: string | null
  /** URL the ad clicks through to (the advertiser's landing page). Useful
   *  for "see what they're selling against" — opens in a new tab. */
  landing_url?: string | null
  /** Optional sub-line shown beneath the headline. Carried for the modal
   *  detail view; not used in the thumbnail strip. */
  link_desc?: string | null
}

export interface AdvertiserBlock {
  label: string
  query: string
  ads: GatheredAd[]
}

export interface AdConcept {
  angle?: string
  format?: string
  /** Maps to a wizard route for the "Generate this" handoff. */
  route?: 'single' | 'carousel' | 'product-shot' | 'product-swap' | string
  /** Real template id the LLM picked from the catalog (or null). Drives the
   *  concept mockup + the wizard template pre-select. */
  template_id?: string | null
  headline?: string
  subhead?: string
  visual_direction?: string
  why_it_wins?: string
  template_hint?: string
  /** Public URL of an auto-rendered Low-quality hero (scene routes only;
   *  premium-absorbed). Null/absent → mockup falls back to the thumbnail. */
  hero_url?: string | null
  /** Carousel-only: ONE sentence locking palette + lighting + typography
   *  across all 4 frames. Repeated verbatim in each frame's render prompt. */
  shared_style?: string
  /** Carousel-only: exactly 4 frames in beat order (hook → benefit → proof
   *  → cta). The image model renders one frame per entry. */
  frames?: AdConceptFrame[]
}

export interface AdConceptFrame {
  beat?: 'hook' | 'benefit' | 'proof' | 'cta' | string
  headline?: string
  subhead?: string
  visual_direction?: string
}

export interface AdBrief {
  brand_snapshot?: string
  competitor_landscape?: { competitor: string; summary: string; notable_hooks?: string[] }[]
  gap_analysis?: string
  /** AS-V1 only: a vision-grounded "how to stand out visually" finding from the
   *  top competitor ad IMAGES (not just copy). Surfaced in the strategy step
   *  and folded into the angles so the concepts inherit the visual direction. */
  visual_gap?: string
  differentiation_angles?: { angle: string; rationale: string }[]
  concepts?: AdConcept[]
  /** Phase 2 multi-product output (5 buckets × 4 concepts). Populated by
   *  build-20-concepts; overwritten by add-20-more (the original gets
   *  archived server-side into _prior_concepts_archive). Drives the
   *  "+ 20 more from this strategy" CTA on archived analyses — we only
   *  show it when this exists, meaning the user has already built ads. */
  ideas?: AdStrategist20Idea[]
}

export interface ProductCandidate {
  image_url: string
  name?: string
  description?: string
}

export interface AdStrategistResult {
  gathered: { brand: AdvertiserBlock | null; competitors: AdvertiserBlock[] }
  brief: AdBrief
  /** Candidate products auto-detected from the brand's site (best-seller first).
   *  Frontend shows them in a picker AFTER the brief; the user confirms which
   *  to render. Empty when the site isn't scrapable. */
  product_candidates?: ProductCandidate[]
  counts: { brand_ads: number; competitor_ads: number }
  gathered_at: string
}

export interface AdStrategistJob {
  id: string
  status: 'pending' | 'gathering' | 'analyzing' | 'ready' | 'failed'
  /** User-given label. Null = client falls back to "{brand_name} · {date}". */
  name?: string | null
  input?: unknown
  result?: AdStrategistResult
  error?: string | null
  created_at?: string
  /** Auto-rerun cadence. null = no schedule. Allowed: 7/14/20. */
  rerun_interval_days?: number | null
  next_run_at?: string | null
  /** Scheduled re-run rows point back to the original via parent_id. */
  parent_id?: string | null
  /** Bumped when the user opens the page; sidebar unread pill = newer rows. */
  last_viewed_at?: string | null
  /** Strategic angle names from result.brief.ideas[].angle_name — projected
   *  by the list endpoint so Previous-analyses rows can show a meaningful
   *  auto-name. Null/empty when Phase 2 hasn't run yet. */
  angle_names?: string[] | null
}

export interface RunAdStrategistInput {
  brandName?: string
  brandQuery?: string
  brandDescription?: string
  competitors: { label?: string; query: string }[]
  /** Per-route template catalog (from buildTemplateCatalog) so the LLM can
   *  pick a real template_id per concept. */
  templateCatalog?: unknown
  /** The user's product (from My Products) used to auto-render scene heroes. */
  productId?: string
  productName?: string
  productDescription?: string
  /** Brand website (My Brand sourceUrl) — backend auto-picks the homepage
   *  hero/best-seller product from it. */
  brandWebsite?: string
  /** Optional analysis label. Null/omitted → client renders a default. */
  name?: string
  /** Opt-in auto re-run. 7|14|20 → schedule; null/omitted → off. */
  rerunIntervalDays?: number | null
}

/** Run a competitive analysis. Synchronous + long-running (gathers from the
 *  ad vendor + LLM synthesis — can take a minute or two), so no fetch
 *  timeout; the caller shows staged progress while awaiting. */
export async function runAdStrategist(input: RunAdStrategistInput): Promise<AdStrategistJob> {
  let response: Response
  try {
    response = await fetch(`${API_BASE}/api/ad-strategist/run`, {
      method: 'POST',
      headers: { ...(await getAuthHeaders()), 'Content-Type': 'application/json' },
      body: JSON.stringify({
        brand_name: input.brandName || null,
        brand_query: input.brandQuery || null,
        brand_description: input.brandDescription || null,
        competitors: input.competitors.map((c) => ({ label: c.label || null, query: c.query })),
        template_catalog: input.templateCatalog ?? null,
        product_id: input.productId || null,
        product_name: input.productName || null,
        product_description: input.productDescription || null,
        brand_website: input.brandWebsite || null,
        name: input.name || null,
        rerun_interval_days:
          typeof input.rerunIntervalDays === 'number' ? input.rerunIntervalDays : null,
      }),
    })
  } catch (err) {
    throw new NetworkError(
      err instanceof Error ? `Network error: ${err.message}` : 'Network error',
    )
  }
  if (!response.ok) {
    throw await mapFailure(response)
  }
  return await response.json()
}

/** Fetch ONE prior analysis with its full cached result (brief + gathered
 *  intel + product candidates). Used by the Recent analyses list when the
 *  user opens an old run — we re-render the snapshot read-only and let them
 *  pick a product + Generate concepts off the cached brief (no Apify rerun). */
export async function getAdStrategistJob(id: string): Promise<AdStrategistJob> {
  let response: Response
  try {
    response = await fetch(`${API_BASE}/api/ad-strategist/jobs/${id}`, {
      headers: await getAuthHeaders(),
    })
  } catch (err) {
    throw new NetworkError(
      err instanceof Error ? `Network error: ${err.message}` : 'Network error',
    )
  }
  if (!response.ok) throw await mapFailure(response)
  return await response.json()
}

/** Rename an analysis or change its re-run cadence. Set markViewed=true to
 *  bump last_viewed_at without changing anything else. */
export async function updateAdStrategistJob(
  id: string,
  patch: { name?: string; rerunIntervalDays?: number | null; markViewed?: boolean },
): Promise<AdStrategistJob> {
  let response: Response
  try {
    response = await fetch(`${API_BASE}/api/ad-strategist/jobs/${id}`, {
      method: 'PATCH',
      headers: { ...(await getAuthHeaders()), 'Content-Type': 'application/json' },
      body: JSON.stringify({
        name: patch.name,
        rerun_interval_days:
          typeof patch.rerunIntervalDays !== 'undefined' ? patch.rerunIntervalDays : undefined,
        mark_viewed: patch.markViewed,
      }),
    })
  } catch (err) {
    throw new NetworkError(
      err instanceof Error ? `Network error: ${err.message}` : 'Network error',
    )
  }
  if (!response.ok) throw await mapFailure(response)
  return await response.json()
}

// =============================================================================
// Wizard "Let AI design" — the AI-Generate fork in /appv2
// =============================================================================

// ----- Creative direction (AI Design filter panel) -----
// 5 controls shown in the AI Design tab in /appv2. Backend translates
// each enum value into a prompt fragment via _format_creative_direction_block.
// Unknown values silently drop server-side — safe to extend with new
// values later without breaking older clients.

export type CreativeVibe =
  | 'Premium'
  | 'Warm & lifestyle'
  | 'Playful'
  | 'Clean & minimal'
  | 'Editorial'

export type CreativeLight =
  | 'Soft daylight'
  | 'Golden hour'
  | 'Studio bright'
  | 'Moody low-key'

export type CreativeFraming =
  // ----- Product Shot route (no human) -----
  | 'Hero close-up'
  | 'Centered product'
  | 'Lifestyle wide'
  | 'Flat lay top-down'
  // ----- Held Product route — CAMERA ANGLE (Jun 11 reshuffle) -----
  // Held's "framing" now means lens/angle. Hand position lives in a
  // separate `pose` CD field — see CreativePose below.
  | 'Close-up'
  | 'Eye-level'
  | 'Side-on'
  | 'Three-quarter'
  | 'Low-hero'
  | 'Top-down'
  // ----- Held Product route — LEGACY pose-as-framing values (kept for
  //       back-compat with stored carousel framings + any pre-Jun-11 CD
  //       payloads; no longer surfaced in the new Held filter UI). -----
  | 'Close-up grip'
  | 'Two-hand present'
  | 'Palm display'
  | 'Mid-application'
  | 'Hands only'
  | 'Over-the-shoulder'
  // ----- Worn Item route (model wears garment) -----
  | 'Full-body editorial'
  | 'Mid-shot lifestyle'
  | 'Detail close-crop'
  | 'Walking in-motion'
  | 'Side profile'
  | 'Styled layered'

// Held Product route ONLY (Jun 11). Hand-position / what the model is
// DOING with the product. Layered onto CreativeFraming (camera angle).
// Surfaced as the hero POSE picker on /appv3/product-swap.
export type CreativePose =
  | 'Close-up grip'
  | 'Two-hand present'
  | 'Palm display'
  | 'Mid-application'
  | 'Hands only'
  | 'Over-the-shoulder'

// Product Shot route ONLY (Jun 11). Arrangement style — how the product
// is composed in the rendered scene (single vs multi-instance, angle).
// Surfaced as the hero PATTERN picker on /appv3/product-shot. Maps to
// backend pattern IDs via PATTERN_TO_BACKEND_ID in /appv3/page.tsx.
export type CreativePattern =
  | 'Single'
  | 'Circular'
  | 'Triangle'
  | 'Mosaic'
  | 'Lined up'
  | 'Falling'

export type CreativeBackground =
  | 'Studio'
  | 'Lifestyle'
  | 'Surface'
  | 'Outdoors'
  | 'Abstract'

/** Brand-color steering for the render. Jul 14 — the picker is now binary
 *  On/Off. 'Off' means NO brand-color language goes into the prompt
 *  (backend's _CD_BRAND_PRESENCE allowlist has no 'Off' entry so the
 *  fragment silently drops — exactly the off semantics). 'On' is the former
 *  "Balanced" behavior. 'Subtle'/'Balanced'/'Bold' are legacy values kept
 *  in the union so stored CDs + brand-setup plan slots still type-check. */
export type CreativeBrandPresence = 'Off' | 'On' | 'Subtle' | 'Balanced' | 'Bold'

/** Text density override — Product Shot route ONLY (gated by frontend).
 *  AD_MAGIC_PROMPT bakes headline + subhead + CTA into every render by
 *  default; this override appended at the END of the assembled prompt
 *  flips the model into a no-text / no-CTA mode (last-directive-wins).
 *  'Text + CTA' is the historical default — no override fragment sent. */
export type CreativeTextDensity = 'None' | 'Text only' | 'Text + CTA'

export interface CreativeDirection {
  mood: CreativeVibe
  light: CreativeLight
  background: CreativeBackground
  framing: CreativeFraming
  brand_presence: CreativeBrandPresence
  /** 'On' composites the brand logo (from My Brand) into the corner
   *  post-render via Pillow. 'Off' skips the composite. Not a prompt
   *  directive — the model is always told to leave the corner clear. */
  logo: 'On' | 'Off'
  /** Optional. Only the Product Shot AI panel surfaces this control;
   *  other routes inherit the default behavior (headline + subhead +
   *  CTA all rendered). Unknown / missing → backend treats as default. */
  text_density?: CreativeTextDensity
  /** Held Product route ONLY (Jun 11). Hand-position picked from the
   *  POSE hero picker on /appv3/product-swap. Backend layers this onto
   *  the camera-angle `framing` so the two compose. Absent on Fashion +
   *  Product Shot CDs (backend allowlist silently no-ops there). */
  pose?: CreativePose
}

export interface WizardAiGenerateInput {
  /** The product image file (from the wizard's Upload step). */
  product: File
  /** Wizard route — controls frame count + render path. Includes
   *  /appv3 routes (product-swap = Held Product, fashion = Worn Item)
   *  in addition to the /appv2 routes. Backend whitelist in
   *  routes/wizard.py must match this union. */
  route: 'single' | 'carousel' | 'product-shot' | 'social-post' | 'product-swap' | 'fashion'
  /** Aspect ratio (driven by Platform step; social-post is always 1:1). */
  aspectRatio?: string
  /** Quality picked in the Quality step. Social-post always uses 'low'. */
  quality?: 'low' | 'medium' | 'high'
  /** Optional user-supplied headline direction. Empty → LLM writes freely. */
  headlineHint?: string
  /** CTA button label baked into the ad. One of the 6 safe phrases.
   *  Defaults to "Shop Now" on the backend if omitted. */
  cta?: string
  /** Product name + description — backend uses them to write on-brand copy. */
  productName?: string
  productDescription?: string
  /** Creative-direction filter selections from the AI Design panel.
   *  Optional — when omitted, backend uses its own opinionated defaults
   *  (which it doesn't have yet — current default behavior == empty
   *  CREATIVE DIRECTION block, i.e. the LLM writes freely). */
  creativeDirection?: CreativeDirection
  /** Optional natural-language steer toward a specific model look —
   *  /appv3/fashion's model picker passes "{name} · {vibe}" of the
   *  selected fashionModel. Backend feeds this to the LLM concept
   *  writer (gpt-4.1-mini) so visual_direction mentions the look. Not
   *  load-bearing for the IMAGE gen — that's what modelId does. */
  modelHint?: string
  /** Optional fashion model id (e.g. 'model-5') — /appv3/fashion's
   *  picker passes this when a model is selected. Backend loads the
   *  matching base.png from FASHION_TEMPLATES_DIR and passes it as a
   *  reference image to gpt-image so the rendered model actually
   *  resembles the picked photo. Without this, modelHint alone is just
   *  words to the image model and gets ignored. */
  modelId?: string
  /** Output format — single static frame OR 4-frame swipeable carousel.
   *  Defaults to 'single' on the backend when omitted. /appv3 sends this
   *  explicitly so any route (product-shot / product-swap / fashion) can
   *  produce a carousel. Legacy /appv2 route='carousel' path implies
   *  format='carousel' on the backend (back-compat). */
  format?: 'single' | 'carousel'
  /** When format='carousel', the 4 framings to apply per frame (frame N
   *  uses frameFramings[N-1]). Ordered tight → wide so frame 1 reads as
   *  the hero. /appv3's ROUTE_CONFIG.carouselFramings owns the list.
   *  When absent or wrong length, backend falls back to the legacy beats
   *  carousel (hook → benefit → proof → CTA). */
  frameFramings?: string[]
  /** Product Shot route ONLY (Jun 11). Backend pattern ID (e.g.
   *  'circular-array', 'triangle', 'lined-up', 'top-down-single') for the
   *  hero PATTERN picker. When set, backend bypasses AI Magic flow and
   *  dispatches to the pattern pipeline (PATTERN_PROMPT_TEMPLATE + Pillow
   *  solid bg + reference layout image at backend/templates/patterns/<id>.png).
   *  Other routes ignore this field. */
  patternId?: string
  /** Product Shot route ONLY (Jun 11). AI template ID — when the user
   *  picks one of the pre-rendered AI templates on /appv3/product-shot
   *  Templates tab. Backend looks up the template's prompt in
   *  PRODUCT_SHOT_AI_TEMPLATES (backend/ai_templates.py) and uses it
   *  verbatim as the render's visual_direction, bypassing the concept
   *  LLM call entirely. No headline / no subhead / no CTA — pure
   *  product photography. */
  aiTemplateId?: string
  /** V3 Carousel ONLY (Jun 14). Structural type — one of:
   *  'bullet-list' / 'text-heavy-split' / 'story-arc' / 'before-after' /
   *  'step-by-step'. When set + format='carousel', backend uses a
   *  type-specific scaffold in the LLM concept prompt so each frame's
   *  visual_direction follows the type's structural shape. Unknown
   *  values silently fall back to framings/beats carousel. */
  carouselType?: string
  /** V3 Carousel ONLY (Jun 14). Frame count — 2, 3, or 4. Sent as a
   *  forward-compat handshake; backend currently renders 4 frames
   *  regardless (variable count is Phase 2). */
  carouselCount?: number
  /** V3 Social Post ONLY (Jun 15). One of 6 intent ids:
   *  'sell' / 'awareness' / 'compare' / 'why-buy' / 'social-proof' /
   *  'problem-promise'. Sent when the user picks an intent on the
   *  /appv3/social-post pre-step. Backend routes through
   *  INTENT_SCAFFOLDS in helpers/ad_brief.py → injected into the LLM
   *  concept writer's prompt to shape composition + headline tone +
   *  CTA presence. Unknown ids silently no-op. */
  intent?: string
  /** V3 Social Post ONLY (Jun 15 v3). When set to 'product-only',
   *  backend appends a "NO humans in frame" directive to the LLM
   *  concept writer's prompt so renders stay product-only. Omitted
   *  for AI Chooses (LLM decides) + In Use (route='product-swap'
   *  carries its own scaffold). Unknown values silently no-op. */
  shotMode?: string
}

export interface WizardAiGenerateResult {
  /** Base64-encoded PNG frames. Length 1 for single/product-shot/social-post;
   *  length 4 for carousel (in beat order: hook → benefit → proof → cta). */
  frames: string[]
  /** Credits charged for this Generate (matches the template path's math). */
  credits_consumed: number
  /** Concept text the LLM wrote — surfaced for "Try again" comparisons +
   *  potential save-with-metadata in My Creatives. */
  concept?: {
    headline?: string
    subhead?: string
    shared_style?: string
  }
  /** Text-overlay tile (Jun 29) — present when text is ON on Product Shot and
   *  the overlay engine is enabled. `base_image` = b64 text-free scene,
   *  `text_overlay` = the JSON editor doc. Pass BOTH to savePost so the saved
   *  tile persists base_image_path + render_config.text_overlay and opens in
   *  the /creatives overlay editor. Absent on every other Generate. */
  base_image?: string
  text_overlay?: Record<string, unknown>
}

/** Generate an AI-designed ad from the wizard. Single endpoint dispatches
 *  by route. Backend handles brand context auto-pull (name + brief + colors
 *  + logo), LLM concept writing, rendering, and the credit lifecycle. */
export async function wizardAiGenerate(input: WizardAiGenerateInput): Promise<WizardAiGenerateResult> {
  const fd = new FormData()
  fd.append('product', input.product)
  fd.append('route', input.route)
  if (input.aspectRatio) fd.append('aspect_ratio', input.aspectRatio)
  if (input.quality) fd.append('quality', input.quality)
  if (input.headlineHint) fd.append('headline_hint', input.headlineHint)
  if (input.cta) fd.append('cta', input.cta)
  if (input.productName) fd.append('product_name', input.productName)
  if (input.productDescription) fd.append('product_description', input.productDescription)
  if (input.creativeDirection) {
    // JSON-serialise so the backend can json.loads it. Smaller payload
    // + single Form field vs. 5 separate fields = cleaner endpoint
    // signature.
    fd.append('creative_direction', JSON.stringify(input.creativeDirection))
  }
  if (input.modelHint) fd.append('model_hint', input.modelHint)
  if (input.modelId) fd.append('model_id', input.modelId)
  if (input.format) fd.append('format', input.format)
  if (input.frameFramings && input.frameFramings.length === 4) {
    // JSON-encode so backend can json.loads + validate shape. Backend
    // tolerates wrong-length / wrong-shape silently (falls back to beats
    // carousel) so a buggy client never blocks a generation.
    fd.append('frame_framings', JSON.stringify(input.frameFramings))
  }
  if (input.patternId) fd.append('pattern_id', input.patternId)
  if (input.aiTemplateId) fd.append('ai_template_id', input.aiTemplateId)
  if (input.carouselType) fd.append('carousel_type', input.carouselType)
  if (input.carouselCount) fd.append('carousel_count', String(input.carouselCount))
  if (input.intent) fd.append('intent', input.intent)
  if (input.shotMode) fd.append('shot_mode', input.shotMode)

  let response: Response
  try {
    response = await fetch(`${API_BASE}/api/wizard/ai-generate`, {
      method: 'POST',
      headers: await getAuthHeaders(),
      body: fd,
    })
  } catch (err) {
    throw new NetworkError(
      err instanceof Error ? `Network error: ${err.message}` : 'Network error',
    )
  }
  if (!response.ok) {
    throw await mapFailure(response)
  }
  return await response.json()
}

// =============================================================================
// Recipes-as-Templates (Jun 18 — Phase 3). Production-facing endpoints
// for the Product Shot wizard's Templates tab. listTemplateRecipes
// fetches approved recipes WITH baked previews; wizardRenderRecipe
// generates an ad using a recipe as the template (user's product,
// brand context, optional logo). Same credit lifecycle as wizardAiGenerate.

export interface ProductionRecipe {
  id: string
  vibe: string
  vibe_slug: string
  product_types: string[]
  /** Model holding/using the product — drives the Product Swap route filter. */
  requires_model: boolean
  /** Recipe renders headline/body text — Social Post shows only these
   *  (every social post carries a message). */
  supports_text: boolean
  preview_url: string
  preview_generated_at?: string | null
  /** Per-category previews baked on a representative mock for a tagged
   *  product_type (Jul 21). Keyed by leaf type (e.g. 'tech'). The Templates
   *  browse page + wizard swap the thumbnail to category_previews[type].url
   *  when showing that category, so a multi-category recipe shows a headphone
   *  on the Tech tab and a serum on the Skincare tab. Absent on recipes that
   *  were never per-category baked → the single preview_url is used. */
  category_previews?: Record<string, { url: string; generated_at?: string }>
  tags: string[]
  style_summary: string
  preview_stale: boolean
  /** Curator flags mirrored from the manifest — the user-facing Templates grid
   *  excludes these (fusion-only "ingredient" recipes never shown standalone,
   *  and curator-hidden ones), matching /api/templates/recipes' server-side
   *  filter. Present on every seed entry; optional for older/other shapes. */
  fusion_only?: boolean
  hide_from_templates?: boolean
}

// Build-time recipe snapshot. version = the manifest content hash at build
// time (the reconcile ETag); entries = the full unfiltered list the grid
// renders instantly. Recipes are curated live so we can't hardcode them —
// this is a fast starting point, never the source of truth.
const RECIPE_SEED = recipesSeedRaw as { version: string; entries: ProductionRecipe[] }

// The seed above is the RAW manifest (all recipes). The Templates grid must apply
// the SAME exclusion the backend endpoint does (wizard.py list_template_recipes):
// drop fusion_only "ingredient" recipes (never standalone templates) and
// curator-hidden ones. Filter here so the instant-paint seed path matches the
// filtered /api/templates/recipes response — otherwise a freshly-baked seed whose
// hash matches live is trusted verbatim and fusion-only recipes leak into
// Templates (Aug 14 2026 fix). Filtered calls (vibe/product_type) already hit the
// server directly, so this only guards the unfiltered "all templates" seed path.
const RECIPE_SEED_TEMPLATE_ENTRIES = RECIPE_SEED.entries.filter(
  (r) => !r.fusion_only && !r.hide_from_templates,
)

// Session cache for the unfiltered recipe list — the browser-side equivalent
// of how legacy templates are "bundled" (zero refetch on route switch). The
// frontend always fetches the FULL list (no params) and filters client-side,
// so one cache entry covers every route. Seeded instantly from RECIPE_SEED,
// then reconciled against the live manifest version once per session.
// Filtered calls (vibe/productType) hit the server directly and aren't cached.
let _recipesCache: Promise<{ recipes: ProductionRecipe[] }> | null = null
// Reconcile runs at most once per session. `done` ⇒ the session cache is
// authoritative; later callers don't register and don't re-check.
let _recipesReconcileStarted = false
let _recipesReconcileDone = false
// Notified iff a background reconcile finds the seed/cache stale and fetches
// a fresher list — lets every mounted grid patch in place. Cleared after
// firing (delta) or when the seed proves current (match).
const _recipeReconcileSubs = new Set<(recipes: ProductionRecipe[]) => void>()

async function fetchRecipesLive(suffix = ''): Promise<{ recipes: ProductionRecipe[] }> {
  let response: Response
  try {
    response = await fetch(`${API_BASE}/api/templates/recipes${suffix}`, {
      headers: await getAuthHeaders(),
    })
  } catch (err) {
    throw new NetworkError(
      err instanceof Error ? `Network error: ${err.message}` : 'Network error',
    )
  }
  if (!response.ok) throw await mapFailure(response)
  return await response.json()
}

async function fetchRecipesVersion(): Promise<string | null> {
  try {
    const res = await fetch(`${API_BASE}/api/templates/recipes/version`, {
      headers: await getAuthHeaders(),
    })
    if (!res.ok) return null
    const j = await res.json()
    return typeof j?.version === 'string' ? j.version : null
  } catch {
    return null
  }
}

// Fire-and-forget reconcile. Compare the bundled seed version to the live
// manifest version: a match means the seed is current (skip the ~20KB list
// fetch entirely); a mismatch — or an unknown version, where we reconcile to
// be safe — means a recipe changed since this build, so fetch the full list,
// update the cache, and notify subscribers to patch their grids. Once per
// session.
function kickRecipeReconcile() {
  if (_recipesReconcileStarted) return
  _recipesReconcileStarted = true
  void (async () => {
    const liveVersion = await fetchRecipesVersion()
    // Current ONLY when both versions are known and equal. An empty seed
    // version (pre-hash manifest) or an unreachable /version endpoint falls
    // through to a full fetch — correctness over the skip optimization.
    if (liveVersion && RECIPE_SEED.version && liveVersion === RECIPE_SEED.version) {
      _recipesReconcileDone = true
      _recipeReconcileSubs.clear()
      return
    }
    try {
      const fresh = await fetchRecipesLive()
      _recipesCache = Promise.resolve(fresh)
      const subs = Array.from(_recipeReconcileSubs)
      _recipeReconcileSubs.clear()
      _recipesReconcileDone = true
      subs.forEach((cb) => {
        try { cb(fresh.recipes) } catch { /* consumer unmounted */ }
      })
    } catch {
      // Offline / server error — keep the seed showing (fast, slightly stale).
      _recipesReconcileDone = true
      _recipeReconcileSubs.clear()
    }
  })()
}

export async function listTemplateRecipes(params?: {
  vibe?: string
  productType?: string
  /** Raw classifier category (e.g. 'coats', 'bags'). The backend maps it to a
   *  product_type + applies family subsumption — single source of truth, no
   *  stale client-side vocab to drift (Jul 13). Ignored if productType is set. */
  productCategory?: string
  /** Bypass the seed + session cache, refetch live (e.g. after a manual reload). */
  force?: boolean
  /** Called once if a background version check finds the bundled seed /
   *  cached list is stale and a fresher list is fetched. Lets the grid patch
   *  in place after the instant first paint. No-op when the seed is current. */
  onReconcile?: (recipes: ProductionRecipe[]) => void
}): Promise<{ recipes: ProductionRecipe[] }> {
  const qs = new URLSearchParams()
  if (params?.vibe) qs.set('vibe', params.vibe)
  if (params?.productType) qs.set('product_type', params.productType)
  else if (params?.productCategory) qs.set('product_category', params.productCategory)
  const suffix = qs.toString() ? `?${qs.toString()}` : ''

  // Filtered calls hit the server directly (uncached, no seed).
  if (suffix) return fetchRecipesLive(suffix)

  // Register the reconcile subscriber while a reconcile could still fire
  // (covers concurrent first-callers in the same session). Skipped once
  // reconcile has settled — those callers just get the up-to-date cache.
  if (params?.onReconcile && !_recipesReconcileDone) {
    _recipeReconcileSubs.add(params.onReconcile)
  }

  if (params?.force) {
    _recipesReconcileDone = true // force = authoritative; no reconcile needed
    _recipeReconcileSubs.clear()
    _recipesCache = fetchRecipesLive().catch((e) => {
      _recipesCache = null
      throw e
    })
    return _recipesCache
  }

  if (_recipesCache) return _recipesCache

  // First unfiltered call of the session.
  if (RECIPE_SEED_TEMPLATE_ENTRIES.length > 0) {
    // Instant paint from the bundled snapshot (fusion-only + curator-hidden
    // excluded, mirroring the server); reconcile in the background.
    _recipesCache = Promise.resolve({ recipes: RECIPE_SEED_TEMPLATE_ENTRIES })
    kickRecipeReconcile()
    return _recipesCache
  }

  // No seed (build skipped the bake, or empty library) → live fetch, exactly
  // the pre-seed behaviour (shows a skeleton until it lands).
  _recipesReconcileDone = true
  _recipeReconcileSubs.clear()
  _recipesCache = fetchRecipesLive().catch((e) => {
    _recipesCache = null
    throw e
  })
  return _recipesCache
}

export interface WizardRenderRecipeInput {
  product: File
  /** "<vibe-slug>/<moodboard-id>" — same format as the picker's recipe.id */
  recipeId: string
  productName?: string
  productDescription?: string
  aspectRatio?: string
  quality?: 'low' | 'medium' | 'high'
  creativeDirection?: unknown
  /** 'carousel' → 1 recipe rendered at 4 camera distances (hero+variants).
   *  Result carries `frames` (4) instead of `image_b64`. Default 'single'. */
  format?: 'single' | 'carousel'
  /** Social Post intent (sell / awareness / …) — shapes the COPY only;
   *  the recipe owns the visual. Omit on other routes. */
  intent?: string
}

export interface WizardRenderRecipeResult {
  /** Present for single renders. */
  image_b64?: string
  /** Present for carousel renders — 4 base64 frames (hero, close, wide, macro). */
  frames?: string[]
  cost: number
  credits_consumed: number
  recipe_id: string
  aspect_ratio: string
  format?: 'single' | 'carousel'
  /** Text-overlay tile (Jun 29) — present on single renders when Text is On +
   *  overlay enabled. Same shape as SynthesizeRecipeResult: base_image = b64
   *  text-free scene, text_overlay = JSON editor doc. Pass BOTH to savePost so
   *  the tile opens in the /creatives overlay editor. */
  base_image?: string
  text_overlay?: Record<string, unknown>
}

export async function wizardRenderRecipe(input: WizardRenderRecipeInput): Promise<WizardRenderRecipeResult> {
  const fd = new FormData()
  fd.append('product', input.product)
  fd.append('recipe_id', input.recipeId)
  if (input.productName) fd.append('product_name', input.productName)
  if (input.productDescription) fd.append('product_description', input.productDescription)
  if (input.aspectRatio) fd.append('aspect_ratio', input.aspectRatio)
  if (input.quality) fd.append('quality', input.quality)
  if (input.format) fd.append('format', input.format)
  if (input.intent) fd.append('intent', input.intent)
  if (input.creativeDirection) {
    fd.append('creative_direction', JSON.stringify(input.creativeDirection))
  }
  let response: Response
  try {
    response = await fetch(`${API_BASE}/api/wizard/render-recipe`, {
      method: 'POST',
      headers: await getAuthHeaders(),
      body: fd,
    })
  } catch (err) {
    throw new NetworkError(
      err instanceof Error ? `Network error: ${err.message}` : 'Network error',
    )
  }
  if (!response.ok) throw await mapFailure(response)
  return await response.json()
}

// =============================================================================
// Synthesize route (Jun 19 — "AI Magic"). The recipe is LLM-synthesized by
// recombining a vibe's curated recipe attributes (layout/typography/lighting/
// palette/composition) into a brand-new coherent spec, then rendered. No
// recipe_id — the backend picks a vibe + composes. Sentinel id used in the
// picker so the AI Magic card slots into the existing selectedRecipeId flow.
// =============================================================================

/** Sentinel selectedRecipeId for the AI Magic / Synthesize card. */
export const SYNTHESIZE_RECIPE_ID = 'synthesize'

export interface SynthesizeRecipeInput {
  product: File
  productName?: string
  productDescription?: string
  aspectRatio?: string
  quality?: 'low' | 'medium' | 'high'
  creativeDirection?: unknown
  /** Optional vibe pin; omit → the synthesizer picks one (variety). */
  vibe?: string
  /** Jun 22 — optional product-type hint for synth pool routing.
   *  Fashion route passes 'apparel' so the synthesizer narrows to
   *  wearing-recipes (model wearing the product). Omit for the
   *  legacy product-only pool. */
  productType?: string
  /** Jul 13 — the wizard route (product-shot / fashion / …). Used by the
   *  backend's empty-pool freestyle fallback so the invented scene gets the
   *  right scaffold (Fashion → "model wearing the garment"). */
  route?: string
  /** Jul 13 — 'single' | 'carousel'. Carousel renders the picked recipe at
   *  4 camera distances → a 4-frame set. */
  format?: 'single' | 'carousel'
}

export interface SynthesizeRecipeResult {
  /** Present on single renders (format: 'single'). */
  image_b64?: string
  /** Jul 13 — present on carousel renders (format: 'carousel') — 4 b64
   *  frames of the picked recipe at hero/close/wide/macro framing. */
  frames?: string[]
  cost: number
  credits_consumed: number
  synthesized: true
  vibe: string
  aspect_ratio: string
  format: 'single' | 'carousel'
  /** Text-overlay tile (Jun 29) — present when Text is On + overlay enabled.
   *  base_image = b64 text-free scene; text_overlay = JSON editor doc. Pass
   *  BOTH to savePost so the tile persists base_image_path +
   *  render_config.text_overlay and opens in the /creatives overlay editor. */
  base_image?: string
  text_overlay?: Record<string, unknown>
}

export async function synthesizeRecipe(input: SynthesizeRecipeInput): Promise<SynthesizeRecipeResult> {
  const fd = new FormData()
  fd.append('product', input.product)
  if (input.productName) fd.append('product_name', input.productName)
  if (input.productDescription) fd.append('product_description', input.productDescription)
  if (input.aspectRatio) fd.append('aspect_ratio', input.aspectRatio)
  if (input.quality) fd.append('quality', input.quality)
  if (input.vibe) fd.append('vibe', input.vibe)
  if (input.productType) fd.append('product_type', input.productType)
  if (input.route) fd.append('route', input.route)
  if (input.format) fd.append('format', input.format)
  if (input.creativeDirection) {
    fd.append('creative_direction', JSON.stringify(input.creativeDirection))
  }
  let response: Response
  try {
    response = await fetch(`${API_BASE}/api/wizard/synthesize-recipe`, {
      method: 'POST',
      headers: await getAuthHeaders(),
      body: fd,
    })
  } catch (err) {
    throw new NetworkError(
      err instanceof Error ? `Network error: ${err.message}` : 'Network error',
    )
  }
  if (!response.ok) throw await mapFailure(response)
  return await response.json()
}

// =============================================================================
// wizardEditImage — prompt-edit on raw image bytes (Jun 12, /appv3 Studio
// render panel's "Edit with prompt" box). Same render pipeline as the
// post-based reimagine, but the source comes from the CLIENT so text/logo
// overlays (kept client-side) never get baked into the AI base. 1 credit.
// =============================================================================

export interface WizardEditImageInput {
  /** The frame to transform — raw PNG bytes (data-URL-decoded blob). */
  image: Blob
  /** Plain-English change instruction, e.g. "make the background sunset". */
  prompt: string
  aspectRatio?: string
  /** Re-composite the brand logo top-left after the transform (pass the
   *  render's cd.logo === 'On' so logo-off renders don't gain one). */
  compositeLogo?: boolean
  /** 'low' | 'medium' | 'high'. Default low → 1 credit. Powers Re-render
   *  Low/Med/High on /creatives so the user can re-render the base at
   *  higher fidelity. Frees-tier accounts are locked to 'low'. */
  quality?: 'low' | 'medium' | 'high'
}

export interface WizardEditImageResult {
  /** Base64-encoded PNG of the transformed frame. */
  image_b64: string
  cost?: number | null
  provider?: string | null
}

export async function wizardEditImage(input: WizardEditImageInput): Promise<WizardEditImageResult> {
  const fd = new FormData()
  fd.append('image', input.image, 'frame.png')
  fd.append('prompt', input.prompt)
  if (input.aspectRatio) fd.append('aspect_ratio', input.aspectRatio)
  if (input.compositeLogo) fd.append('composite_logo', 'true')
  if (input.quality) fd.append('quality', input.quality)

  let response: Response
  try {
    response = await fetch(`${API_BASE}/api/wizard/edit-image`, {
      method: 'POST',
      headers: await getAuthHeaders(),
      body: fd,
    })
  } catch (err) {
    throw new NetworkError(
      err instanceof Error ? `Network error: ${err.message}` : 'Network error',
    )
  }
  if (!response.ok) {
    throw await mapFailure(response)
  }
  return await response.json()
}

/** Delete one of the current user's analyses. Saved posts that came from
 *  this analysis stay in My Creatives (FK is ON DELETE SET NULL); they just
 *  lose the analysis-link badge. Idempotent — 404 from the backend is
 *  swallowed (the row was already gone). */
export async function deleteAdStrategistJob(id: string): Promise<void> {
  let response: Response
  try {
    response = await fetch(`${API_BASE}/api/ad-strategist/jobs/${id}`, {
      method: 'DELETE',
      headers: await getAuthHeaders(),
    })
  } catch (err) {
    throw new NetworkError(
      err instanceof Error ? `Network error: ${err.message}` : 'Network error',
    )
  }
  if (!response.ok && response.status !== 404) {
    throw await mapFailure(response)
  }
}

/** Clear the sidebar unread badge by bumping last_viewed_at on every job. */
export async function markAllAdStrategistViewed(): Promise<void> {
  try {
    await fetch(`${API_BASE}/api/ad-strategist/mark-all-viewed`, {
      method: 'POST',
      headers: await getAuthHeaders(),
    })
  } catch {
    /* non-critical — silent fail is fine for badge clearing */
  }
}

/** Regenerate the brief's concepts for a different featured product (uses
 *  the job's already-gathered intel; one LLM call, no new vendor scrape).
 *  Returns the updated brief. */
/** Queue 5 stub renders against a finished AS analysis. Each item pairs
 *  one concept (by index in brief.concepts) with one product (by My
 *  Products row id). Backend inserts N locked-stub posts immediately
 *  and spawns a background worker that renders each in parallel,
 *  flipping unlocked=true as it goes.
 *
 *  Returns the new post ids so the caller can navigate to /gallery and
 *  surface a "watching N renders" affordance. Per-item failures show
 *  up in `skipped` — the partial success still goes through.
 *
 *  Throws InsufficientCreditsError (402) only if the user is missing
 *  AT LEAST one credit to start the batch; per-stub credits get
 *  reserved later inside the worker (soft-fails leave stubs locked).
 */
export async function queueAdStrategistRenders(input: {
  jobId: string
  items: Array<{ conceptIdx: number; productId: string }>
  aspectRatio?: string
}): Promise<{
  queued: number
  postIds: string[]
  skipped: Array<{ conceptIdx: number; reason: string }>
}> {
  let response: Response
  try {
    response = await fetch(`${API_BASE}/api/ad-strategist/queue-renders`, {
      method: 'POST',
      headers: { ...(await getAuthHeaders()), 'Content-Type': 'application/json' },
      body: JSON.stringify({
        job_id: input.jobId,
        items: input.items.map((it) => ({
          concept_idx: it.conceptIdx,
          product_id: it.productId,
        })),
        aspect_ratio: input.aspectRatio || '1:1',
      }),
    })
  } catch (err) {
    throw new NetworkError(
      err instanceof Error ? `Network error: ${err.message}` : 'Network error',
    )
  }
  if (!response.ok) {
    throw await mapFailure(response)
  }
  const data = await response.json()
  return {
    queued: Number(data.queued ?? 0),
    postIds: Array.isArray(data.post_ids) ? data.post_ids : [],
    skipped: Array.isArray(data.skipped)
      ? data.skipped.map((s: { concept_idx: number; reason: string }) => ({
          conceptIdx: s.concept_idx,
          reason: s.reason,
        }))
      : [],
  }
}

export async function regenerateAdConcepts(input: {
  jobId: string
  productName?: string
  productDescription?: string
  templateCatalog?: unknown
}): Promise<AdBrief> {
  let response: Response
  try {
    response = await fetch(`${API_BASE}/api/ad-strategist/regenerate-concepts`, {
      method: 'POST',
      headers: { ...(await getAuthHeaders()), 'Content-Type': 'application/json' },
      body: JSON.stringify({
        job_id: input.jobId,
        product_name: input.productName || null,
        product_description: input.productDescription || null,
        template_catalog: input.templateCatalog ?? null,
      }),
    })
  } catch (err) {
    throw new NetworkError(
      err instanceof Error ? `Network error: ${err.message}` : 'Network error',
    )
  }
  if (!response.ok) {
    throw await mapFailure(response)
  }
  const data = await response.json()
  return data.brief as AdBrief
}

/** One concept in the Phase 2 multi-product output. 4 per idea bucket × 5
 *  buckets = 20 concepts per run. Visual_direction is what the AI Magic
 *  render path uses for the prompt; hook_type drives the UI chip + lets
 *  the user mentally group similar takes; recommended_product_id is the
 *  LLM's pick from the user's selection (swappable inline). */
export interface AdStrategist20Concept {
  headline: string
  subhead: string
  visual_direction: string
  hook_type: string
  recommended_product_id: string
}

/** One of the 5 idea buckets returned by build-20-concepts. angle_idx
 *  pairs with the index in the Phase 1 brief's differentiation_angles. */
export interface AdStrategist20Idea {
  angle_idx: number
  angle_name: string
  concepts: AdStrategist20Concept[]
}

export interface Build20ConceptsResult {
  ideas: AdStrategist20Idea[]
  product_count: number
}

/** Phase 2 multi-product brief: 5 idea buckets × 4 distinct concepts each
 *  (20 total). The LLM grounds each concept in one product from the user's
 *  selection. Cheap (~$0.005), no credit reservation here — credits get
 *  charged when the user clicks Build my ads (Step 6 → queue-renders).
 *
 *  `productIds` empty/null → backend uses the brand's full active catalog
 *  ("All Products" toggle on the picker). Cap is 50 server-side. */
export async function buildAdStrategist20Concepts(input: {
  jobId: string
  productIds?: string[]
}): Promise<Build20ConceptsResult> {
  let response: Response
  try {
    response = await fetch(`${API_BASE}/api/ad-strategist/build-20-concepts`, {
      method: 'POST',
      headers: { ...(await getAuthHeaders()), 'Content-Type': 'application/json' },
      body: JSON.stringify({
        job_id: input.jobId,
        product_ids: input.productIds && input.productIds.length > 0
          ? input.productIds
          : null,
      }),
    })
  } catch (err) {
    throw new NetworkError(
      err instanceof Error ? `Network error: ${err.message}` : 'Network error',
    )
  }
  if (!response.ok) {
    throw await mapFailure(response)
  }
  const data = await response.json()
  return {
    ideas: Array.isArray(data?.ideas) ? data.ideas : [],
    product_count: typeof data?.product_count === 'number' ? data.product_count : 0,
  }
}

/** Result of /api/ad-strategist/run-precheck. Tells the wizard what the
 *  next analysis will cost + how long since the last run, so we can show
 *  a recency warning before the user spends credits. */
export interface AdStrategistRunPrecheck {
  /** True when the user has zero prior runs for the active brand. The
   *  first run is FREE; every subsequent run charges `credit_cost`. */
  is_first_run: boolean
  /** Credits the next run will reserve. 0 on first-run; non-zero on rerun. */
  credit_cost: number
  /** ISO timestamp of the user's most recent run for the active brand,
   *  or null when there are no prior runs. Drives the "you ran this
   *  N days ago" copy in the recency warning modal. */
  last_run_at: string | null
  /** Days since `last_run_at` (UTC). Null when no prior run exists. */
  days_since_last: number | null
  /** Server-side cap on competitors per run. Frontend uses this as the
   *  authoritative cap (currently 10) so the UI never gets out of sync
   *  with the backend slice. */
  competitor_cap: number
}

/** Fetch run-cost + recency info for the active brand. Cheap, idempotent
 *  — call right before showing the recency warning modal so the copy and
 *  credit cost match what the run endpoint will actually charge. */
export async function getAdStrategistRunPrecheck(): Promise<AdStrategistRunPrecheck> {
  let response: Response
  try {
    response = await fetch(`${API_BASE}/api/ad-strategist/run-precheck`, {
      headers: await getAuthHeaders(),
    })
  } catch (err) {
    throw new NetworkError(
      err instanceof Error ? `Network error: ${err.message}` : 'Network error',
    )
  }
  if (!response.ok) throw await mapFailure(response)
  const data = await response.json()
  return {
    is_first_run: !!data?.is_first_run,
    credit_cost: typeof data?.credit_cost === 'number' ? data.credit_cost : 0,
    last_run_at: typeof data?.last_run_at === 'string' ? data.last_run_at : null,
    days_since_last: typeof data?.days_since_last === 'number' ? data.days_since_last : null,
    competitor_cap: typeof data?.competitor_cap === 'number' ? data.competitor_cap : 10,
  }
}

/** Result of /api/ad-strategist/queue-renders-20. immediate_post_ids are
 *  the 5 ads that will start rendering server-side immediately (one per
 *  idea bucket — first concept of each angle); stub_post_ids are the 15
 *  locked tiles that sit in the gallery awaiting per-1-credit unlocks via
 *  the existing /api/gallery/posts/{id}/unlock endpoint. */
export interface QueueRenders20Result {
  immediate_count: number
  stub_count: number
  total_inserted: number
  immediate_post_ids: string[]
  stub_post_ids: string[]
  skipped: Array<{ idea_idx: number; concept_idx: number; reason: string }>
}

export interface Add20MoreResult {
  ideas: AdStrategist20Idea[]
  phase2_cost?: number | null
}

/** "+ 20 more from this strategy" — diverges from every prior batch.
 *  NEW MODEL: returns just the new ideas (no auto-render). The frontend
 *  swaps brief.ideas in place and the user clicks Generate per-card or
 *  Generate-all on the new cards to actually render. */
export async function addAdStrategist20More(input: {
  jobId: string
}): Promise<Add20MoreResult> {
  let response: Response
  try {
    response = await fetch(`${API_BASE}/api/ad-strategist/add-20-more`, {
      method: 'POST',
      headers: { ...(await getAuthHeaders()), 'Content-Type': 'application/json' },
      body: JSON.stringify({
        job_id: input.jobId,
      }),
    })
  } catch (err) {
    throw new NetworkError(
      err instanceof Error ? `Network error: ${err.message}` : 'Network error',
    )
  }
  if (!response.ok) throw await mapFailure(response)
  const data = await response.json()
  return {
    ideas: Array.isArray(data?.ideas) ? data.ideas : [],
    phase2_cost: typeof data?.phase2_cost === 'number' ? data.phase2_cost : null,
  }
}

/** Phase 2 fan-out — "Build my ads" CTA on Step 6. Renders 5 ads upfront
 *  (one per idea bucket) and stubs 15 for lazy unlock. The brief.ideas
 *  payload was persisted server-side by build-20-concepts, so the
 *  request only needs the job_id + aspect ratio. */
export async function queueAdStrategist20Renders(input: {
  jobId: string
  aspectRatio?: string
}): Promise<QueueRenders20Result> {
  let response: Response
  try {
    response = await fetch(`${API_BASE}/api/ad-strategist/queue-renders-20`, {
      method: 'POST',
      headers: { ...(await getAuthHeaders()), 'Content-Type': 'application/json' },
      body: JSON.stringify({
        job_id: input.jobId,
        aspect_ratio: input.aspectRatio || '1:1',
      }),
    })
  } catch (err) {
    throw new NetworkError(
      err instanceof Error ? `Network error: ${err.message}` : 'Network error',
    )
  }
  if (!response.ok) {
    throw await mapFailure(response)
  }
  const data = await response.json()
  return {
    immediate_count: data?.immediate_count ?? 0,
    stub_count: data?.stub_count ?? 0,
    total_inserted: data?.total_inserted ?? 0,
    immediate_post_ids: Array.isArray(data?.immediate_post_ids) ? data.immediate_post_ids : [],
    stub_post_ids: Array.isArray(data?.stub_post_ids) ? data.stub_post_ids : [],
    skipped: Array.isArray(data?.skipped) ? data.skipped : [],
  }
}

// ── "Generate 20 more" top-up (My Creatives strip). Product-pick is the
// only input. Server spins up a fresh no-competitor Ad Strategist run +
// renders ALL 20 (20 credits). Poll progress via getAdStrategistJobRenders.
export interface AdTopUp20Result {
  job_id: string
  total: number
}

export async function adStrategistTopUp20(input: {
  productIds: string[]
  aspectRatio?: string
  /** How many ads to generate: 5 / 10 / 20 (default 20). 1 credit each. */
  count?: number
}): Promise<AdTopUp20Result> {
  let response: Response
  try {
    response = await fetch(`${API_BASE}/api/ad-strategist/top-up-20`, {
      method: 'POST',
      headers: { ...(await getAuthHeaders()), 'Content-Type': 'application/json' },
      body: JSON.stringify({
        product_ids: input.productIds,
        aspect_ratio: input.aspectRatio || '1:1',
        count: input.count ?? 20,
      }),
    })
  } catch (err) {
    throw new NetworkError(
      err instanceof Error ? `Network error: ${err.message}` : 'Network error',
    )
  }
  if (!response.ok) {
    throw await mapFailure(response)
  }
  const data = await response.json()
  return {
    job_id: String(data?.job_id || ''),
    total: Number(data?.total ?? 0),
  }
}

// ── Ad Strategist V1 "generate" — AS-owned copy of the top-up render chain
// (Cloud Tasks, slot-mapping fixed). Same {job_id,total} contract as top-up;
// poll with getAdStrategistJob (brief) + getAdStrategistJobRenders (slots).
export async function adStrategistV1Generate(input: {
  productIds: string[]
  aspectRatio?: string
  count?: number
}): Promise<AdTopUp20Result> {
  let response: Response
  try {
    response = await fetch(`${API_BASE}/api/ad-strategist-v1/generate`, {
      method: 'POST',
      headers: { ...(await getAuthHeaders()), 'Content-Type': 'application/json' },
      body: JSON.stringify({
        product_ids: input.productIds,
        aspect_ratio: input.aspectRatio || '1:1',
        count: input.count ?? 20,
      }),
    })
  } catch (err) {
    throw new NetworkError(
      err instanceof Error ? `Network error: ${err.message}` : 'Network error',
    )
  }
  if (!response.ok) {
    throw await mapFailure(response)
  }
  const data = await response.json()
  return {
    job_id: String(data?.job_id || ''),
    total: Number(data?.total ?? 0),
  }
}

// ── Ad Strategist V1 full flow (Option B) — AS-owned endpoints, all under
// /api/ad-strategist-v1/*. Step 1 run, Step 3 build-concepts, reads, per-card
// render. Reuses existing types; the old /api/ad-strategist/* stays untouched.
export async function adStrategistV1Run(input: RunAdStrategistInput): Promise<AdStrategistJob> {
  let response: Response
  try {
    response = await fetch(`${API_BASE}/api/ad-strategist-v1/run`, {
      method: 'POST',
      headers: { ...(await getAuthHeaders()), 'Content-Type': 'application/json' },
      body: JSON.stringify({
        brand_name: input.brandName || null,
        brand_query: input.brandQuery || null,
        brand_description: input.brandDescription || null,
        competitors: input.competitors.map((c) => ({ label: c.label || null, query: c.query })),
        brand_website: input.brandWebsite || null,
        name: input.name || null,
        rerun_interval_days:
          typeof input.rerunIntervalDays === 'number' ? input.rerunIntervalDays : null,
      }),
    })
  } catch (err) {
    throw new NetworkError(err instanceof Error ? `Network error: ${err.message}` : 'Network error')
  }
  if (!response.ok) throw await mapFailure(response)
  return await response.json()
}

export async function adStrategistV1Build20Concepts(input: {
  jobId: string
  productIds?: string[]
}): Promise<{ ideas: AdStrategist20Idea[]; product_count: number }> {
  let response: Response
  try {
    response = await fetch(`${API_BASE}/api/ad-strategist-v1/build-20-concepts`, {
      method: 'POST',
      headers: { ...(await getAuthHeaders()), 'Content-Type': 'application/json' },
      body: JSON.stringify({
        job_id: input.jobId,
        product_ids: input.productIds && input.productIds.length ? input.productIds : null,
      }),
    })
  } catch (err) {
    throw new NetworkError(err instanceof Error ? `Network error: ${err.message}` : 'Network error')
  }
  if (!response.ok) throw await mapFailure(response)
  const data = await response.json()
  return {
    ideas: Array.isArray(data?.ideas) ? data.ideas : [],
    product_count: Number(data?.product_count ?? 0),
  }
}

export async function getAdStrategistV1Job(id: string): Promise<AdStrategistJob> {
  let response: Response
  try {
    response = await fetch(`${API_BASE}/api/ad-strategist-v1/jobs/${encodeURIComponent(id)}`, {
      headers: await getAuthHeaders(),
      cache: 'no-store',
    })
  } catch (err) {
    throw new NetworkError(err instanceof Error ? `Network error: ${err.message}` : 'Network error')
  }
  if (!response.ok) throw await mapFailure(response)
  return await response.json()
}

/** List the active brand's AS-V1 runs (newest first). Powers the saved-runs
 *  landing so the page doesn't start a fresh run on every visit. */
export async function listAdStrategistV1Jobs(): Promise<AdStrategistJob[]> {
  let response: Response
  try {
    response = await fetch(`${API_BASE}/api/ad-strategist-v1/jobs`, {
      headers: await getAuthHeaders(),
      cache: 'no-store',
    })
  } catch (err) {
    throw new NetworkError(err instanceof Error ? `Network error: ${err.message}` : 'Network error')
  }
  if (!response.ok) throw await mapFailure(response)
  const data = await response.json()
  return Array.isArray(data) ? (data as AdStrategistJob[]) : []
}

export async function getAdStrategistV1JobRenders(jobId: string): Promise<JobRendersResponse> {
  let response: Response
  try {
    response = await fetch(
      `${API_BASE}/api/ad-strategist-v1/jobs/${encodeURIComponent(jobId)}/renders`,
      { headers: await getAuthHeaders(), cache: 'no-store' },
    )
  } catch (err) {
    throw new NetworkError(err instanceof Error ? `Network error: ${err.message}` : 'Network error')
  }
  if (!response.ok) throw await mapFailure(response)
  const data = await response.json()
  const arr = Array.isArray(data?.renders) ? data.renders : []
  return {
    renders: arr.map((r: any) => ({
      post_id: String(r?.post_id || ''),
      idea_idx: Number(r?.idea_idx ?? -1),
      concept_idx: Number(r?.concept_idx ?? -1),
      status: r?.status === 'rendered' ? 'rendered' : 'queued',
      image_url: r?.image_url || null,
      created_at: r?.created_at || null,
    })) as AdConceptRenderRow[],
    orphaned_count: Number(data?.orphaned_count ?? 0),
    failed_count: Number(data?.failed_count ?? 0),
  }
}

export async function renderAdStrategistV1Concept(input: {
  jobId: string
  ideaIdx: number
  conceptIdx: number
  productId?: string
  aspectRatio?: string
}): Promise<AdConceptRenderResult> {
  let response: Response
  try {
    response = await fetch(`${API_BASE}/api/ad-strategist-v1/render-concept`, {
      method: 'POST',
      headers: { ...(await getAuthHeaders()), 'Content-Type': 'application/json' },
      body: JSON.stringify({
        job_id: input.jobId,
        idea_idx: input.ideaIdx,
        concept_idx: input.conceptIdx,
        product_id: input.productId,
        aspect_ratio: input.aspectRatio || '1:1',
      }),
    })
  } catch (err) {
    throw new NetworkError(err instanceof Error ? `Network error: ${err.message}` : 'Network error')
  }
  if (!response.ok) throw await mapFailure(response)
  const data = await response.json()
  return {
    post_id: String(data?.post_id || ''),
    status: data?.status === 'rendered' ? 'rendered' : 'queued',
    reused: !!data?.reused,
  }
}

// ── Edit a winning-idea concept's copy (headline/subhead) in place. Persists
// onto the stored analysis so the display AND the render both reflect the edit.
// FREE (no credits). Only the fields passed are updated.
export async function editAdStrategistV1Concept(input: {
  jobId: string
  ideaIdx: number
  conceptIdx: number
  headline?: string
  subhead?: string
}): Promise<{ headline: string; subhead: string }> {
  let response: Response
  try {
    response = await fetch(`${API_BASE}/api/ad-strategist-v1/edit-concept`, {
      method: 'POST',
      headers: { ...(await getAuthHeaders()), 'Content-Type': 'application/json' },
      body: JSON.stringify({
        job_id: input.jobId,
        idea_idx: input.ideaIdx,
        concept_idx: input.conceptIdx,
        headline: input.headline,
        subhead: input.subhead,
      }),
    })
  } catch (err) {
    throw new NetworkError(err instanceof Error ? `Network error: ${err.message}` : 'Network error')
  }
  if (!response.ok) throw await mapFailure(response)
  const data = await response.json()
  return {
    headline: String(data?.headline || ''),
    subhead: String(data?.subhead || ''),
  }
}

// ── Per-concept render (NEW model — replaces the stub-locked tile flow).
// Each Winning Ideas card has its own Generate button; Generate-all fires
// N of these in parallel. Idempotent server-side: if a post already exists
// for this (job_id, idea_idx, concept_idx), the existing post_id is returned.

export type AdConceptRenderStatus = 'idle' | 'queued' | 'rendering' | 'rendered' | 'failed'

export interface AdConceptRenderResult {
  post_id: string
  /** 'queued' = stub inserted, worker spawned; 'rendered' = already done */
  status: 'queued' | 'rendered'
  /** true means an existing post was returned (no new charge) */
  reused: boolean
}

/** Render ONE concept on-demand. 1 credit (reserved inside the worker).
 *  Body override `productId` swaps which product the concept renders with;
 *  omitting it uses the concept's brief-assigned recommended_product_id. */
export async function renderAdStrategistConcept(input: {
  jobId: string
  ideaIdx: number
  conceptIdx: number
  productId?: string
  aspectRatio?: string
}): Promise<AdConceptRenderResult> {
  let response: Response
  try {
    response = await fetch(`${API_BASE}/api/ad-strategist/render-concept`, {
      method: 'POST',
      headers: { ...(await getAuthHeaders()), 'Content-Type': 'application/json' },
      body: JSON.stringify({
        job_id: input.jobId,
        idea_idx: input.ideaIdx,
        concept_idx: input.conceptIdx,
        product_id: input.productId,
        aspect_ratio: input.aspectRatio || '1:1',
      }),
    })
  } catch (err) {
    throw new NetworkError(
      err instanceof Error ? `Network error: ${err.message}` : 'Network error',
    )
  }
  if (!response.ok) throw await mapFailure(response)
  const data = await response.json()
  return {
    post_id: String(data?.post_id || ''),
    status: data?.status === 'rendered' ? 'rendered' : 'queued',
    reused: !!data?.reused,
  }
}

export interface AdConceptRenderRow {
  post_id: string
  idea_idx: number
  concept_idx: number
  status: 'queued' | 'rendered'
  image_url: string | null
  created_at: string | null
}

export interface JobRendersResponse {
  renders: AdConceptRenderRow[]
  /** Count of rendered posts attached to this job that LOST their
   *  (idea_idx, concept_idx) slot link — typically because they were
   *  rendered by an older worker that NULLed render_config. They still
   *  live in My Creatives but can't be re-slotted onto the grid. */
  orphaned_count: number
  /** Count of slots that TERMINALLY failed (worker stamped render_error:
   *  150s timeout / render error / 6-min backstop). The loader completes on
   *  (rendered + failed >= total) so a genuinely stuck slot finalizes the batch
   *  without a blind timer that would false-flag a slow-but-fine tail. */
  failed_count: number
}

/** Hydrate the render-status state for the Winning Ideas grid. Returns one
 *  entry per concept that has EVER been rendered or queued for this job,
 *  plus an orphaned_count for rendered-but-unlinked posts. */
export async function getAdStrategistJobRenders(jobId: string): Promise<JobRendersResponse> {
  let response: Response
  try {
    response = await fetch(`${API_BASE}/api/ad-strategist/jobs/${encodeURIComponent(jobId)}/renders`, {
      headers: await getAuthHeaders(),
      cache: 'no-store',
    })
  } catch (err) {
    throw new NetworkError(
      err instanceof Error ? `Network error: ${err.message}` : 'Network error',
    )
  }
  if (!response.ok) throw await mapFailure(response)
  const data = await response.json()
  const arr = Array.isArray(data?.renders) ? data.renders : []
  return {
    renders: arr.map((r: any) => ({
      post_id: String(r?.post_id || ''),
      idea_idx: Number(r?.idea_idx ?? -1),
      concept_idx: Number(r?.concept_idx ?? -1),
      status: r?.status === 'rendered' ? 'rendered' : 'queued',
      image_url: r?.image_url || null,
      created_at: r?.created_at || null,
    })) as AdConceptRenderRow[],
    orphaned_count: Number(data?.orphaned_count ?? 0),
    failed_count: Number(data?.failed_count ?? 0),
  }
}

/** 6-min backstop for a top-up batch: finalize slots still pending because the
 *  worker died (refund the leaked reservation + stamp render_error) so the
 *  loader can complete. Best-effort; never throws. Returns {finalized, refunded}. */
export async function finalizeStaleTopUp(
  jobId: string,
): Promise<{ finalized: number; refunded: number }> {
  try {
    const response = await fetch(
      `${API_BASE}/api/ad-strategist/jobs/${encodeURIComponent(jobId)}/finalize-stale`,
      { method: 'POST', headers: await getAuthHeaders() },
    )
    if (!response.ok) return { finalized: 0, refunded: 0 }
    const data = await response.json()
    return {
      finalized: Number(data?.finalized ?? 0),
      refunded: Number(data?.refunded ?? 0),
    }
  } catch {
    return { finalized: 0, refunded: 0 }
  }
}

/** Backstop for a stalled brand-setup batch (worker died mid-render): refunds
 *  the leaked per-render reservations + marks the job failed. Best-effort;
 *  never throws. `refunded` = how many credits were returned. */
export async function finalizeBrandSetupStale(
  jobId: string,
): Promise<{ finalized: number; refunded: number }> {
  try {
    const response = await fetch(
      `${API_BASE}/api/brand-setup/jobs/${encodeURIComponent(jobId)}/finalize-stale`,
      { method: 'POST', headers: await getAuthHeaders() },
    )
    if (!response.ok) return { finalized: 0, refunded: 0 }
    const data = await response.json()
    return {
      finalized: Number(data?.finalized ?? 0),
      refunded: Number(data?.refunded ?? 0),
    }
  } catch {
    return { finalized: 0, refunded: 0 }
  }
}

/** Cancel a stuck/abandoned top-up batch — deletes the unrendered stubs +
 *  marks the job cancelled. Best-effort; never throws (the caller clears the
 *  UI controller regardless of the result). */
export async function cancelAdStrategistTopUp(jobId: string): Promise<void> {
  try {
    await fetch(`${API_BASE}/api/ad-strategist/jobs/${encodeURIComponent(jobId)}/cancel`, {
      method: 'POST',
      headers: await getAuthHeaders(),
    })
  } catch {
    /* best-effort — the strip is cleared client-side regardless */
  }
}

/** Compose the Editable tab's transparent product cutout (gpt-image-1 social-bg
 *  + rembg). One call per Generate; the result is reused across all 5
 *  paste-mode Editable cards. Premium-absorbed (covered by the 8-credit batch). */
export async function composeEditableProduct(input: {
  productImageUrl: string
  slotColor?: string
}): Promise<string> {
  let response: Response
  try {
    response = await fetch(`${API_BASE}/api/ad-strategist/compose-editable-product`, {
      method: 'POST',
      headers: { ...(await getAuthHeaders()), 'Content-Type': 'application/json' },
      body: JSON.stringify({
        product_image_url: input.productImageUrl,
        slot_color: input.slotColor || null,
      }),
    })
  } catch (err) {
    throw new NetworkError(
      err instanceof Error ? `Network error: ${err.message}` : 'Network error',
    )
  }
  if (!response.ok) {
    throw await mapFailure(response)
  }
  const data = await response.json()
  return data.url as string
}

/** Scrape one product page → {image_url, name, description}. Backs the
 *  product picker's "paste a product URL" path. */
export async function scrapeAdStrategistProduct(url: string): Promise<ProductCandidate> {
  let response: Response
  try {
    response = await fetch(`${API_BASE}/api/ad-strategist/scrape-product`, {
      method: 'POST',
      headers: { ...(await getAuthHeaders()), 'Content-Type': 'application/json' },
      body: JSON.stringify({ url }),
    })
  } catch (err) {
    throw new NetworkError(
      err instanceof Error ? `Network error: ${err.message}` : 'Network error',
    )
  }
  if (!response.ok) {
    throw await mapFailure(response)
  }
  return await response.json()
}

export interface RenderConceptInput {
  mode: 'ai-magic' | 'editable'
  /** Public URL of the product image (hero from website OR My Products). */
  productImageUrl?: string
  productId?: string
  productName?: string
  productDescription?: string
  templateId?: string | null
  route?: string
  headline?: string
  subhead?: string
  visualDirection?: string
  /** Real brand logo URL (My Brand) — composited onto AI Magic ads. */
  brandLogoUrl?: string
  /** Carousel-only (mode='ai-magic' + route='carousel'). `sharedStyle` and
   *  `frames` describe the whole carousel. When `carouselFrameIndex` (0-3)
   *  is ALSO set the backend renders just that single frame and returns
   *  one url — the streaming path the frontend uses today. When the index
   *  is omitted the backend renders all 4 sequentially in one call (legacy,
   *  not currently used by the frontend). */
  sharedStyle?: string
  frames?: AdConceptFrame[]
  carouselFrameIndex?: number
}

/** Result of a single concept render. For non-carousel concepts the backend
 *  returns `{url}` and we surface it as a 1-element urls array — the caller
 *  treats single vs carousel uniformly. */
export interface RenderConceptResult {
  /** All rendered frames as public URLs. Length 1 for single posts /
   *  editable / scene routes; length 4 for AI-Magic carousels. */
  urls: string[]
}

/** Render ONE concept (possibly multi-image for carousel). Long-ish — a
 *  single gpt-image-1 Low edit, or 4 in parallel for carousel — no fetch
 *  timeout. Returns the public URL(s) of the stored render(s). */
export async function renderAdConcept(input: RenderConceptInput): Promise<RenderConceptResult> {
  let response: Response
  try {
    response = await fetch(`${API_BASE}/api/ad-strategist/render`, {
      method: 'POST',
      headers: { ...(await getAuthHeaders()), 'Content-Type': 'application/json' },
      body: JSON.stringify({
        mode: input.mode,
        product_image_url: input.productImageUrl || null,
        product_id: input.productId || null,
        product_name: input.productName || null,
        product_description: input.productDescription || null,
        template_id: input.templateId || null,
        route: input.route || null,
        headline: input.headline || null,
        subhead: input.subhead || null,
        visual_direction: input.visualDirection || null,
        brand_logo_url: input.brandLogoUrl || null,
        shared_style: input.sharedStyle || null,
        frames: input.frames || null,
        carousel_frame_index:
          typeof input.carouselFrameIndex === 'number'
            ? input.carouselFrameIndex
            : null,
      }),
    })
  } catch (err) {
    throw new NetworkError(
      err instanceof Error ? `Network error: ${err.message}` : 'Network error',
    )
  }
  if (!response.ok) {
    throw await mapFailure(response)
  }
  const data = await response.json()
  // Backend returns {urls: [...]} for carousel, {url: "..."} for single.
  // Normalise so callers always read .urls.
  if (Array.isArray(data?.urls)) return { urls: data.urls as string[] }
  if (typeof data?.url === 'string') return { urls: [data.url] }
  throw new Error('Render response did not include a url or urls field')
}

export async function listAdStrategistJobs(): Promise<AdStrategistJob[]> {
  let response: Response
  try {
    response = await fetch(`${API_BASE}/api/ad-strategist/jobs`, {
      headers: await getAuthHeaders(),
    })
  } catch (err) {
    throw new NetworkError(
      err instanceof Error ? `Network error: ${err.message}` : 'Network error',
    )
  }
  if (!response.ok) {
    throw await mapFailure(response)
  }
  return await response.json()
}


/** One competitor suggestion — LLM-seeded only (text). Apify validation
 *  has moved to the Add / View Ads paths to keep this endpoint fast. */
export interface CompetitorSuggestion {
  name: string
  description: string
  facebook_handle: string
  /** The constructed FB URL we'd use as `query` if the user picks this one. */
  query: string
  /** Active Meta ad count (keyword-search subset — a rough lower bound). */
  active_ad_count?: number
  /** Resolved exact Meta advertiser page id. When present, the user adds via
   *  the page-scoped path (addCompetitorPage) so every future pull returns ALL
   *  of that advertiser's ads — not just the 1-2 that keyword-match the name. */
  page_id?: string
}

export interface SuggestCompetitorsResult {
  suggestions: CompetitorSuggestion[]
  from_cache: boolean
  /** True when the backend used real My Products signals (categories + tags)
   *  to specialise the LLM prompt. False when the user has no products yet
   *  and the picks were brand-description-only (less accurate). The UI uses
   *  this to nudge users to add products if they got generic picks. */
  product_signals_used: boolean
}

// =============================================================================
// Saved competitors library (/competitors page)
// =============================================================================

export interface SavedCompetitor {
  id: string
  user_id: string
  query: string
  label: string
  facebook_handle: string | null
  active_ads_count: number
  /** Subset of active_ads_count that have visual media. When 0, the UI
   *  hides the "View ads" button on the list page — there's nothing
   *  visual to render. Text-only ads still surface on the detail page. */
  image_ads_count: number
  last_refreshed_at: string | null
  added_at: string
  /** Brand logo URL (scraped from competitor's homepage HTML, or fallback
   *  to https://graph.facebook.com/{handle}/picture). Null when neither
   *  source resolved — frontend renders a coloured initial-letter chip. */
  logo_url: string | null
  /** og:image (or twitter:image fallback) from the competitor's home page.
   *  Used as a small banner on the card. Null when no og:image present —
   *  frontend renders a gradient placeholder. */
  preview_image_url: string | null
}

export interface ListCompetitorsResult {
  competitors: SavedCompetitor[]
}

export async function listCompetitors(): Promise<ListCompetitorsResult> {
  let response: Response
  try {
    response = await fetch(`${API_BASE}/api/competitors`, {
      headers: await getAuthHeaders(),
    })
  } catch (err) {
    throw new NetworkError(
      err instanceof Error ? `Network error: ${err.message}` : 'Network error',
    )
  }
  if (!response.ok) throw await mapFailure(response)
  return await response.json()
}

export interface AddCompetitorInput {
  /** FB URL, `facebook.com/x`, or bare handle. Backend normalises. */
  query: string
  /** Optional display label; defaults to the handle when omitted. */
  label?: string
}

export interface AddCompetitorResult {
  competitor: SavedCompetitor
  /** True when Apify ran (typical). False when Apify isn't configured
   *  and the row was saved with active_ads_count=0. */
  ads_fetched: boolean
}

export async function addCompetitor(input: AddCompetitorInput): Promise<AddCompetitorResult> {
  let response: Response
  try {
    response = await fetch(`${API_BASE}/api/competitors`, {
      method: 'POST',
      headers: { ...(await getAuthHeaders()), 'Content-Type': 'application/json' },
      body: JSON.stringify({ query: input.query, label: input.label ?? null }),
    })
  } catch (err) {
    throw new NetworkError(
      err instanceof Error ? `Network error: ${err.message}` : 'Network error',
    )
  }
  if (!response.ok) throw await mapFailure(response)
  return await response.json()
}

// Jul 23 — search-and-pick a competitor by name. Returns the distinct Meta
// Ad Library advertiser PAGES matching the name so the user picks the exact one.
export interface AdvertiserPage {
  page_id: string
  name: string
  active_ad_count: number
  image_ad_count: number
  thumbnail_url: string | null
}

export async function searchAdvertisers(q: string): Promise<{ pages: AdvertiserPage[] }> {
  let response: Response
  try {
    response = await fetch(
      `${API_BASE}/api/competitors/search-advertisers?q=${encodeURIComponent(q)}`,
      { headers: await getAuthHeaders() },
    )
  } catch (err) {
    throw new NetworkError(
      err instanceof Error ? `Network error: ${err.message}` : 'Network error',
    )
  }
  if (!response.ok) throw await mapFailure(response)
  return await response.json()
}

// Add a competitor the user PICKED from search — by exact Meta page id.
export async function addCompetitorPage(input: {
  pageId: string
  name: string
}): Promise<AddCompetitorResult> {
  let response: Response
  try {
    response = await fetch(`${API_BASE}/api/competitors/add-page`, {
      method: 'POST',
      headers: { ...(await getAuthHeaders()), 'Content-Type': 'application/json' },
      body: JSON.stringify({ page_id: input.pageId, name: input.name }),
    })
  } catch (err) {
    throw new NetworkError(
      err instanceof Error ? `Network error: ${err.message}` : 'Network error',
    )
  }
  if (!response.ok) throw await mapFailure(response)
  return await response.json()
}

export async function deleteCompetitor(id: string): Promise<void> {
  let response: Response
  try {
    response = await fetch(`${API_BASE}/api/competitors/${encodeURIComponent(id)}`, {
      method: 'DELETE',
      headers: await getAuthHeaders(),
    })
  } catch (err) {
    throw new NetworkError(
      err instanceof Error ? `Network error: ${err.message}` : 'Network error',
    )
  }
  if (!response.ok && response.status !== 404) throw await mapFailure(response)
}

export async function refreshCompetitor(id: string): Promise<{ competitor: SavedCompetitor }> {
  let response: Response
  try {
    response = await fetch(`${API_BASE}/api/competitors/${encodeURIComponent(id)}/refresh`, {
      method: 'POST',
      headers: await getAuthHeaders(),
    })
  } catch (err) {
    throw new NetworkError(
      err instanceof Error ? `Network error: ${err.message}` : 'Network error',
    )
  }
  if (!response.ok) throw await mapFailure(response)
  return await response.json()
}

export interface RefreshAllCompetitorsResult {
  refreshed: number
  failed: number
  total: number
}

export async function refreshAllCompetitors(): Promise<RefreshAllCompetitorsResult> {
  let response: Response
  try {
    response = await fetch(`${API_BASE}/api/competitors/refresh-all`, {
      method: 'POST',
      headers: await getAuthHeaders(),
    })
  } catch (err) {
    throw new NetworkError(
      err instanceof Error ? `Network error: ${err.message}` : 'Network error',
    )
  }
  if (!response.ok) throw await mapFailure(response)
  return await response.json()
}

export interface CompetitorAdsResult {
  label: string | null
  last_refreshed_at: string | null
  ads: GatheredAd[]
}

export async function getCompetitorAds(id: string): Promise<CompetitorAdsResult> {
  let response: Response
  try {
    response = await fetch(`${API_BASE}/api/competitors/${encodeURIComponent(id)}/ads`, {
      headers: await getAuthHeaders(),
    })
  } catch (err) {
    throw new NetworkError(
      err instanceof Error ? `Network error: ${err.message}` : 'Network error',
    )
  }
  if (!response.ok) throw await mapFailure(response)
  return await response.json()
}


export interface FetchCompetitorAdsByQueryResult {
  ads: GatheredAd[]
  query: string
}

/**
 * Read-only competitor-ads fetch by Facebook URL/handle. Hits
 * /api/competitor-ads/fetch-by-query — same _gather_advertiser (cache +
 * in-flight dedup + Apify) that powers addCompetitor, but DOES NOT write
 * to user_competitors. The brand-setup wizard uses this in its
 * competitor-ads step so picking a suggested competitor doesn't silently
 * persist them to the library — library-save is a separate explicit
 * action via addCompetitor.
 */
export async function fetchCompetitorAdsByQuery(
  query: string,
  /** Jun 15 — optional AbortSignal. Wired so the wizard's Skip button
   *  can ACTUALLY cancel the in-flight Apify call mid-request instead
   *  of letting it run to completion server-side. fetch() will reject
   *  with AbortError when triggered. */
  signal?: AbortSignal,
): Promise<FetchCompetitorAdsByQueryResult> {
  let response: Response
  try {
    response = await fetch(`${API_BASE}/api/competitor-ads/fetch-by-query`, {
      method: 'POST',
      headers: {
        ...(await getAuthHeaders()),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ query }),
      signal,
    })
  } catch (err) {
    // AbortError is intentional, not a real failure — let callers
    // distinguish via `err.name === 'AbortError'`.
    if (err instanceof DOMException && err.name === 'AbortError') {
      throw err
    }
    throw new NetworkError(
      err instanceof Error ? `Network error: ${err.message}` : 'Network error',
    )
  }
  if (!response.ok) throw await mapFailure(response)
  return await response.json()
}


// =============================================================================
// Brand-setup preview — 5 themed cards for the wizard's review step
// =============================================================================

export interface BrandSetupTheme {
  vibe: string
  headline: string
  summary: string
}

export interface BrandSetupPreviewResult {
  opening: string
  themes: BrandSetupTheme[]
  /** 20-slot plan with `concept` (headline/subhead/visual_direction) pre-
   *  built per slot. Wizard caches this and ships it back to /start so
   *  the worker + stub inserter skip per-slot LLM calls — eliminating
   *  the 20-concept burst that used to fire DURING render. Opaque to
   *  the frontend (treat as a black box); just pass through. */
  plan?: unknown[]
}

/** Generate the brand-setup wizard's review step payload:
 *   - opening line + 5 vibe themes (one gpt-4.1-mini JSON-out call)
 *   - 20-slot plan with concepts pre-built per slot (20 parallel
 *     _build_wizard_concept calls, ~3-5s wall time)
 *  Runs in parallel server-side; total wall time ≈ themes call.
 *  Wizard MUST pass `plan` through to startBrandSetup or the worker
 *  falls back to building concepts at render-time (legacy path). */
export async function getBrandSetupPreview(): Promise<BrandSetupPreviewResult> {
  let response: Response
  try {
    response = await fetch(`${API_BASE}/api/brand-setup/preview`, {
      method: 'POST',
      headers: {
        ...(await getAuthHeaders()),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({}),
    })
  } catch (err) {
    throw new NetworkError(
      err instanceof Error ? `Network error: ${err.message}` : 'Network error',
    )
  }
  if (!response.ok) throw await mapFailure(response)
  return await response.json()
}


// =============================================================================
// /api/brand-setup/write-concepts (Jun 15 — replaces /preview)
// =============================================================================
//
// Product-aware concept writer. Returns 5 winning ideas (matched 1:1 to
// the first 5 ads we'll render) + a 20-slot plan ready for startBrandSetup.
// Required input: featured_product_ids. Optional: competitor_blocks.

export interface BrandSetupWinningIdea {
  angle_name: string
  headline: string
  subhead: string
  visual_direction?: string
}

export interface BrandSetupWriteConceptsResult {
  opening: string
  winning_ideas: BrandSetupWinningIdea[]
  plan: unknown[]   // opaque; pass to startBrandSetup
}

export async function writeBrandSetupConcepts(input: {
  featuredProductIds: string[]
  competitorBlocks?: unknown[]
}): Promise<BrandSetupWriteConceptsResult> {
  let response: Response
  try {
    response = await fetch(`${API_BASE}/api/brand-setup/write-concepts`, {
      method: 'POST',
      headers: {
        ...(await getAuthHeaders()),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        featured_product_ids: input.featuredProductIds,
        competitor_blocks: input.competitorBlocks ?? null,
      }),
    })
  } catch (err) {
    throw new NetworkError(
      err instanceof Error ? `Network error: ${err.message}` : 'Network error',
    )
  }
  if (!response.ok) throw await mapFailure(response)
  return await response.json()
}


// =============================================================================
// Competitor cache-check — wizard skips 0-ad competitors before fetching
// =============================================================================

export type CompetitorCacheStatus = 'has-ads' | 'no-ads' | 'unknown'

export interface CompetitorCacheCheckEntry {
  query: string
  status: CompetitorCacheStatus
  count: number
}

export async function checkCompetitorsCache(
  queries: string[],
): Promise<CompetitorCacheCheckEntry[]> {
  if (queries.length === 0) return []
  let response: Response
  try {
    response = await fetch(`${API_BASE}/api/competitors/cache-check`, {
      method: 'POST',
      headers: {
        ...(await getAuthHeaders()),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ queries }),
    })
  } catch (err) {
    throw new NetworkError(
      err instanceof Error ? `Network error: ${err.message}` : 'Network error',
    )
  }
  if (!response.ok) throw await mapFailure(response)
  const data = await response.json()
  return Array.isArray(data?.results) ? data.results : []
}


/** Get LLM-seeded competitor suggestions for the user's brand (text-only).
 *  Backend automatically pulls product signals (categories + tags + sample
 *  product names) from My Products to specialise the LLM picks. ~5-15s on
 *  a fresh run; instant on cached. Cache is per-brand-AND-product-mix
 *  snapshot, 14-day TTL. Apify ad-availability check is now done
 *  separately (see checkCompetitorsCache) so this call stays fast. */
export async function suggestCompetitors(
  /** Jun 15 — optional AbortSignal so the wizard's Skip can ACTUALLY
   *  cancel the LLM + website-verify burst mid-flight (was burning
   *  ~$0.001 in LLM + dozens of httpx fetches even after user clicked
   *  Skip). */
  signal?: AbortSignal,
): Promise<SuggestCompetitorsResult> {
  let response: Response
  try {
    response = await fetch(`${API_BASE}/api/ad-strategist/suggest-competitors`, {
      method: 'POST',
      headers: { ...(await getAuthHeaders()), 'Content-Type': 'application/json' },
      body: JSON.stringify({}),
      signal,
    })
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') {
      throw err
    }
    throw new NetworkError(
      err instanceof Error ? `Network error: ${err.message}` : 'Network error',
    )
  }
  if (!response.ok) throw await mapFailure(response)
  return await response.json()
}


// =============================================================================
// Content scheduler (/schedule page)
// =============================================================================

/** A scheduled-post row. Mode 'auto_generate' fields (generation_spec,
 *  generated_post_id) are populated only when the auto-schedule batch's
 *  worker renders ads; in Slice A they sit pending. */
export interface ScheduledPost {
  id: string
  user_id: string
  scheduled_at: string                      // ISO UTC, hour-aligned
  platform: string                          // 'instagram' | 'facebook' | …
  status: 'pending' | 'generating' | 'publishing' | 'posted' | 'failed' | 'cancelled'
  mode: 'ready_post' | 'auto_generate'
  post_id: string | null                    // ready_post: existing My Creatives id
  generation_spec: unknown | null           // auto_generate: brief shape
  generated_post_id: string | null          // auto_generate: post id after render
  /** FK to auto_schedule_batches.id when this tile was created by an
   *  auto-schedule batch; null for manual schedules. Lets the UI badge
   *  auto-gen tiles distinctly + group them by batch when editing. */
  auto_batch_id: string | null
  /** FK to ad_strategist_jobs.id — stamped on Ad Pilot tiles so the
   *  calendar can deep-link the tile click → AS row showing the prompt
   *  + concept that drove this slot. null for non-Ad-Pilot tiles. */
  source_job_id: string | null
  platform_post_id: string | null           // populated on successful publish
  error: string | null
  retry_count: number
  posted_at: string | null
  created_at: string
  updated_at: string
}

/** Schedule one approved post on N platforms — backend fans out into N
 *  rows (one per platform). v1 only supports ready_post mode. */
export interface CreateScheduledPostInput {
  postId: string
  scheduledAt: string                       // ISO UTC (will snap to hour)
  platforms: string[]                       // 1+ of the 5 known platforms
}

export interface CreateScheduledPostResult {
  scheduled: ScheduledPost[]                // N rows when N platforms
}

export async function createScheduledPosts(
  input: CreateScheduledPostInput,
): Promise<CreateScheduledPostResult> {
  let response: Response
  try {
    response = await fetch(`${API_BASE}/api/scheduler/posts`, {
      method: 'POST',
      headers: { ...(await getAuthHeaders()), 'Content-Type': 'application/json' },
      body: JSON.stringify({
        post_id: input.postId,
        scheduled_at: input.scheduledAt,
        platforms: input.platforms,
      }),
    })
  } catch (err) {
    throw new NetworkError(
      err instanceof Error ? `Network error: ${err.message}` : 'Network error',
    )
  }
  if (!response.ok) throw await mapFailure(response)
  return await response.json()
}

/** List the user's scheduled tiles in a date window. Defaults backend-side
 *  to "rolling 60 days from today" if start/end omitted — enough for the
 *  current month + one buffer either side. */
export async function listScheduledPosts(
  range?: { start?: Date; end?: Date },
): Promise<ScheduledPost[]> {
  const params = new URLSearchParams()
  if (range?.start) params.set('start', range.start.toISOString())
  if (range?.end) params.set('end', range.end.toISOString())
  const url = `${API_BASE}/api/scheduler/posts${params.toString() ? `?${params}` : ''}`
  let response: Response
  try {
    response = await fetch(url, { headers: await getAuthHeaders() })
  } catch (err) {
    throw new NetworkError(
      err instanceof Error ? `Network error: ${err.message}` : 'Network error',
    )
  }
  if (!response.ok) throw await mapFailure(response)
  return await response.json()
}

/** Reschedule a tile or move it to a different platform. Allowed only
 *  while status='pending' — backend rejects edits to posted/failed tiles
 *  with a 409. */
export async function updateScheduledPost(
  id: string,
  patch: { scheduledAt?: string; platform?: string },
): Promise<ScheduledPost> {
  let response: Response
  try {
    response = await fetch(`${API_BASE}/api/scheduler/posts/${id}`, {
      method: 'PATCH',
      headers: { ...(await getAuthHeaders()), 'Content-Type': 'application/json' },
      body: JSON.stringify({
        scheduled_at: patch.scheduledAt,
        platform: patch.platform,
      }),
    })
  } catch (err) {
    throw new NetworkError(
      err instanceof Error ? `Network error: ${err.message}` : 'Network error',
    )
  }
  if (!response.ok) throw await mapFailure(response)
  return await response.json()
}

/** Wipe the calendar of all future scheduled posts and cancel any active
 *  auto-schedule batches. Past + posted tiles are preserved. Returns
 *  counts so the UI can confirm what was cleared. */
export interface ClearFutureScheduledResult {
  tiles_deleted: number
  batches_cancelled: number
}

export async function clearFutureScheduled(): Promise<ClearFutureScheduledResult> {
  let response: Response
  try {
    response = await fetch(`${API_BASE}/api/scheduler/clear-future`, {
      method: 'POST',
      headers: await getAuthHeaders(),
    })
  } catch (err) {
    throw new NetworkError(
      err instanceof Error ? `Network error: ${err.message}` : 'Network error',
    )
  }
  if (!response.ok) throw await mapFailure(response)
  return await response.json()
}

/** Cancel a scheduled tile. Hard delete (the source post stays in My
 *  Posts). Idempotent — 404 from the backend is swallowed. */
export async function deleteScheduledPost(id: string): Promise<void> {
  let response: Response
  try {
    response = await fetch(`${API_BASE}/api/scheduler/posts/${id}`, {
      method: 'DELETE',
      headers: await getAuthHeaders(),
    })
  } catch (err) {
    throw new NetworkError(
      err instanceof Error ? `Network error: ${err.message}` : 'Network error',
    )
  }
  if (!response.ok && response.status !== 404) {
    throw await mapFailure(response)
  }
}


// =============================================================================
// Auto-schedule batches (/schedule/auto)
// =============================================================================

export interface AutoScheduleBatch {
  id: string
  user_id: string
  cadence_per_week: number
  platforms: string[]
  competitor_urls: string[] | null
  brand_snapshot: unknown | null
  weeks_ahead: number
  product_ids?: string[] | null
  status: 'active' | 'paused' | 'cancelled' | 'completed'
  next_run_at: string | null
  last_generated_at: string | null
  created_at: string
  updated_at: string
  /** Server-derived for the Ad Pilot list card. */
  source?: 'generate' | 'approved'
  /** Real first/last tile dates + % through the pilot's fixed run. */
  start_date?: string | null
  end_date?: string | null
  progress_pct?: number
}

export interface AutoBatchCreateInput {
  cadencePerWeek: number             // 1..7
  platforms: string[]                // 1+ of the 5 known platforms
  source: 'generate' | 'approved'
  competitorUrls?: string[]          // 'generate' only — null/empty = brand-only
  weeksAhead?: number                // default 4
  approvedPostIds?: string[]         // required when source='approved'
  /** Optional Collection id when products came from a saved Collection.
   *  Metadata only — the backend resolves the collection to its members
   *  at create time and freezes them into product_ids. */
  collectionId?: string | null
  /** Required when source='generate'. Either the user's ad-hoc multi-
   *  select OR the resolved-from-collection list — the backend stores
   *  the final product set and the worker cycles through it per slot. */
  productIds?: string[]
  /** JS Date.getTimezoneOffset() — POSITIVE for zones west of UTC.
   *  Backend uses this to translate local best-time hours into UTC. */
  userOffsetMinutes: number
  /** Optional YYYY-MM-DD local start date. Defaults to next Monday. */
  startDate?: string
}

export interface AutoBatchCreateResult {
  batch: AutoScheduleBatch
  tiles_created: number
  /** Credits the worker will spend rendering week-1 (immediate) plus
   *  the remaining weeks via the cron. 0 for source='approved' since
   *  saved posts don't re-render. Phase 1 assumes 1 credit per ad
   *  (single image); Phase 3 will return a range when mix format ships. */
  estimated_credits: number
}

/** Create a recurring auto-schedule batch + fan out placeholder tiles
 *  across the next N weeks. v1 (Slice A) creates the tiles but no
 *  worker exists yet to render 'generate'-mode ads — they sit at
 *  status='pending' until Slice B ships. 'approved' mode is fully
 *  functional in Slice A (cycles through picked My Creatives). */
export async function createAutoBatch(
  input: AutoBatchCreateInput,
): Promise<AutoBatchCreateResult> {
  let response: Response
  try {
    response = await fetch(`${API_BASE}/api/scheduler/auto-batch`, {
      method: 'POST',
      headers: { ...(await getAuthHeaders()), 'Content-Type': 'application/json' },
      body: JSON.stringify({
        cadence_per_week: input.cadencePerWeek,
        platforms: input.platforms,
        source: input.source,
        competitor_urls: input.competitorUrls ?? null,
        weeks_ahead: input.weeksAhead ?? 4,
        approved_post_ids: input.approvedPostIds ?? null,
        collection_id: input.collectionId ?? null,
        product_ids: input.productIds ?? null,
        user_offset_minutes: input.userOffsetMinutes,
        start_date: input.startDate ?? null,
      }),
    })
  } catch (err) {
    throw new NetworkError(
      err instanceof Error ? `Network error: ${err.message}` : 'Network error',
    )
  }
  if (!response.ok) throw await mapFailure(response)
  return await response.json()
}

/** List the user's auto-schedule batches. Newest first. */
export async function listAutoBatches(): Promise<AutoScheduleBatch[]> {
  let response: Response
  try {
    response = await fetch(`${API_BASE}/api/scheduler/auto-batches`, {
      headers: await getAuthHeaders(),
    })
  } catch (err) {
    throw new NetworkError(
      err instanceof Error ? `Network error: ${err.message}` : 'Network error',
    )
  }
  if (!response.ok) throw await mapFailure(response)
  return await response.json()
}

/** Stop + remove an Ad Pilot: cancels future generation, deletes its
 *  still-unrendered future tiles, and removes it from the list. Already-
 *  rendered + past tiles stay on the calendar. */
export async function deleteAutoBatch(batchId: string): Promise<void> {
  let response: Response
  try {
    response = await fetch(`${API_BASE}/api/scheduler/auto-batches/${batchId}`, {
      method: 'DELETE',
      headers: await getAuthHeaders(),
    })
  } catch (err) {
    throw new NetworkError(
      err instanceof Error ? `Network error: ${err.message}` : 'Network error',
    )
  }
  if (!response.ok) throw await mapFailure(response)
}

// ============================================================================
// Emails (M1 — generate + export only)
// ============================================================================

export type EmailCampaignType =
  | 'product-launch'
  | 'promo'
  | 'newsletter'
  | 'welcome'
  | 'abandoned-cart'
  | 're-engagement'

export type EmailTemplateId =
  | 'editorial-hero'
  | 'product-showcase'
  | 'minimal-text'
  | 'three-section'

export interface EmailSlots {
  subject?: string | null
  preheader?: string | null
  hero_headline?: string | null
  body?: string | null
  cta_label?: string | null
  cta_url?: string | null
}

export interface EmailRecord {
  id: string
  campaignType: EmailCampaignType
  templateId: EmailTemplateId
  slots: EmailSlots
  productIds: string[]
  featuredImageUrl: string | null
  html: string
  createdAt: string | null
  updatedAt: string | null
}

export interface EmailListItem {
  id: string
  campaignType: EmailCampaignType
  templateId: EmailTemplateId
  subject: string | null
  featuredImageUrl: string | null
  createdAt: string | null
}

interface RawEmailRecord {
  id: string
  campaign_type: EmailCampaignType
  template_id: EmailTemplateId
  slots: EmailSlots
  product_ids: string[]
  featured_image_url: string | null
  html: string
  created_at: string | null
  updated_at: string | null
}

interface RawEmailListItem {
  id: string
  campaign_type: EmailCampaignType
  template_id: EmailTemplateId
  subject: string | null
  featured_image_url: string | null
  created_at: string | null
}

function rowToEmailRecord(r: RawEmailRecord): EmailRecord {
  return {
    id: r.id,
    campaignType: r.campaign_type,
    templateId: r.template_id,
    slots: r.slots || {},
    productIds: r.product_ids || [],
    featuredImageUrl: r.featured_image_url ?? null,
    html: r.html || '',
    createdAt: r.created_at,
    updatedAt: r.updated_at,
  }
}

function rowToEmailListItem(r: RawEmailListItem): EmailListItem {
  return {
    id: r.id,
    campaignType: r.campaign_type,
    templateId: r.template_id,
    subject: r.subject ?? null,
    featuredImageUrl: r.featured_image_url ?? null,
    createdAt: r.created_at,
  }
}

export interface GenerateEmailInput {
  campaignType: EmailCampaignType
  templateId: EmailTemplateId
  productIds?: string[]
  customInput?: string | null
}

/** 3 credits. Reserves on entry, refunds on any failure. */
export async function generateEmail(input: GenerateEmailInput): Promise<EmailRecord> {
  let response: Response
  try {
    response = await fetch(`${API_BASE}/api/email/generate`, {
      method: 'POST',
      body: JSON.stringify({
        campaign_type: input.campaignType,
        template_id: input.templateId,
        product_ids: input.productIds || [],
        custom_input: input.customInput ?? null,
      }),
      headers: {
        ...(await getAuthHeaders()),
        'Content-Type': 'application/json',
      },
    })
  } catch (err) {
    throw new NetworkError(
      err instanceof Error ? `Network error: ${err.message}` : 'Network error',
    )
  }
  if (!response.ok) throw await mapFailure(response)
  return rowToEmailRecord(await response.json())
}

export async function getEmail(id: string): Promise<EmailRecord> {
  let response: Response
  try {
    response = await fetch(`${API_BASE}/api/email/${id}`, {
      headers: await getAuthHeaders(),
    })
  } catch (err) {
    throw new NetworkError(
      err instanceof Error ? `Network error: ${err.message}` : 'Network error',
    )
  }
  if (!response.ok) throw await mapFailure(response)
  return rowToEmailRecord(await response.json())
}

export interface PatchEmailInput {
  slots?: EmailSlots
  templateId?: EmailTemplateId
}

export async function patchEmail(id: string, input: PatchEmailInput): Promise<EmailRecord> {
  let response: Response
  try {
    response = await fetch(`${API_BASE}/api/email/${id}`, {
      method: 'PATCH',
      body: JSON.stringify({
        slots: input.slots ?? null,
        template_id: input.templateId ?? null,
      }),
      headers: {
        ...(await getAuthHeaders()),
        'Content-Type': 'application/json',
      },
    })
  } catch (err) {
    throw new NetworkError(
      err instanceof Error ? `Network error: ${err.message}` : 'Network error',
    )
  }
  if (!response.ok) throw await mapFailure(response)
  return rowToEmailRecord(await response.json())
}

export async function listEmails(): Promise<EmailListItem[]> {
  let response: Response
  try {
    response = await fetch(`${API_BASE}/api/emails`, {
      headers: await getAuthHeaders(),
    })
  } catch (err) {
    throw new NetworkError(
      err instanceof Error ? `Network error: ${err.message}` : 'Network error',
    )
  }
  if (!response.ok) throw await mapFailure(response)
  const raw = await response.json()
  if (!Array.isArray(raw)) return []
  return (raw as RawEmailListItem[]).map(rowToEmailListItem)
}

export async function deleteEmail(id: string): Promise<void> {
  let response: Response
  try {
    response = await fetch(`${API_BASE}/api/email/${id}`, {
      method: 'DELETE',
      headers: await getAuthHeaders(),
    })
  } catch (err) {
    throw new NetworkError(
      err instanceof Error ? `Network error: ${err.message}` : 'Network error',
    )
  }
  if (!response.ok) throw await mapFailure(response)
}

// Display metadata for the wizard (kept on the frontend so we don't need
// a metadata endpoint for static data). Keep in sync with backend
// CAMPAIGN_LABELS / TEMPLATE_META.
export const EMAIL_CAMPAIGN_META: Array<{
  id: EmailCampaignType
  label: string
  description: string
  icon: string
  needsProduct: boolean
}> = [
  { id: 'product-launch',  label: 'Product launch',  description: 'Announce something new', icon: 'ri-rocket-line',         needsProduct: true },
  { id: 'promo',           label: 'Promo / sale',    description: 'Discount or limited offer', icon: 'ri-price-tag-3-line', needsProduct: true },
  { id: 'newsletter',      label: 'Newsletter',      description: 'Brand story or update',  icon: 'ri-newspaper-line',      needsProduct: false },
  { id: 'welcome',         label: 'Welcome email',   description: 'First touch after signup', icon: 'ri-mail-open-line',    needsProduct: false },
  { id: 'abandoned-cart',  label: 'Abandoned cart',  description: 'Remind shoppers of their items', icon: 'ri-shopping-cart-line', needsProduct: true },
  { id: 're-engagement',   label: 'Re-engagement',   description: 'Win back lapsed subscribers', icon: 'ri-magic-line',     needsProduct: false },
]

export const EMAIL_TEMPLATE_META: Array<{
  id: EmailTemplateId
  name: string
  description: string
  ready: boolean
}> = [
  { id: 'editorial-hero',   name: 'Editorial Hero',  description: 'Hero image, bold headline, single CTA', ready: true },
  { id: 'minimal-text',     name: 'Minimal Text',    description: 'Logo, short copy, single CTA',          ready: true },
  { id: 'product-showcase', name: 'Product Showcase', description: 'Hero + product cards (Beta)',          ready: false },
  { id: 'three-section',    name: 'Three-Section',   description: 'Hero, benefits, social proof (Beta)',   ready: false },
]


// =============================================================================
// SMS — phone number registration + verification.
// =============================================================================
// Three-step flow on /auth/profile:
//   1. User types their phone → registerPhone({phone}) sends a 6-digit code
//   2. User types the code from the SMS → verifyPhone({code}) stamps verified
//   3. (Anytime later) User clicks Unlink → unlinkPhone() clears the row
// Once verified, inbound SMS to the Twilio number resolves to this user
// and the SMS webhook actions can run.

export interface PhoneStatus {
  phone: string | null
  verified: boolean
  pending: boolean  // code sent + not yet expired + not yet verified
}

export async function getPhoneStatus(): Promise<PhoneStatus> {
  const r = await fetch(`${API_BASE}/api/auth/phone`, {
    headers: await getAuthHeaders(),
  })
  if (!r.ok) throw await mapFailure(r)
  return r.json()
}

export async function registerPhone(phone: string): Promise<{ ok: boolean; expires_at: string }> {
  const r = await fetch(`${API_BASE}/api/auth/phone/register`, {
    method: 'POST',
    headers: { ...(await getAuthHeaders()), 'Content-Type': 'application/json' },
    body: JSON.stringify({ phone }),
  })
  if (!r.ok) throw await mapFailure(r)
  return r.json()
}

export async function verifyPhone(code: string): Promise<{ ok: boolean; phone: string }> {
  const r = await fetch(`${API_BASE}/api/auth/phone/verify`, {
    method: 'POST',
    headers: { ...(await getAuthHeaders()), 'Content-Type': 'application/json' },
    body: JSON.stringify({ code }),
  })
  if (!r.ok) throw await mapFailure(r)
  return r.json()
}

export async function unlinkPhone(): Promise<{ ok: boolean }> {
  const r = await fetch(`${API_BASE}/api/auth/phone`, {
    method: 'DELETE',
    headers: await getAuthHeaders(),
  })
  if (!r.ok) throw await mapFailure(r)
  return r.json()
}


// =============================================================================
// Facebook Page integration (v1 — single Page per user, Post Now on tiles)
// =============================================================================

export interface FacebookConnectionStatus {
  connected: boolean
  pageId?: string
  pageName?: string
  tokenExpiresAt?: string | null
  /** Instagram Business Account linked to the FB Page (when present).
   *  v1 grants IG perms in the same OAuth flow; IG is discovered via the
   *  Page at connect-time. NULL when: Page has no linked IG, IG is
   *  Personal-type (not Business / Creator), or the user connected
   *  before IG scopes were added (they need to disconnect + reconnect). */
  instagramUserId?: string
  instagramUsername?: string
  instagramProfilePictureUrl?: string
}

/** Build the Meta OAuth URL + redirect the browser. Backend returns the
 *  authorize URL with an HMAC-signed `state` (encodes user_id); Meta
 *  bounces back to /api/auth/facebook/callback which validates state,
 *  exchanges code, stores the connection, then redirects the browser
 *  to /gallery?fb=connected (or ?fb=error&msg=...). */
export async function startFacebookOAuth(): Promise<{ redirectUrl: string }> {
  const r = await fetch(`${API_BASE}/api/auth/facebook/start`, {
    method: 'POST',
    headers: { ...(await getAuthHeaders()), 'Content-Type': 'application/json' },
  })
  if (!r.ok) throw await mapFailure(r)
  const data = await r.json()
  return { redirectUrl: data.redirect_url }
}

export async function getFacebookConnection(): Promise<FacebookConnectionStatus> {
  const r = await fetch(`${API_BASE}/api/auth/facebook`, {
    headers: await getAuthHeaders(),
  })
  if (!r.ok) throw await mapFailure(r)
  const data = await r.json()
  return {
    connected: !!data.connected,
    pageId: data.page_id ?? undefined,
    pageName: data.page_name ?? undefined,
    tokenExpiresAt: data.token_expires_at ?? null,
    instagramUserId: data.instagram_user_id ?? undefined,
    instagramUsername: data.instagram_username ?? undefined,
    instagramProfilePictureUrl: data.instagram_profile_picture_url ?? undefined,
  }
}

export async function disconnectFacebook(): Promise<{ ok: boolean }> {
  const r = await fetch(`${API_BASE}/api/auth/facebook`, {
    method: 'DELETE',
    headers: await getAuthHeaders(),
  })
  if (!r.ok) throw await mapFailure(r)
  return r.json()
}

/** One entry in the OAuth-pending pages list. instagramUsername is
 *  populated when the page has an IG Business Account linked (the
 *  picker shows it as a badge so users can pick the right page). */
export interface FacebookPendingPage {
  id: string
  name: string
  category?: string
  instagramUsername?: string
  instagramProfilePictureUrl?: string
}

/** Get the pages from the user's most recent OAuth grant. Empty array
 *  when nothing pending (user landed on /connect-facebook directly,
 *  pending row expired (>10 min old), or OAuth already auto-finalized
 *  on a single-page grant). */
export async function getFacebookPendingPages(): Promise<FacebookPendingPage[]> {
  const r = await fetch(`${API_BASE}/api/auth/facebook/pending`, {
    headers: await getAuthHeaders(),
  })
  if (!r.ok) throw await mapFailure(r)
  const data = await r.json()
  const raw = Array.isArray(data?.pages) ? data.pages : []
  return raw.map((p: any) => ({
    id: String(p.id),
    name: String(p.name),
    category: p.category ?? undefined,
    instagramUsername: p.instagram_username ?? undefined,
    instagramProfilePictureUrl: p.instagram_profile_picture_url ?? undefined,
  }))
}

/** Commit the user's page pick. Backend UPSERTs facebook_connections
 *  and deletes the pending row. Returns the new connection status so
 *  the caller can update sidebar state without a second round-trip. */
export async function finalizeFacebookConnection(
  pageId: string,
): Promise<FacebookConnectionStatus> {
  const r = await fetch(`${API_BASE}/api/auth/facebook/finalize`, {
    method: 'POST',
    headers: { ...(await getAuthHeaders()), 'Content-Type': 'application/json' },
    body: JSON.stringify({ page_id: pageId }),
  })
  if (!r.ok) throw await mapFailure(r)
  const data = await r.json()
  return {
    connected: !!data.connected,
    pageId: data.page_id ?? undefined,
    pageName: data.page_name ?? undefined,
    tokenExpiresAt: data.token_expires_at ?? null,
    instagramUserId: data.instagram_user_id ?? undefined,
    instagramUsername: data.instagram_username ?? undefined,
    instagramProfilePictureUrl: data.instagram_profile_picture_url ?? undefined,
  }
}

export interface FacebookPostResult {
  facebookPostId: string
  pageId: string
}

/** AI-suggest a FB caption for a My Creatives post. ~$0.0005 + ~2-3s.
 *  Returns post.headline → title as a fallback string if the LLM fails;
 *  never throws on the LLM error path (always returns SOMETHING for the
 *  Post-to-FB modal to display + let the user edit). */
export async function suggestFacebookCaption(
  postId: string,
): Promise<{ caption: string }> {
  const r = await fetch(`${API_BASE}/api/facebook/suggest-caption`, {
    method: 'POST',
    headers: { ...(await getAuthHeaders()), 'Content-Type': 'application/json' },
    body: JSON.stringify({ post_id: postId }),
  })
  if (!r.ok) throw await mapFailure(r)
  return r.json()
}

/** Publish a My Creatives post to the connected FB Page. Caption falls
 *  back server-side to post.headline → post.title → '' when omitted. */
export async function postToFacebook(
  postId: string,
  caption?: string,
): Promise<FacebookPostResult> {
  const r = await fetch(`${API_BASE}/api/facebook/post-now`, {
    method: 'POST',
    headers: { ...(await getAuthHeaders()), 'Content-Type': 'application/json' },
    body: JSON.stringify({ post_id: postId, caption: caption ?? null }),
  })
  if (!r.ok) throw await mapFailure(r)
  const data = await r.json()
  return {
    facebookPostId: data.facebook_post_id,
    pageId: data.page_id,
  }
}


// =============================================================================
// Instagram — discovered via the FB Page connection (no separate OAuth).
// =============================================================================

export interface InstagramPostResult {
  instagramPostId: string
  instagramUserId: string
}

/** Publish a My Creatives post to the connected Instagram Business
 *  Account. Backend uses the same page_access_token stored at FB
 *  connect-time. Caption falls back server-side to post.headline →
 *  post.title → '' when omitted. */
export async function postToInstagram(
  postId: string,
  caption?: string,
): Promise<InstagramPostResult> {
  const r = await fetch(`${API_BASE}/api/instagram/post-now`, {
    method: 'POST',
    headers: { ...(await getAuthHeaders()), 'Content-Type': 'application/json' },
    body: JSON.stringify({ post_id: postId, caption: caption ?? null }),
  })
  if (!r.ok) throw await mapFailure(r)
  const data = await r.json()
  return {
    instagramPostId: data.instagram_post_id,
    instagramUserId: data.instagram_user_id,
  }
}

// =============================================================================
// Video publish (Jun 23) — async job pattern. FB Page videos + IG Reels
// take 30s-5min for Meta to process. POST returns a job_id immediately;
// frontend polls getPublishJob(job_id) every 5s for status updates.
// =============================================================================

/** A publish job — terminal statuses are 'posted' and 'failed'.
 *  In-flight statuses ('uploading', 'processing', 'publishing') mean
 *  the worker is still running. Frontend should keep polling. */
export interface PublishJob {
  jobId: string
  postId: string
  channel: 'facebook' | 'instagram'
  status: 'uploading' | 'processing' | 'publishing' | 'posted' | 'failed'
  error?: string | null
  platformPostId?: string | null
  postedAt?: string | null
  attempts: number
}

/** Submit a video post to FB Page. Returns immediately with a job_id;
 *  poll getPublishJob(jobId) until status='posted' or 'failed'. */
export async function postVideoToFacebook(
  postId: string,
  caption?: string,
): Promise<PublishJob> {
  const r = await fetch(`${API_BASE}/api/facebook/post-video-now`, {
    method: 'POST',
    headers: { ...(await getAuthHeaders()), 'Content-Type': 'application/json' },
    body: JSON.stringify({ post_id: postId, caption: caption ?? null }),
  })
  if (!r.ok) throw await mapFailure(r)
  const data = await r.json()
  return {
    jobId: data.job_id,
    postId: data.post_id,
    channel: data.channel,
    status: data.status,
    error: null,
    platformPostId: null,
    postedAt: null,
    attempts: 0,
  }
}

/** Submit a video post to IG as a Reel. Reels must be 3-90s (enforced
 *  via pre-flight in the frontend). Returns immediately with a job_id;
 *  poll getPublishJob(jobId) until status='posted' or 'failed'. */
export async function postVideoToInstagram(
  postId: string,
  caption?: string,
): Promise<PublishJob> {
  const r = await fetch(`${API_BASE}/api/instagram/post-video-now`, {
    method: 'POST',
    headers: { ...(await getAuthHeaders()), 'Content-Type': 'application/json' },
    body: JSON.stringify({ post_id: postId, caption: caption ?? null }),
  })
  if (!r.ok) throw await mapFailure(r)
  const data = await r.json()
  return {
    jobId: data.job_id,
    postId: data.post_id,
    channel: data.channel,
    status: data.status,
    error: null,
    platformPostId: null,
    postedAt: null,
    attempts: 0,
  }
}

/** Poll a publish job's current status. Frontend uses this every ~5s
 *  while a video publish is in flight. Terminal: 'posted', 'failed'. */
export async function getPublishJob(jobId: string): Promise<PublishJob> {
  const r = await fetch(`${API_BASE}/api/publish-jobs/${jobId}`, {
    headers: await getAuthHeaders(),
  })
  if (!r.ok) throw await mapFailure(r)
  const data = await r.json()
  return {
    jobId: data.job_id,
    postId: data.post_id,
    channel: data.channel,
    status: data.status,
    error: data.error ?? null,
    platformPostId: data.platform_post_id ?? null,
    postedAt: data.posted_at ?? null,
    attempts: data.attempts ?? 0,
  }
}


/** AI-suggest an IG-tuned caption. Same product-metadata > vision >
 *  text fallback chain as suggestFacebookCaption, but with an IG-style
 *  prompt (sensory, emoji-friendly, ends with 2-4 hashtags). */
export async function suggestInstagramCaption(
  postId: string,
): Promise<{ caption: string }> {
  const r = await fetch(`${API_BASE}/api/instagram/suggest-caption`, {
    method: 'POST',
    headers: { ...(await getAuthHeaders()), 'Content-Type': 'application/json' },
    body: JSON.stringify({ post_id: postId }),
  })
  if (!r.ok) throw await mapFailure(r)
  return r.json()
}

// =============================================================================
// Analytics — per-platform insights for posts we published through the app
// =============================================================================

export type AnalyticsChannel = 'facebook' | 'instagram'

/** Normalized metrics — keys per channel. Frontend treats null as "—". */
export interface FacebookMetrics {
  impressions?: number | null
  reach?: number | null
  clicks?: number | null
  engaged_users?: number | null
  reactions?: {
    like?: number | null
    love?: number | null
    wow?: number | null
    haha?: number | null
    sad?: number | null
    angry?: number | null
    total?: number | null
  } | null
}

export interface InstagramMetrics {
  impressions?: number | null
  reach?: number | null
  likes?: number | null
  comments?: number | null
  saves?: number | null
  shares?: number | null
  total_interactions?: number | null
}

export type AnalyticsMetrics = FacebookMetrics | InstagramMetrics

export interface AnalyticsPostRow {
  post_id: string
  image_url: string
  title: string | null
  headline: string | null
  aspect_ratio: string
  external_post_id: string
  published_at: string
  page_name: string | null
  metrics: AnalyticsMetrics | null
  metrics_fetched_at: string | null
  fetch_error: string | null
}

export interface AnalyticsListResponse {
  channel: AnalyticsChannel
  connected: boolean
  rows: AnalyticsPostRow[]
}

export async function listAnalytics(
  channel: AnalyticsChannel,
): Promise<AnalyticsListResponse> {
  const r = await fetch(`${API_BASE}/api/analytics/${channel}`, {
    headers: await getAuthHeaders(),
  })
  if (!r.ok) throw await mapFailure(r)
  return r.json()
}

export interface AnalyticsRefreshResponse {
  post_id: string
  channel: AnalyticsChannel
  metrics: AnalyticsMetrics | null
  fetched_at: string
  error: string | null
}

export async function refreshAnalytics(
  channel: AnalyticsChannel,
  postId: string,
): Promise<AnalyticsRefreshResponse> {
  const r = await fetch(`${API_BASE}/api/analytics/${channel}/refresh/${postId}`, {
    method: 'POST',
    headers: await getAuthHeaders(),
  })
  if (!r.ok) throw await mapFailure(r)
  return r.json()
}


// =============================================================================
// Animation route (Jun 11) — export charge.
// The animation itself renders + encodes client-side (zero backend
// cost); this endpoint is the 1-credit charge point. The frontend calls
// it once per export session — and NOT at all when the user generated
// the source frames via the AI path (those 4 credits include the export).
// Throws InsufficientCreditsError (402) so callers can show the
// upgrade CTA.
// =============================================================================

export interface AnimationChargeResult {
  ok: boolean
  credits_consumed: number
}

export async function chargeAnimationExport(): Promise<AnimationChargeResult> {
  let r: Response
  try {
    r = await fetch(`${API_BASE}/api/animation/charge`, {
      method: 'POST',
      headers: await getAuthHeaders(),
    })
  } catch (err) {
    throw new NetworkError(
      err instanceof Error ? `Network error: ${err.message}` : 'Network error',
    )
  }
  if (!r.ok) throw await mapFailure(r)
  return r.json()
}


// ─────────────────────────────────────────────────────────────────────────
// UGC Finder (Aug 2) — creator/influencer discovery + saved library.
// Backend: routes/creators.py (/api/ugc/*). Discovery is premium-absorbed +
// cached 7d per brand; email is enriched lazily when a creator is saved.
// ─────────────────────────────────────────────────────────────────────────

export interface DiscoveredCreator {
  platform: 'tiktok' | 'instagram'
  handle: string
  display_name: string
  followers: number
  bio: string
  avatar_url: string | null
  verified: boolean
  external_link: string | null
  contact_email: string | null
  category: string | null
  engagement_rate: number | null
  sample_thumb: string | null
  sample_url: string | null
  profile_url: string
  tier: string
  relevance: number
  score: number
}

export interface SavedCreator {
  id: string
  platform: 'tiktok' | 'instagram'
  handle: string
  display_name: string | null
  followers: number
  bio: string | null
  avatar_url: string | null
  verified: boolean
  external_link: string | null
  contact_email: string | null
  category: string | null
  engagement_rate: number | null
  profile_url: string | null
  niche_tags: string[]
  status: 'prospect' | 'contacted' | 'partnered'
  notes: string | null
  created_at: string
}

export interface UgcDiscoverInput {
  keywords?: string[]
  hashtags?: string[]
  platforms?: ('tiktok' | 'instagram')[]
  filters?: {
    min_followers?: number
    max_followers?: number
    min_engagement?: number
    verified_only?: boolean
  }
  refresh?: boolean
}

export interface UgcDiscoverResult {
  creators: DiscoveredCreator[]
  from_cache: boolean
  credits_charged: number
  searches?: number
  errors?: string[]
  keywords: string[]
  hashtags: string[]
  platforms: string[]
}

export async function suggestUgcTerms(): Promise<{ keywords: string[]; hashtags: string[]; seeded_from_products: boolean }> {
  let r: Response
  try {
    r = await fetch(`${API_BASE}/api/ugc/suggest-terms`, {
      method: 'POST',
      headers: await getAuthHeaders(),
    })
  } catch (err) {
    throw new NetworkError(err instanceof Error ? `Network error: ${err.message}` : 'Network error')
  }
  if (!r.ok) throw await mapFailure(r)
  return r.json()
}

export async function discoverCreators(input: UgcDiscoverInput): Promise<UgcDiscoverResult> {
  let r: Response
  try {
    r = await fetch(`${API_BASE}/api/ugc/discover`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(await getAuthHeaders()) },
      body: JSON.stringify(input),
    })
  } catch (err) {
    throw new NetworkError(err instanceof Error ? `Network error: ${err.message}` : 'Network error')
  }
  if (!r.ok) throw await mapFailure(r)
  return r.json()
}

export async function listSavedCreators(status?: 'prospect' | 'contacted' | 'partnered'): Promise<SavedCreator[]> {
  const qs = status ? `?status=${status}` : ''
  let r: Response
  try {
    r = await fetch(`${API_BASE}/api/ugc/creators${qs}`, { headers: await getAuthHeaders() })
  } catch (err) {
    throw new NetworkError(err instanceof Error ? `Network error: ${err.message}` : 'Network error')
  }
  if (!r.ok) throw await mapFailure(r)
  const data = await r.json()
  return data.creators ?? []
}

export async function saveCreator(creator: DiscoveredCreator | Partial<SavedCreator>): Promise<SavedCreator> {
  let r: Response
  try {
    r = await fetch(`${API_BASE}/api/ugc/creators`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(await getAuthHeaders()) },
      body: JSON.stringify(creator),
    })
  } catch (err) {
    throw new NetworkError(err instanceof Error ? `Network error: ${err.message}` : 'Network error')
  }
  if (!r.ok) throw await mapFailure(r)
  return r.json()
}

export async function updateCreator(
  id: string,
  patch: { status?: 'prospect' | 'contacted' | 'partnered'; notes?: string },
): Promise<SavedCreator> {
  let r: Response
  try {
    r = await fetch(`${API_BASE}/api/ugc/creators/${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', ...(await getAuthHeaders()) },
      body: JSON.stringify(patch),
    })
  } catch (err) {
    throw new NetworkError(err instanceof Error ? `Network error: ${err.message}` : 'Network error')
  }
  if (!r.ok) throw await mapFailure(r)
  return r.json()
}

export async function deleteCreator(id: string): Promise<void> {
  let r: Response
  try {
    r = await fetch(`${API_BASE}/api/ugc/creators/${id}`, {
      method: 'DELETE',
      headers: await getAuthHeaders(),
    })
  } catch (err) {
    throw new NetworkError(err instanceof Error ? `Network error: ${err.message}` : 'Network error')
  }
  if (!r.ok) throw await mapFailure(r)
}


// ═══════════════════════════════════════════════════════════════════════
// Fake UGC — render a product in a real creator/phone-shot aesthetic.
// Prod: generateUgcCreative (pick product → auto UGC render → My Creatives).
// Dev: curator endpoints for /ugc-builder (extract / save / list / delete /
// test-render) — only reachable when ENABLE_DEV_ENDPOINTS is on server-side.
// ═══════════════════════════════════════════════════════════════════════

export interface UgcGenerateResult {
  post_id: string
  image_url: string
  title: string
}

/** Prod: render the given product in an auto-picked UGC style, saved to My
 *  Creatives. 1 credit, synchronous (~15-25s). */
export async function generateUgcCreative(productId: string): Promise<UgcGenerateResult> {
  let r: Response
  try {
    r = await fetch(`${API_BASE}/api/ugc-creative/generate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(await getAuthHeaders()) },
      body: JSON.stringify({ product_id: productId }),
    })
  } catch (err) {
    throw new NetworkError(err instanceof Error ? `Network error: ${err.message}` : 'Network error')
  }
  if (!r.ok) throw await mapFailure(r)
  return r.json()
}

// ---- Dev curator (/ugc-builder) ----
export interface UgcRecipe {
  lighting?: string
  setting?: string
  camera?: string
  composition?: string
  mood?: string
  color_tone?: string
  text_overlay?: { present?: boolean; style?: string; sample?: string }
  [k: string]: unknown
}

export interface UgcReference {
  id: string
  name: string
  recipe: UgcRecipe
  image_url: string
  image_path: string
  created_at: string
}

export async function extractUgcRecipe(imageB64: string, mime = 'image/webp'): Promise<UgcRecipe> {
  let r: Response
  try {
    r = await fetch(`${API_BASE}/api/dev/ugc/extract`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(await getAuthHeaders()) },
      body: JSON.stringify({ image_b64: imageB64, mime }),
    })
  } catch (err) {
    throw new NetworkError(err instanceof Error ? `Network error: ${err.message}` : 'Network error')
  }
  if (!r.ok) throw await mapFailure(r)
  return (await r.json()).recipe as UgcRecipe
}

export async function saveUgcReference(
  imageB64: string, recipe: UgcRecipe, name = '', mime = 'image/webp',
): Promise<UgcReference> {
  let r: Response
  try {
    r = await fetch(`${API_BASE}/api/dev/ugc/save`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(await getAuthHeaders()) },
      body: JSON.stringify({ image_b64: imageB64, recipe, name, mime }),
    })
  } catch (err) {
    throw new NetworkError(err instanceof Error ? `Network error: ${err.message}` : 'Network error')
  }
  if (!r.ok) throw await mapFailure(r)
  return r.json()
}

export async function listUgcReferences(): Promise<UgcReference[]> {
  let r: Response
  try {
    r = await fetch(`${API_BASE}/api/dev/ugc/list`, { headers: await getAuthHeaders() })
  } catch (err) {
    throw new NetworkError(err instanceof Error ? `Network error: ${err.message}` : 'Network error')
  }
  if (!r.ok) throw await mapFailure(r)
  return (await r.json()).ugc as UgcReference[]
}

export async function deleteUgcReference(id: string): Promise<void> {
  let r: Response
  try {
    r = await fetch(`${API_BASE}/api/dev/ugc/${id}`, { method: 'DELETE', headers: await getAuthHeaders() })
  } catch (err) {
    throw new NetworkError(err instanceof Error ? `Network error: ${err.message}` : 'Network error')
  }
  if (!r.ok) throw await mapFailure(r)
}

export async function testRenderUgc(
  recipe: UgcRecipe, productImageB64: string, productMime = 'image/png',
  productName = 'the product', brandName?: string, quality?: string,
): Promise<{ image_b64: string; cost: number }> {
  let r: Response
  try {
    r = await fetch(`${API_BASE}/api/dev/ugc/test-render`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(await getAuthHeaders()) },
      body: JSON.stringify({
        recipe, product_image_b64: productImageB64, product_mime: productMime,
        product_name: productName, brand_name: brandName ?? null, quality: quality ?? null,
      }),
    })
  } catch (err) {
    throw new NetworkError(err instanceof Error ? `Network error: ${err.message}` : 'Network error')
  }
  if (!r.ok) throw await mapFailure(r)
  return r.json()
}


// ---- Import from Moodboards (UGC builder) ----
export interface ImportableMoodboard {
  id: string
  vibe: string
  image_url: string
  style_summary?: string
}

/** List UNTAGGED moodboards (no product_types) so the UGC builder can pull
 *  them in. Reads the existing dev list endpoint + filters client-side. */
export async function listMoodboardsUntagged(): Promise<ImportableMoodboard[]> {
  let r: Response
  try {
    r = await fetch(`${API_BASE}/api/dev/moodboard/list`, { headers: await getAuthHeaders() })
  } catch (err) {
    throw new NetworkError(err instanceof Error ? `Network error: ${err.message}` : 'Network error')
  }
  if (!r.ok) throw await mapFailure(r)
  const data = await r.json()
  const groups: Record<string, any[]> = (data && data.groups) || {}
  const out: ImportableMoodboard[] = []
  for (const [vibe, entries] of Object.entries(groups)) {
    for (const e of entries || []) {
      const pt = e?.product_types
      const untagged = !Array.isArray(pt) || pt.length === 0
      if (untagged && e?.image_url && e?.id) {
        out.push({ id: String(e.id), vibe, image_url: e.image_url, style_summary: e.style_summary })
      }
    }
  }
  return out
}

/** Pull one moodboard image into the UGC flow: server fetches the bytes + runs
 *  the UGC extract. Returns the image (b64) + a draft recipe to review + save. */
export async function importMoodboardToUgc(imageUrl: string): Promise<{ image_b64: string; mime: string; recipe: UgcRecipe }> {
  let r: Response
  try {
    r = await fetch(`${API_BASE}/api/dev/ugc/import-from-moodboard`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(await getAuthHeaders()) },
      body: JSON.stringify({ image_url: imageUrl }),
    })
  } catch (err) {
    throw new NetworkError(err instanceof Error ? `Network error: ${err.message}` : 'Network error')
  }
  if (!r.ok) throw await mapFailure(r)
  return r.json()
}
